import _ from 'lodash';
import moment from 'moment';
import apiFetch from '@scale/frontend-lib/api-fetch';
import EnrichedError from 'react-scale/lib/enrichedError';
import { IProjectJson } from '@scale/shared/types/project';
import * as queryString from 'query-string';
import { GetPipelineV3ConfigResponse } from '@scale/shared/pipelinev3/requestTypes';
import { GetWorkerSkillsResult, WorkerSkillResponse } from '@scale/genai-data-service-client';
import { ProjectChangelogCreateBatchDTO } from '@scale/shared/projectChangelogs';
import {
  CorpProjectGroupNode,
  CorpProjectGroupProjectGroupNode,
  ProjectNameProps,
} from '@scale/shared/types/corpProjectGroup';
import {
  WorkerTeamConfigProperties,
  WorkerTeam as IWorkerTeam,
  HydratedWorkerTeam,
} from '@scale/shared/workerTeams/types';
import {
  GetProjectLibraryEntriesResponse,
  GetCourseV2LibraryEntriesResponse,
  GetCourseV2Response,
  ProjectWithStatus,
  GetCourseV2Templates,
  GetCourseV2ExistingQual,
} from '@scale/shared/types/api/corp-api/find';
import {
  PipelineV3HumanNodesSummaryResponse,
  PipelineV3PendingNodesResponse,
  QMShowProjectResponse,
  SpecializedReviewTaskerQuery,
  UnclaimedSubtasksResponse,
  ProjectRoleAddRequest,
  QMBenchmark,
} from '@scale/shared/qm/qmRequestTypes';
import {
  CreateOrUpdateCourseV2Request,
  GetCourseV2VersionHistoryParams,
  PostCourseV2VersionRevertParams,
  GetCourseV2VersionHistoryResponse,
  CourseSectionV2,
  AssignedUsersResult,
} from '@scale/shared/types/params/courseV2';
import {
  IChatTaskFeatureConfig,
  DeliveryBuilderConfigCreate,
  DeliveryBuilderConfigDisable,
} from '@scale/shared/types/config';
import {
  DAY_MS,
  QualityProfileAuditStatus,
  TaskBatchStatus,
  ChatBulkAuditDefaultFilters,
  ChatBulkAuditObjectIdsWithAttemptsResponse,
  SaveProjectPriorityRequestBody,
  ThroughputType,
  ProjectDemandInfo,
  TaskPreviewConfig,
  TaskPreviewDateRange,
  TaskPreviewEvent,
  CourseStatusTypeV2,
  SquadsConfig,
  BatchAssignments,
} from '@scale/shared/defaults';
import {
  TaskAttemptInfo,
  LineChartDataType,
} from 'react-scale/corp/views/qm/QMLLMWorkerPerformancePage/hooks';
import {
  Filter,
  FilterObject,
} from 'react-scale/dashboard.scale.com/components/SelfServe/ModelEval/ModelEvalRevamp/utils/types';
import {
  fetchConfig,
  IConfigMeta,
  parseMixedJSON,
  getConfigQuery,
} from 'corp/components/JsonSchemaConfigs/configTransforms';
import { ConfigAttributes } from '@scale/shared/types/configAttribute';
import { splitDotSeparatedPath } from 'corp/components/JsonSchemaConfigs/JsonSchemaRenderer/pathHelpers';
import {
  CheatingFlaggedTasksResponse,
  GenAIProjectSetupRequest,
  GenAIProjectUpdateRequest,
  GenAIProjectSetupResponse,
  LinterRatesStatsResponse,
  L8EscalatedTaskRate,
  TaskersWithStrikeTagsRequest,
  TaskersWithStrikeTagsResponse,
  WorkersDeactivatedForCheatingResponse,
  TasksTouchedByConfirmedCheatersResponse,
  CopilotAPIRequestType,
} from '@scale/shared/types/chat';
import { StreamableAgentChunkType } from '@scale/shared/types/agents/endpoints';
import { StreamableLinterAgentChunk } from '@scale/shared/types/agents/linters/endpoints';
import { QualitySignalVerification } from 'react-scale/corp/views/CorpProjectGroupManager/types';
import { BatchResults } from '@scale/shared/types/geographic';
import {
  ProjectCostsResponse,
  ProjectCostsDailyResponse,
  ProjectCostsCumulativeResponse,
  ProjectCostsPerTaskResponse,
  FinanceAlertConfig,
  FinanceGuardrailConfig,
  FinanceGuardrailConfigResponse,
  FinanceAlertConfigResponse,
  FinanceGlobalConfigResponse,
  IFinanceApprovalRequest,
  FinanceApprovalRequestResponse,
  FinanceIdNameMapResponse,
  SaveFinanceApprovalRequestInput,
  PausedHistoryResponse,
  AdminFinanceDefaultsResponse,
  ProjectCostPerAttemptLevelResponse,
  BulkUploadGuardrailResponse,
  BulkUploadGuardrailRequest,
} from '@scale/shared/types/finance';
import {
  GenerateInviteLinksRequest,
  GenerateInviteLinksResponse,
  FlagsForStaleRolesResponse,
} from '@scale/shared/types/genAIRequests';
import { Paginated } from '@scale/shared/outlier/api';
import { MarketplaceProjectGroupDTO } from '@scale/shared/outlier/dto/MarketplaceProjectGroupDTO';
import {
  ReRankProjectRequestDTO,
  BulkUpdateProjectPrioritiesRequestDTO,
  BulkUpdateProjectPrioritiesImpactResponseDTO,
  CreateOrUpdateProjectPriorityRequestDTO,
  InviteBatchDTO,
  InviteBatchStatusDTO,
  ProjectMarketplaceStatusWorkerSkillsResponseDTO,
  UserEligibilityDTO,
} from '@scale/shared/outlier/dto';
import {
  TnSSpecDocDTO,
  ArchieCheatingAgentEndpointOutput,
  TnSRequirementSpecDocInput,
} from '@scale/shared/trustAndSafety/taskCheatingSpec/types';
import {
  BusinessUnit,
  BusinessUnitProduct,
  GetProjectSxsQualitySignalsResponse,
  IPipelineV3Config,
  IQualityValidationJson,
  IQualityValidationComponentJson,
  AccessPermission,
  TaskType,
  ProjectMarketplaceStatus,
  ReviewLevel,
  PauseAction,
} from '@scale/shared-types';
import { TaskAttemptLookupJson } from '@scale/shared/types/corp/lookup';
import { SpeedAuditLookupResponse } from '@scale/shared/types/corp/attemptAudit';
import { ChatBulkAuditObjectType } from '@scale/genai-compass-shared/cards/chat_bulk_audit';
import { INamedObjectTypes, ITranslateTaskResponse } from '@scale/shared/types/lookup';
import {
  CreateProjectGroupRequest,
  CreateProjectGroupResponse,
  ProjectGroupDiff,
  UpdateProjectGroupRequest,
  UpdateProjectGroupResponse,
} from '@scale/shared/dailyPlan/types/api';
import { isAfter, subDays } from 'date-fns';
import { GetTemplateResponse } from '@scale/shared/taskTemplate/requestTypes';
import { ProvisionWorkspaceRequest } from '@scale/shared/sphereEngine/types';
import { Role } from '@scale/shared/corp/projectRoles';
import { HydratedCorpJobJson } from '@scale/shared/types/corpJobs/HydratedCorpJobJson';
import { ReportIssueReqBody, ReportIssueResBody } from '@scale/shared/reportIssue/api';
import { DevErrorStatus } from '@scale/shared/devErrorsTesting';
import { ICourse } from 'react-scale/components/Expert/Course/courseUtils';
import { QueueListOutput } from '@scale/shared/types/api/corp-api/explainQueue/ExplainV2Response';
import {
  IAuditErrorCategory,
  IAuditErrorCategoryInput,
} from '@scale/shared/types/auditErrorCategories';
import { IQueryCorpJobs } from '@scale/shared/types/corp';
import {
  GenAIOpsHubPaths,
  MainSidebarMenu,
  LookupSearchName,
} from 'react-scale/corp/views/CorpProjectGroupManager/constants';
import { matchesLookupId, parseEmails } from 'react-scale/corp/lib/parseInputText';
import {
  IProjectBillingSetup,
  BillingApprovalRequestResponse,
} from '@scale/shared/types/projectParameterGroup';
import {
  TeamAssignmentsExtrasResponse,
  HydratedWorkerTeamEvent,
  TeamContributorsResponse,
} from '@scale/shared/types/api/corp-api/workerTeams';
import { ModelRegistryEndpoint } from '@scale/shared/types/modelRegistry/modelRegistryEndpoint';
import { Event, logEvent } from 'react-scale/lib/events';
import { SandboxUsersCreateConfig } from 'react-scale/corp/views/CorpProjectGroupManager/views/ProjectSetup/ProjectSetupStore';
import { SelectOption } from 'react-scale/corp/components/UI/Select';
import { DeliveryJSONSchema } from '@scale/shared/types/schemaRegistry/deliveryJSONSchema';
import { IUseRedashQueryProps, getQueryId } from '@scale/shared/types/redash';
import { routeForUser } from 'react-scale/corp/views/CorpProjectGroupManager/apps/Allocation/constants/nav';
import { CorpCommand } from '@scale/shared/corp/helpers';
import {
  IFetchSkillEntriesParams,
  IFetchWorkerSkillTaskDataParams,
  IFetchWorkerSkillTaskDataResponse,
  CorpProfileProps,
  IUserJson,
} from '@scale/shared/types/user';
import {
  ArchieQCRequirementSpecDocInput,
  ArchieAssistantActions,
  ClarificationQA,
  ArchieQCDisagreementData,
  CustomData,
  ArchieQCEnrichedEvalOutput,
} from '@scale/shared/archieQC/types';
import { GetPayPerTaskInfoResponse } from '@scale/shared/pay/payPerTask';
import { GetProjectPayConfigsResponse } from '@scale/shared/pay/projectPayConfigAPIs';
import { CorpJobStatus } from '@scale/shared/types/corpJobs/CorpJobStatus';
import {
  CreateQuestionBankRequest,
  CreateQuestionRequest,
  UpdateQuestionRequest,
  UpdateQuestionBankRequest,
  QuestionProjectInfo,
} from '@scale/shared/questionBank';
import {
  CreateCompletionRequest,
  CreateCompletionResponse,
  Message,
} from '@scale/shared/types/chat-completion';
import { CreateTagParams } from '@scale/shared/types/tag';
import { FreeTextGradingType, IGraderConfig } from '@scale/shared/types/params/freeTextGrading';
import {
  ProjectPriorityLayer,
  GetEligibleContributorsRequestDTO,
  GetEligibleContributorsResponseDTO,
  DirectAssignContributorsRequestDTO,
  GetWorkerSkillSupplyImpactRequestDTO,
  GetWorkerSkillSupplyImpactResponseDTO,
  AssignOrMigrateAndAssignResponseDTO,
  AssignOrMigrateAndAssignRequestDTO,
} from '@scale/shared/projectPriorities';
import { BenchmarkCandidate } from 'react-scale/corp/views/CorpProjectGroupManager/views/TaskPreview/TaskPreviewAddBenchmarkForm';
import { QualificationListJSON } from '@scale/shared/types/qualification/types';
import {
  TnsAuditJson,
  AuditStatus,
  AuditType,
  AuditOutcomeEnum,
  SuspiciousTaskAttemptAudit,
} from '@scale/shared/trustAndSafety/tnsAudits/types';
import { QuickGrabTasksResponse } from '@scale/shared/quickGrab/types';
import {
  GetProjectSquadTaskStatsResponse,
  GetSquadMemberMetadataResponse,
  GetSquadResponse,
  CreateProjectSquadResult,
  UpdateProjectSquadResult,
  ArchiveProjectSquadResult,
  SquadUsersResult,
  GetSquadMemberAttemptsResponse,
  SquadCSVUploadDataRow,
  SquadCSVUpdateDataRow,
  ParsedSquadCSVUploadDataRow,
  ParsedSquadCSVUpdateDataRow,
  ValidateBulkCreateSquadInputResult,
  ValidateBulkUpdateSquadInputResult,
  BulkCreateSquadsResult,
  BulkUpdateSquadsResult,
  GetUserProjectOwnershipResult,
  MaybeAssignableSquad,
  AssignSquadsToProjectResult,
  ValidateUnassignSquadFromProjectResponse,
  SquadStats,
  ProjectInfo,
  GetSquadTaskStatsResponse,
  GetSquadAuditsResponse,
  GetSquadPendingTasksResponse,
  GetSquadHealthResponse,
  RerunQueueAndGetHealthRequest,
  GetProjectSquadsHealthResponse,
  UserLevelHealthStatus,
  CreateProjectSquadParams,
  UpdateProjectSquadParams,
} from '@scale/shared/types/expertManagement';
import {
  LintRule,
  ScriptLinterCondition,
  LinterRun,
  LinterPerformance,
  LinterCostsResponse,
  LinterCostsRequest,
  AttemptId,
  UpdateLinterWithAgentStreamRequest,
} from '@scale/shared/types/linter';
import { TaskAttemptPreviewCoursesAPIResponse } from '@scale/shared/types/courseV2';
import { deliveryApiFetch } from 'react-scale/corp/components/ChatDeliveryBuilder/deliverySender/deliverySenderApiClient';
import { FilterGroup } from '@scale/compass-shared/filter';
import {
  CopilotRequirement,
  CopilotCheckSeverity,
  ExpectedCopilotResult,
  CopilotStatsTimeFilter,
  CopilotLastKRunsFilter,
  CopilotMetricsMode,
  CopilotResult,
  ArchieCopilotCacheMode,
  CopilotCheck,
} from '@scale/shared/types/quality/contributorCopilot';
import { getComponentScoresByAttemptAuditId } from 'react-scale/components/audits/ChatBulkAudit/ChatBulkAuditApi';
interface TaskAttemptAudit extends TaskAttemptLookupJson {
  subtask: TaskAttemptLookupJson['subtask'] & { id: string };
  responses: SpeedAuditLookupResponse[];
}

export type IGetAttemptByReviewLevel = {
  [key: string]: TaskAttemptInfo[];
};

export interface IFullstoryResponse {
  UserId: number;
  SessionId: number;
  CreatedTime: number;
  FsUrl: string;
  CanonicalId: string;
}

interface FilterOption {
  id: string;
  name: string;
}

export interface CustomTaskFilters {
  primary: string;
  secondary?: string;
  tertiary?: string | string[];
  secondaryOptions?: FilterOption[];
  tertiaryOptions?: FilterOption[];
}

export interface PipelineNodeFilters {
  batchId?: string;
  specializations?: string[];
  workerSkillSpecializations?: string[];
  creationDateRange?: Date[];
  updatedDateRange?: Date[];
  workStarted?: boolean;
}

export interface IGetByReviewLevel {
  projectId: string;
  dateRange: Date[];
  filters: {
    filterScaleUsers: boolean;
    filterRemotasksUsers: boolean;
    workerSpecialization?: string;
  };
}

interface FetchTasksResponse {
  allIds: Record<string, string[]>;
  nonAuditedIds: Record<string, string[]>;
  reviewLevelCounts: Record<string, number>;
}

interface CustomFilter {
  primary: string;
  secondary?: string;
  tertiary?: string | string[];
  secondaryOptions?: FilterOption[];
  tertiaryOptions?: FilterOption[];
}

export class CommunicationService {
  // API call to fetch task preview for customer visibility
  static fetchTaskPreviewForCustomerVisibility = async ({
    taskId,
    deliveryBuilderConfig,
  }: {
    taskId: string;
    deliveryBuilderConfig?: any;
  }) => {
    const result = await deliveryApiFetch(
      'corp-api/delivery-builder/fetch-task-preview-customer-visibility-tab',
      {
        method: 'POST',
        body: {
          taskId,
          deliveryBuilderConfig: deliveryBuilderConfig || null,
        },
      },
    );

    return result;
  };

  // Example for error testing.
  static getDevError = async (projectId: string, status: DevErrorStatus) => {
    const url = `corp-api/corp-project-group-manager/dev-errors-testing`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
        query: {
          projectId,
          status,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Some descriptive user-facing message about fetching. This will appear in the user-facing error toast.',
        e.response?.message ?? e.toString(), // Error message returned from server.
        'Some specific client context about fetching for debugging.',
        url,
      );
    }
  };

  // Example for error testing.
  static mutateDevError = async (status: DevErrorStatus, project: any) => {
    const url = `corp-api/corp-project-group-manager/dev-errors-testing`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          status,
          project,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Some descriptive user-facing message about mutating. This will appear in the user-facing error toast.',
        e.response?.message ?? e.toString(), // Error message returned from server.
        'Some specific client context about mutating for debugging.',
        url,
      );
    }
  };

  static toggleBPOAdmin = async (userId: string, isBPOAdmin: boolean) => {
    const url = `corp-api/lookup/${userId}/runCommand/bpoAdminToggle`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { isBPOAdmin },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Error toggling BPO admin for user ${userId}.`,
        e.message ?? e.toString(),
        `Error toggling BPO admin for user ${userId}.`,
        url,
      );
    }
  };

  static updateWhiteListSettings = async (userId: string, ipWhitelist: string[]) => {
    const url = `corp-api/lookup/${userId}/runCommand/setWorkerIpWhitelist`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { ipWhitelist },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Error while updating whitelist settings for user: ${userId}`,
        e.response?.message ?? e.toString(),
        `Error while updating whitelist settings for user: ${userId}`,
        url,
      );
    }
  };

  static searchTags = async (search: string) => {
    const url = 'corp-api/tags';
    try {
      const results = await apiFetch(null, url, {
        query: { search },
      });
      return results?.tags;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Error while fetching tags for search: ${search}`,
        e.response?.message ?? e.toString(),
        `Error while fetching tags for search: ${search}`,
        url,
      );
    }
  };

  static updateTags = async ({
    userId,
    tagsToAdd,
    tagsToRemove,
  }: {
    userId: string;
    tagsToAdd: string[];
    tagsToRemove: string[];
  }) => {
    const url = `corp-api/tags/updateUserTags/${userId}`;
    try {
      const results = await apiFetch(null, url, {
        method: 'POST',
        body: { tagsToAdd, tagsToRemove },
      });
      return results?.tags;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Error while updating tags for user: ${userId}`,
        e.response?.message ?? e.toString(),
        `Error while updating tags for user: ${userId}`,
        url,
      );
    }
  };

  static updateSingleTag = async ({
    tagId,
    tag,
  }: {
    tagId: string;
    tag: {
      description: string;
      trustedTagMetadata: { qualityType: string; projects: string[]; workerSkills: string[] };
    };
  }) => {
    const url = `corp-api/tags/${tagId}`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: tag,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Error while updating tag: ${tagId}`,
        e.response?.message ?? e.toString(),
        `Error while updating tag: ${tagId}`,
        url,
      );
    }
  };

  static createTag = async (createTagParams: CreateTagParams) => {
    const url = `corp-api/tags`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: createTagParams,
      });
    } catch (e) {
      let text = `Error while creating tag ${createTagParams.name}`;
      if (createTagParams.userId) {
        text += ` for user: ${createTagParams.userId}`;
      }
      throw new EnrichedError(e.status, text, e.response?.message ?? e.toString(), text, url);
    }
  };

  static testFreeTextGrading = async ({
    graderConfigs,
    variables,
    type,
  }: {
    graderConfigs: IGraderConfig;
    variables: Record<string, string>;
    type: FreeTextGradingType;
  }) => {
    const url = `internal/freeTextGrading/testFreeTextGrading`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          graderConfigs,
          variables,
          type,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't test free text grading configuration.",
        e.response?.message ?? e.toString(),
        'Error testing free text grading configuration.',
        url,
      );
    }
  };

  static generateCourse = async ({
    text,
    courseSections,
  }: {
    text: string;
    courseSections?: CourseSectionV2[];
  }) => {
    const url = `internal/courseGeneration/generateCourse`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          text,
          courseSections,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't generate course sections.",
        e.response?.message ?? e.toString(),
        'Error generating course sections.',
        url,
      );
    }
  };

  static changeBPOUniversalStaffingPermission = async (userId: string, allow: boolean) => {
    const url = allow
      ? `corp-api/allow_universal/${userId}`
      : `corp-api/disallow_universal/${userId}`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Error while updating universal staffing permission for user: ${userId}. To update this the user has to be BPO admin.`,
        e.response?.message ?? e.toString(),
        `Error while updating universal staffing permission for user: ${userId}`,
        url,
      );
    }
  };

  static deleteUserAuthenticators = async (userId: string) => {
    const url = `corp-api/lookup/${userId}/runCommand/deleteUserAuthenticators`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't delete user authenticators for ${userId}.`,
        e.response?.message ?? e.toString(),
        `Error deleting user authenticators ${userId}`,
        url,
      );
    }
  };

  static getLinkedinVanityName = async (userId: string) => {
    const url = `corp-api/linkedin/linkedin_vanity_name/${userId}`;
    try {
      const response = await apiFetch(null, url, {
        method: 'GET',
      });
      return response;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't get Linkedin vanity name for ${userId}.`,
        e.response?.message ?? e.toString(),
        `Error getting Linkedin vanity name for ${userId}`,
        url,
      );
    }
  };

  static findProjectGroupNode = async (
    projectId: string,
  ): Promise<CorpProjectGroupNode | undefined> => {
    const url = `corp-api/corp-project-group-manager/projectGroupNode/${projectId}`;
    try {
      const { result } = await apiFetch<{ result?: CorpProjectGroupNode }>(null, url);
      return result;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't getting project group node.",
        e.response?.message ?? e.toString(),
        `Error getting project group node for project ID ${projectId}`,
        url,
      );
    }
  };

  static findProjectGroups = async (): Promise<CorpProjectGroupProjectGroupNode[] | undefined> => {
    const url = `corp-api/corp-project-group-manager/projectGroups`;
    try {
      const { result } = await apiFetch<{ result?: CorpProjectGroupProjectGroupNode[] }>(null, url);
      return result;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get project groups.",
        e.response?.message ?? e.toString(),
        'Error getting project groups',
        url,
      );
    }
  };

  static updateProjectGroupConfig = async ({
    projectGroupNodeId,
    configChanges,
    filteredChildren,
  }: {
    projectGroupNodeId: string;
    configChanges: any;
    filteredChildren: string[];
  }): Promise<CorpProjectGroupProjectGroupNode[] | undefined> => {
    const url = `corp-api/corp-project-group-manager/projectGroupConfigs`;
    try {
      const result = await apiFetch(null, url, {
        method: 'POST',
        body: { projectGroupNodeId, configChanges, filteredChildren },
      });
      return result;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get project group configs.",
        e.response?.message ?? e.toString(),
        `Error getting project group configs for projectGroupNode ID ${projectGroupNodeId}`,
        url,
      );
    }
  };

  static findUnclaimedSubstasksResponse = async (projectId: string) => {
    const url = `corp-api/lookup/${projectId}/unclaimedSubtasksRteReservation`;
    try {
      return apiFetch<UnclaimedSubtasksResponse>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get unclaimed subtasks response.",
        e.response?.message ?? e.toString(),
        `Error getting unclaimed subtasks response for project ID ${projectId}`,
        url,
      );
    }
  };

  // TODO imo we should update this to send only the task/attempt id here instead of the whole object, that will require changes in the endpoint of course
  static translateTask = async (task: any): Promise<ITranslateTaskResponse> => {
    const url = `internal/genai/translateTask`;
    const res = await apiFetch<ITranslateTaskResponse>(null, url, {
      method: 'POST',
      body: {
        task,
      },
    });
    return res;
  };

  static getTaskAttemptForTaskLookup = async (taskId: string) => {
    const url = `corp-api/lookup/${taskId}/taskAttempts`;
    try {
      return await apiFetch<any>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get task attempts for task lookup.",
        e.response?.message ?? e.toString(),
        `Error getting task attempts for task ID ${taskId}`,
        url,
      );
    }
  };

  static getLinkedProjectOptionsFromIds = async (projectIds: string[]) => {
    const url = `/corp-api/billing-hub/project/linked-projects-names`;
    const results = await apiFetch(null, url, { query: { projectIds } });
    return results.projects as SelectOption[];
  };

  static fetchNodeSummary = async (projectId: string, filters: PipelineNodeFilters) => {
    const baseUrl = `corp-api/lookup/${projectId}/pipelineV3HumanNodesSummary`;
    const params = new URLSearchParams();

    if (filters.batchId) {
      params.append('batchId', filters.batchId);
    }
    if (filters.specializations) {
      params.append('specializations', filters.specializations.join(','));
    }
    if (filters.workerSkillSpecializations) {
      params.append('workerSkillSpecializations', filters.workerSkillSpecializations.join(','));
    }
    if (filters.creationDateRange) {
      params.append('creationDateRange', filters.creationDateRange.toString());
    }
    if (filters.updatedDateRange) {
      params.append('updatedDateRange', filters.updatedDateRange.toString());
    }
    if (filters.workStarted !== undefined) {
      params.append('workStarted', filters.workStarted.toString());
    }

    const url = `${baseUrl}?${params.toString()}`;
    try {
      return await apiFetch<PipelineV3HumanNodesSummaryResponse>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get pipeline nodes summary.",
        e.response?.message ?? e.toString(),
        `Error getting pipelineV3HumanNodesSummary for project ID ${projectId}`,
        url,
      );
    }
  };

  static fetchPendingNodeSummary = async (projectId: string) => {
    const url = `corp-api/lookup/${projectId}/pipelineV3PendingNodesSummary`;
    try {
      return await apiFetch<PipelineV3PendingNodesResponse>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get pending pipeline nodes summary.",
        e.response?.message ?? e.toString(),
        `Error getting pipelineV3HumanNodesSummary for project ID ${projectId}`,
        url,
      );
    }
  };

  static fetchBenchmarkStatistics = async (
    projectId: string,
    startDate?: Date,
    endDate?: Date,
    workerSpecialization?: string,
  ) => {
    const url = '/corp-api/qm/benchmark-project-metrics/benchmark-statistics';
    try {
      return await apiFetch(null, url, {
        method: 'GET',
        query: { projectId, startDate, endDate, workerSpecialization },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get benchmark statistics.",
        e.response?.message ?? e.toString(),
        `Error getting benchmark statistics for project ID ${projectId}`,
        url,
      );
    }
  };

  static getAttemptsByReviewLevel = async (
    props: IGetByReviewLevel,
  ): Promise<IGetAttemptByReviewLevel> => {
    const { projectId, dateRange, filters } = props;
    const url = `internal/self_serve/qualityProfile/getTaskAttemptInfoByProjectAndDateRange`;
    try {
      const allAttempts = await apiFetch(null, url, {
        method: 'GET',
        query: {
          projectId,
          dateRange,
          filterScaleUsers: filters.filterScaleUsers,
          filterRemotasksUsers: filters.filterRemotasksUsers,
          workerSpecialization: filters.workerSpecialization,
        },
      });
      const attemptsGroupedByReviewLevel: IGetAttemptByReviewLevel = _.groupBy(
        allAttempts,
        'attemptedAtReviewLevel',
      );
      return attemptsGroupedByReviewLevel;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get task attempts.",
        e.response?.message ?? e.toString(),
        `Error getting task attempt info for project ID ${projectId}`,
        url,
      );
    }
  };

  static updateManualAuditStatus = async ({
    projectId,
    changes,
  }: {
    projectId: string;
    changes: Record<string, { auditStatus: QualityProfileAuditStatus }>;
  }): Promise<{ success: boolean }> => {
    const url = `internal/self_serve/qualityProfile/manual_updates_by_tasker`;
    try {
      return await apiFetch<{ success: boolean }>(null, url, {
        method: 'PUT',
        body: {
          projectId,
          changes,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Issue updating manual audit status.',
        e.response?.message ?? e.toString(),
        `Error updating manual audit status for project ID ${projectId}`,
        url,
      );
    }
  };

  static getDisputeCategoryDefinitions = async () => {
    const url = 'internal/self_serve/qualityProfile/getDisputeCategoryDefinitions';
    try {
      const x = await apiFetch(null, url, { method: 'GET' });
      return x;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to get dispute category definitions',
        e.response?.message ?? e.toString(),
        `Error getting dispute category definitions.`,
        url,
      );
    }
  };

  static isUserFromQCTeam = async (): Promise<{ isQCUser: boolean }> => {
    const url = `internal/self_serve/qualityProfile/isUserFromQCTeam`;
    try {
      const response = await apiFetch(null, url, {
        method: 'GET',
        query: {},
      });
      return response;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't define if user is QC or not.",
        e.response?.message ?? e.toString(),
        'Error getting user QC status.',
        url,
      );
    }
  };

  static updateQualitySignalValidations = async ({
    changes,
  }: {
    changes: Record<string, Partial<IQualityValidationJson>>;
  }): Promise<{ success: boolean }> => {
    const url = `internal/self_serve/qualityProfile/bulkUpdateQualityValidations`;
    try {
      return await apiFetch<{ success: boolean }>(null, url, {
        method: 'POST',
        body: {
          qualityValidationChanges: Object.entries(changes).map(([key, value]) => ({
            ...value,
            attemptAuditId: key,
          })),
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Issue creating/updating quality validations.',
        e.response?.message ?? e.toString(),
        `Error creating/updating quality validations.`,
        url,
      );
    }
  };

  static createOrUpdateQualitySignalVerification = async ({
    changes,
  }: {
    changes: Record<string, QualitySignalVerification>;
  }): Promise<{ success: boolean }> => {
    const url = `internal/self_serve/qualityProfile/bulkCreateOrUpdateQualitySignalVerifications`;
    try {
      return await apiFetch<{ success: boolean }>(null, url, {
        method: 'POST',
        body: {
          signals: Object.values(changes),
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Issue creating/updating quality signal verifications.',
        e.response?.message ?? e.toString(),
        `Error creating/updating quality signal verifications.`,
        url,
      );
    }
  };

  static getComponentValidations = async ({
    attemptAuditId,
  }: {
    attemptAuditId: string;
  }): Promise<IQualityValidationComponentJson[]> => {
    const url = `internal/self_serve/qualityProfile/componentValidations/${attemptAuditId}`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get component validations.",
        e.response?.message ?? e.toString(),
        `Error getting component validations for attempt audit ID ${attemptAuditId}.`,
        url,
      );
    }
  };

  static updateComponentValidations = async ({
    updatedFields,
    attemptAuditId,
    auditRubricId,
  }: {
    updatedFields: (Partial<IQualityValidationComponentJson> & {
      attemptAuditScoreId: string;
    })[];
    attemptAuditId: string;
    auditRubricId: string;
  }) => {
    const url = `internal/self_serve/qualityProfile/componentValidations/${attemptAuditId}/${auditRubricId}`;
    try {
      return await apiFetch(null, url, {
        method: 'PUT',
        body: updatedFields,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't update component validations.",
        e.response?.message ?? e.toString(),
        `Error updating component validations.`,
        url,
      );
    }
  };

  static createProject = async (reqBody: GenAIProjectSetupRequest) => {
    const url = `corp-api/corp-project-group-manager/projectSetup`;
    try {
      return await apiFetch<GenAIProjectSetupResponse>(null, url, {
        method: 'POST',
        body: reqBody,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Issue creating project: ${reqBody.projectName}.`,
        e.response?.message ?? e.toString(),
        `Error creating project: ${reqBody.projectName}.`,
        url,
      );
    }
  };

  // [SandboxTestProject]
  static createTasks = async (body: { projectId: string; numberOfTasks?: number }) => {
    const url = `corp-api/corp-project-group-manager/taskCreation`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't create tasks.",
        e.response?.message ?? e.toString(),
        'Error creating tasks.',
        url,
      );
    }
  };

  // [SandboxTestProject]
  static createSandboxTestUsers = async (body: {
    projectId: string;
    sandboxUsersCreateConfigs: SandboxUsersCreateConfig[];
  }) => {
    const url = `corp-api/corp-project-group-manager/sandbox/test_users`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't create Sandbox test users.",
        e.response?.message ?? e.toString(),
        'Error creating sandbox test users.',
        url,
      );
    }
  };

  static getProjectIsEligibleForApiV2 = async ({
    projectId,
    taskIds,
    templateVersions,
  }: {
    projectId: string;
    taskIds?: string[];
    templateVersions?: number[];
  }) => {
    const url = `corp-api/delivery-builder/projects/${projectId}/api_v2_eligibility`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { taskIds, templateVersions },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Error getting project is eligible for api v2.',
        e.response?.message ?? e.toString(),
        `Error getting project is eligible for api v2 for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getApiV2FlattenedTasks = async ({
    taskIds,
    deliveryBuilderConfigId,
  }: {
    taskIds: string[];
    deliveryBuilderConfigId: string;
  }) => {
    const url = `corp-api/delivery-builder/delivery_formatter/get_api_v2_formatted_tasks`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { taskIds, deliveryBuilderConfigId },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Error getting formatted tasks for api v2.',
        e.response?.message ?? e.toString(),
        `Error getting formatted tasks for api v2`,
        url,
      );
    }
  };

  static importProject = async (projectId: string, taskType?: TaskType) => {
    const url = `corp-api/corp-project-group-manager/importProject`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { projectId, taskType },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't import project with project ID ${projectId}.`,
        e.response?.message ?? e.message ?? e.toString(),
        `Error importing project with project ID ${projectId}.`,
        url,
      );
    }
  };

  static getProjectSetupDefaultTemplates = async () => {
    const url = `corp-api/corp-project-group-manager/projectSetupDefaultTemplates`;
    try {
      const projectIdToTaskTemplate = await apiFetch(null, url, {
        method: 'GET',
        query: {},
      });
      return projectIdToTaskTemplate.templates;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get default templates for project setup.",
        e.response?.message ?? e.toString(),
        'Error getting default templates for project setup.',
        url,
      );
    }
  };

  static getAttemptsByReviewLevelByDate = async (
    props: IGetByReviewLevel,
  ): Promise<LineChartDataType> => {
    const { projectId, dateRange, filters } = props;
    const url = `internal/self_serve/qualityProfile/getTaskAttemptsAverageGroupedByDay`;
    try {
      const filteredAndGrouped = await apiFetch(null, url, {
        method: 'GET',
        query: {
          projectId,
          dateRange,
          filterScaleUsers: filters.filterScaleUsers,
          filterRemotasksUsers: filters.filterRemotasksUsers,
        },
      });
      return filteredAndGrouped;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get task attempts in the given date range.",
        e.response?.message ?? e.toString(),
        `Error getting task attempts for project ID ${projectId} in date range ${dateRange}.`,
        url,
      );
    }
  };

  static getWorkerSkillEntriesByWorkerId = async ({
    workerId,
    filter,
  }: IFetchSkillEntriesParams): Promise<Record<string, any>[]> => {
    const url = 'internal/self_serve/qualityProfile/getWorkerSkillWithMetadataByWorkerId';
    try {
      const res = await apiFetch(null, url, {
        method: 'GET',
        query: {
          workerId,
          filter,
        },
      });

      return res;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Could not get worker skill entries.',
        e.response?.message ?? e.toString(),
        `Error getting worker skill entries by worker ID ${workerId}.`,
        url,
      );
    }
  };

  static getWorkerSkillTaskData = async ({
    workerId,
    workerSkillId,
  }: IFetchWorkerSkillTaskDataParams): Promise<IFetchWorkerSkillTaskDataResponse[]> => {
    const url = 'internal/self_serve/qualityProfile/getWorkerSkillTaskData';
    try {
      const { result }: { result: IFetchWorkerSkillTaskDataResponse[] } = await apiFetch(
        null,
        url,
        {
          method: 'GET',
          query: {
            workerId,
            workerSkillId,
          },
        },
      );

      return result;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Could not get worker skill task data.',
        e.response?.message ?? e.toString(),
        `Error getting worker skill task data by worker ID ${workerId} and skill ${workerSkillId}.`,
        url,
      );
    }
  };

  static findProjectConfigs = async (projectIds: string[], configsToFetch?: string[]) => {
    const url = 'internal/config/bulk_project_configs/find';
    try {
      return await apiFetch(null, url, {
        method: 'GET',
        query: { projectIds, configsPaths: configsToFetch?.join(',') },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get project configs.",
        e.response?.message ?? e.toString(),
        `Error getting project configs for project IDs ${projectIds.toString()}.`,
        url,
      );
    }
  };

  static findProjectTaskers = async (projectId: string, query: SpecializedReviewTaskerQuery) => {
    const url = `corp-api/specialized-review/${projectId}/taskers`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
        query,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get specialized review taskers.",
        e.response?.message ?? e.toString(),
        `Error getting specialized review taskers for project ID ${projectId}.`,
        url,
      );
    }
  };

  static findProjectExampleSubtasks = async (projectId: string) => {
    const url = `corp-api/specialized-review/${projectId}/example-subtasks`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get example subtasks for specialized review.",
        e.response?.message ?? e.toString(),
        `Error getting example subtasks for specialized review for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getProjectById = async (projectId: string): Promise<IProjectJson> => {
    const url = `corp-api/projects/${projectId}`;
    try {
      return await apiFetch<IProjectJson>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get projects.",
        e.response?.message ?? e.toString(),
        `Error getting project name for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getProjectsByIds = async (projectIds: string[]): Promise<ProjectNameProps[]> => {
    const url = `corp-api/corp-project-group-manager/projectsNames`;
    try {
      const result = await apiFetch(null, url, {
        method: 'GET',
        query: { projectIds },
      });
      return result;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get projects.",
        e.response?.message ?? e.toString(),
        `Error getting project names for project IDs ${projectIds.toString()}.`,
        url,
      );
    }
  };

  static getSLAGroupByProjectId = async (projectId: string) => {
    const url = `corp-api/project-groups?projectId=${projectId}`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get SLA group for project.",
        e.response?.message ?? e.toString(),
        `Error getting SLA group for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getConfigMetadata = async () => {
    const url = 'https://scale-static-assets.s3.us-west-2.amazonaws.com/configMetadata.json';
    try {
      const res = await fetch(url);
      return res.json();
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get config metadata.",
        e.response?.message ?? e.toString(),
        `Error getting config metadata from Scale static assets.`,
        url,
      );
    }
  };

  static getConfigData = async (
    configMeta: IConfigMeta,
    configAttributes: ConfigAttributes,
    configsPaths?: string,
  ) => {
    try {
      return await fetchConfig(configMeta, configAttributes, configsPaths);
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get config data.",
        e.response?.message ?? e.toString(),
        'Error getting config data from fetchConfig.',
        '',
      );
    }
  };

  static getQMProject = async (projectId: string) => {
    const url = `corp-api/qm/projects/${projectId}`;
    try {
      return await apiFetch<QMShowProjectResponse>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get project.",
        e.response?.message ?? e.toString(),
        `Error getting project ID ${projectId}.`,
        url,
      );
    }
  };

  static getProjectDefaultErrorCategories = async (projectId: string) => {
    const url = `corp-api/auditErrorCategories/defaultErrorCategories/${projectId}`;
    try {
      const response = await apiFetch<{ errorCategories: IAuditErrorCategory[] }>(null, url, {
        method: 'GET',
      });
      return response.errorCategories;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get project default error categories.",
        e.response?.message ?? e.toString(),
        `Error getting project default error categories for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getProjectSpecificErrorCategories = async (projectId: string) => {
    const url = `corp-api/auditErrorCategories/projectSpecificErrorCategories/${projectId}`;
    try {
      const response = await apiFetch<{ errorCategories: IAuditErrorCategory[] }>(null, url, {
        method: 'GET',
      });
      return response.errorCategories;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get project specific error categories.",
        e.response?.message ?? e.toString(),
        `Error getting project specific error categories for project ID ${projectId}.`,
        url,
      );
    }
  };

  static addProjectSpecificErrorCategory = async (
    projectId: string,
    category: IAuditErrorCategoryInput,
  ) => {
    const url = `corp-api/auditErrorCategories/${projectId}/add`;
    try {
      const response = await apiFetch<IAuditErrorCategory[]>(null, url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(category),
      });
      return response;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't add error category.",
        e.response?.message ?? e.toString(),
        `Error adding error category for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getUseDefaultErrorCategories = async (projectId: string) => {
    const url = `corp-api/auditErrorCategories/shouldUseDefaultErrorCategories/${projectId}`;
    try {
      const response = await apiFetch<{ useDefaultErrorCategories: boolean }>(null, url, {
        method: 'GET',
      });
      return response.useDefaultErrorCategories;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get use default error categories.",
        e.response?.message ?? e.toString(),
        `Error getting use default error categories for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getProjectFieldIds = async (projectId: string) => {
    const url = `corp-api/auditErrorCategories/fieldIds/${projectId}`;
    try {
      const response = await apiFetch<{ fieldIds: string[] }>(null, url, {
        method: 'GET',
      });
      return response.fieldIds;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get project field ids.",
        e.response?.message ?? e.toString(),
        `Error getting project field ids for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getProjectCostsByDate = async (fromDate: Date, toDate: Date, projectId?: string) => {
    const url = `corp-api/corp-project-group-manager/projectCosts`;
    try {
      return await apiFetch<ProjectCostsResponse>(null, url, {
        method: 'GET',
        query: { fromDate, toDate, projectId },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't get project costs in date range ${fromDate} to ${toDate}.`,
        e.response?.message ?? e.toString(),
        `Couldn't get project costs for project ID ${projectId} in date range ${fromDate} to ${toDate}.`,
        url,
      );
    }
  };

  static getProjectCostsCumulative = async (projectId: string) => {
    const url = `corp-api/corp-project-group-manager/projectCosts/cumulative`;
    try {
      return await apiFetch<ProjectCostsCumulativeResponse>(null, url, {
        method: 'GET',
        query: { projectId },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get cumulative project costs.",
        e.response?.message ?? e.toString(),
        `Error getting cumulative project costs for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getProjectCostsPerTask = async (projectId: string, forceUpdate = false) => {
    const url = `corp-api/corp-project-group-manager/projectCosts/perTask`;
    try {
      return await apiFetch<ProjectCostsPerTaskResponse>(null, url, {
        method: 'GET',
        query: { projectId, forceUpdate },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get project costs per task.",
        e.response?.message ?? e.toString(),
        `Error getting project costs per task for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getProjectCostPerAttemptByReviewLevel = async (projectId: string) => {
    const url = `corp-api/corp-project-group-manager/projectCosts/costPerAttemptByReviewLevel`;
    try {
      return await apiFetch<ProjectCostPerAttemptLevelResponse>(null, url, {
        method: 'GET',
        query: { projectId },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get project costs per attempt/review level.",
        e.response?.message ?? e.toString(),
        `Error getting project costs per attempt/review level for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getProjectCostsYesterdayAndToday = async (projectId: string) => {
    const url = `corp-api/corp-project-group-manager/projectCosts/daily`;
    try {
      return await apiFetch<ProjectCostsDailyResponse>(null, url, {
        method: 'GET',
        query: { projectId },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get daily project costs.",
        e.response?.message ?? e.toString(),
        `Error getting daily project costs for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getProjectFinanceAlertConfigs = async (projectId: string) => {
    const url = `corp-api/corp-project-group-manager/projectAlerts/financeAlertConfigs`;
    try {
      return await apiFetch<FinanceAlertConfigResponse>(null, url, {
        method: 'GET',
        query: { projectId },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get finance alert configs for project.",
        e.response?.message ?? e.toString(),
        `Error getting finance alert configs for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getFinanceGlobalConfigs = async () => {
    const url = `corp-api/corp-project-group-manager/admin/financeGlobalConfigs`;
    try {
      return await apiFetch<FinanceGlobalConfigResponse>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get finance global configs.",
        e.response?.message ?? e.toString(),
        'Error getting finance global configs.',
        url,
      );
    }
  };

  static getFinanceGuardrailConfigs = async (projectId: string) => {
    const url = `corp-api/corp-project-group-manager/admin/financeGuardrailConfigs/${projectId}`;
    try {
      return await apiFetch<FinanceGuardrailConfigResponse>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get finance guardrail configs.",
        e.response?.message ?? e.toString(),
        `Error getting finance guardrail configs for project ID ${projectId}.`,
        url,
      );
    }
  };

  static updateFinanceAlertConfig = async (config: FinanceAlertConfig) => {
    const url = `corp-api/corp-project-group-manager/projectAlerts/financeAlertConfigs`;
    try {
      return apiFetch<FinanceAlertConfigResponse>(null, url, {
        method: 'PUT',
        body: config,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get finance alert configs.",
        e.response?.message ?? e.toString(),
        `Error getting finance alert configs for project ID ${config.projectId}.`,
        url,
      );
    }
  };

  static updateFinanceGuardrailConfig = async (config: FinanceGuardrailConfig) => {
    const url = `corp-api/corp-project-group-manager/projectAlerts/financeGuardrailConfigs`;
    try {
      return apiFetch<FinanceGlobalConfigResponse>(null, url, {
        method: 'PUT',
        body: config,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't update finance guardrail configs.",
        e.response?.message ?? e.toString(),
        `Error updating finance guardrail configs for project ID ${config.projectId}.`,
        url,
      );
    }
  };

  static updateProjectPauseStatus = async (
    projectId: string,
    userEmail: string,
    userId: string,
    action: PauseAction,
    automaticallyRemoveProjectRoles: boolean,
  ) => {
    const url = `corp-api/corp-project-group-manager/pauseProject`;
    try {
      return apiFetch(null, url, {
        method: 'PUT',
        body: { projectId, userEmail, userId, action, automaticallyRemoveProjectRoles },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't pause project.",
        e.response?.message ?? e.toString(),
        `Error pausing project for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getProjectPausedHistory = async (projectId: string) => {
    const url = `corp-api/corp-project-group-manager/pausedHistory/${projectId}`;
    try {
      return apiFetch<PausedHistoryResponse>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get project pause history.",
        e.response?.message ?? e.toString(),
        `Error getting project pause history for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getProjectNotificationsSlackChannel = async (projectId: string) => {
    const url = `corp-api/corp-project-group-manager/notifications/slack/${projectId}`;
    try {
      return await apiFetch<{ slackChannel: string }>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get Slack channel for project.",
        e.response?.message ?? e.toString(),
        `Error getting Slack channel for project ID ${projectId}.`,
        url,
      );
    }
  };

  static updateProjectNotificationsSlackChannel = async (
    projectId: string,
    slackChannel: string,
  ) => {
    const url = `corp-api/corp-project-group-manager/notifications/slack`;
    try {
      return await apiFetch<{ success: boolean; error?: string }>(null, url, {
        method: 'PUT',
        body: { slackChannel, projectId },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't update Slack channel for project.",
        e.response?.message ?? e.toString(),
        `Error updating Slack channel for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getManyExplainQueue = async (
    workerIds: string[],
    skipProjectIds?: string[],
    projectId?: string,
  ) => {
    return await apiFetch<{ queueResults: QueueListOutput; error?: string }>(
      null,
      `corp-api/corp-project-group-manager/manyExplainQueue`,
      {
        method: 'POST',
        body: { workerIds, projectId, skipProjectIds },
      },
    );
  };

  static getPermittedEmails = async () => {
    const url = `corp-api/random-configs?name=pod-lead-center-users`;
    try {
      return await apiFetch<{ name: string; value: any }>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get Pod Lead Center users.",
        e.response?.message ?? e.toString(),
        `Error getting Pod Lead Center users.`,
        url,
      );
    }
  };

  static getProjectBillingProposalApprovers = async (projectId: string) => {
    const url = `corp-api/corp-project-group-manager/billingProposalApprovers/${projectId}`;
    try {
      return await apiFetch<{ email: string; userId: string }[]>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't get Billing Proposal Approvers for Project : ${projectId}.`,
        e.response?.message ?? e.toString(),
        `Error getting Billing Proposal Approvers.`,
        url,
      );
    }
  };

  static getAllBillingApprovalRequests = async () => {
    const url = `corp-api/corp-project-group-manager/billingApprovalRequests`;
    try {
      return await apiFetch<BillingApprovalRequestResponse>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get all billing approval requests.",
        e.response?.message ?? e.toString(),
        `Error getting all billing approval requests.`,
        url,
      );
    }
  };

  static getAllFinanceApprovalRequests = async () => {
    const url = `corp-api/corp-project-group-manager/financeApprovalRequests`;
    try {
      return await apiFetch<FinanceApprovalRequestResponse>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get all finance approval requests.",
        e.response?.message ?? e.toString(),
        `Error getting all finance approval requests.`,
        url,
      );
    }
  };

  static getFinanceApprovalRequestsByProjectId = async (projectId: string) => {
    const url = `corp-api/corp-project-group-manager/financeApprovalRequests/${projectId}`;
    try {
      return await apiFetch<FinanceApprovalRequestResponse>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get finance approval requests for project.",
        e.response?.message ?? e.toString(),
        `Error getting finance approval requests for project ID ${projectId}.`,
        url,
      );
    }
  };

  static createFinanceApprovalRequest = async (request: SaveFinanceApprovalRequestInput) => {
    const url = `corp-api/corp-project-group-manager/financeApprovalRequests/create`;
    try {
      return await apiFetch<{ success: boolean; error?: string }>(null, url, {
        method: 'POST',
        body: { ...request },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't create finance approval request.",
        e.response?.message ?? e.toString(),
        `Error creating finance approval request for project ID ${request.projectId}.`,
        url,
      );
    }
  };

  static approveFinanceApprovalRequest = async (requestId: string, reviewerUserId: string) => {
    const url = `corp-api/corp-project-group-manager/financeApprovalRequests/approve`;
    try {
      return await apiFetch<{ success: boolean; error?: string }>(null, url, {
        method: 'PUT',
        body: { requestId, reviewerUserId },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't approval finance approval request.",
        e.response?.message ?? e.toString(),
        `Error approving finance approval request for request ID ${requestId}, reviewerUser ID ${reviewerUserId}.`,
        url,
      );
    }
  };

  static updateFinanceApprovalRequest = async (
    request: SaveFinanceApprovalRequestInput,
    id: string,
  ) => {
    const url = `corp-api/corp-project-group-manager/financeApprovalRequests/update`;
    try {
      return await apiFetch<{ success: boolean; error?: string }>(null, url, {
        method: 'PUT',
        body: { ...request, _id: id },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't update finance approval request.`,
        e.response?.message ?? e.toString(),
        `Error updating finance approval request for request ID ${id}.`,
        url,
      );
    }
  };

  static getValidExperimentQualifications = async () => {
    const url = `corp-api/scalers/qualifications/experiments/valid`;
    try {
      return await apiFetch<{ _id: string; name: string }[]>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get experiment qualifications.",
        e.response?.message ?? e.toString(),
        'Error getting experiment qualifications.',
        url,
      );
    }
  };

  static getValidExperimentGroupNames = async (experimentName: string) => {
    const url = `corp-api/scalers/qualifications/experiments/valid/group_names?experimentName=${experimentName}`;
    if (!experimentName) {
      return [];
    }
    try {
      return await apiFetch<string[]>(null, url, { method: 'GET' });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get experiment group names.",
        e.response?.message ?? e.toString(),
        `Error getting experiment group names for experiment ${experimentName}.`,
        url,
      );
    }
  };

  static getBranchingQualificationDetails = async (qualificationListId: string) => {
    const url = `corp-api/scalers/qualifications/lists/${qualificationListId}`;
    try {
      return await apiFetch<QualificationListJSON>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get branching qualification details.",
        e.response?.message ?? e.toString(),
        `Error getting branching qualification details for qualification ID ${qualificationListId}.`,
        url,
      );
    }
  };

  static createOrUpdateCourseV2 = async (data: CreateOrUpdateCourseV2Request) => {
    const url = `internal/coursesV2/courses`;
    try {
      return await apiFetch<{ success: boolean; courseId?: string; error?: string }>(null, url, {
        method: 'POST',
        body: data,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't create/update course.",
        e.response?.message ?? e.toString(),
        `Error creating/updating v2 course.`,
        url,
      );
    }
  };

  static publishCourseV2 = async (reqBody: CreateOrUpdateCourseV2Request) => {
    const url = `internal/coursesV2/publish`;
    try {
      return await apiFetch<{ success: boolean; courseId?: string; error?: string }>(null, url, {
        method: 'POST',
        body: reqBody,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't publish course: ${reqBody.name}.`,
        e.response?.message ?? e.toString(),
        `Error publishing v2 course for project ID ${reqBody.project}`,
        url,
      );
    }
  };

  static getCourseV2Results = async (courseId: string) => {
    const url = `internal/coursesV2/results/${courseId}`;
    try {
      return await apiFetch<{
        courseTitle: string;
        coursePassingGrade: number;
        numStarted: number;
        numCompleted: number;
        numPassed: number;
        isUsingCompassGrading?: boolean;
        averageActiveDuration?: number;
        averageTotalDuration?: number;
      }>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't get course results.`,
        e.response?.message ?? e.toString(),
        `Error getting course results for course ID ${courseId}.`,
        url,
      );
    }
  };

  static getCourseV2AssignedUsers = async (courseId: string) => {
    const url = `internal/coursesV2/assignedUsers/${courseId}`;
    try {
      return await apiFetch<AssignedUsersResult>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't get course assigned users.`,
        e.response?.message ?? e.toString(),
        `Error getting course assigned users for course ID ${courseId}.`,
        url,
      );
    }
  };

  static getCourseV2ProgressByWorker = async (courseId: string, workerId?: string) => {
    const url = `internal/coursesV2/progress/${courseId}${workerId ? `?workerId=${workerId}` : ''}`;
    try {
      return await apiFetch<{
        progressData: Array<{
          workerId: string;
          status: CourseStatusTypeV2;
          startedAt: Date;
          completedAt?: Date;
          score?: number;
        }>;
      }>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't get course progress data.`,
        e.response?.message ?? e.toString(),
        `Error getting course progress for course ID ${courseId}.`,
        url,
      );
    }
  };

  static getCourseV2UserStatus = async (courseId: string) => {
    const url = `internal/coursesV2/userStatus/${courseId}`;
    try {
      return await apiFetch<{ statusCounts: Record<string, number> }>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't get course user status.`,
        e.response?.message ?? e.toString(),
        `Error getting course user status for course ID ${courseId}.`,
        url,
      );
    }
  };

  static getCourseV2ProblemBreakdown = async (courseId: string) => {
    const url = `internal/coursesV2/problemBreakdown/${courseId}`;
    try {
      return await apiFetch<{
        multipleChoiceQuestionIds: string[];
        correctAnswersPerQuestion: Record<string, number>;
        incorrectAnswersPerQuestion: Record<string, number>;
        textQuestionIds: string[];
        audioQuestionIds: string[];
        allQuestionIds: string[];
        scoresPerQuestion: Record<string, { total: number; count: number }>;
        finalAverageScores: Record<string, number>;
      }>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't get course problem breakdown.`,
        e.response?.message ?? e.toString(),
        `Error getting course problem breakdown for course ID ${courseId}.`,
        url,
      );
    }
  };

  static getCourseV2Stats = async (courseId: string, workerId?: string) => {
    const url = `internal/coursesV2/stats/${courseId}${workerId ? `?workerId=${workerId}` : ''}`;
    try {
      return await apiFetch<{
        multipleChoiceQuestionIds: string[];
        questionIdToCorrectAnswer: Record<string, string[]>;
        questionIdToQuestion: Record<string, string>;
        userToAnswers: Record<string, any>;
      }>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't get course stats.`,
        e.response?.message ?? e.toString(),
        `Error getting course stats for course ID ${courseId}.`,
        url,
      );
    }
  };

  static getCourseV2UserProgress = async (
    courseId: string,
    workerId?: string,
    isCompleted?: boolean,
    page?: number,
    pageSize?: number,
    sortBy?: string,
    sortOrder?: string,
  ) => {
    const params = [];
    if (workerId) params.push(`workerId=${workerId}`);
    if (typeof isCompleted === 'boolean') params.push(`isCompleted=${isCompleted}`);
    if (page) params.push(`page=${page}`);
    if (pageSize) params.push(`pageSize=${pageSize}`);
    if (sortBy) params.push(`sortBy=${sortBy}`);
    if (sortOrder) params.push(`sortOrder=${sortOrder}`);
    const query = params.length > 0 ? `?${params.join('&')}` : '';

    const url = `internal/coursesV2/userProgress/${courseId}${query}`;
    try {
      return await apiFetch<{
        progressData: Array<{
          workerId: string;
          workerEmail?: string;
          status: CourseStatusTypeV2;
          startedAt: Date;
          completedAt?: Date;
          score?: number;
          courseAnswer?: Record<string, any>;
          v2TimeSpentSecs?: number;
          fullstorySessions?: string[];
          cheatingOutcome?: string;
          violationsDetected?: string[];
        }>;
        totalPages: number;
        totalCount: number;
      }>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't get course user progress.`,
        e.response?.message ?? e.toString(),
        `Error getting course user progress for course ID ${courseId}.`,
        url,
      );
    }
  };

  static updateCourseV2Enablement = async (reqBody: { courseId: string; disabled: boolean }) => {
    const url = `internal/coursesV2/updateEnablement`;
    try {
      return await apiFetch<{ success: boolean; message?: string; error?: string }>(null, url, {
        method: 'POST',
        body: reqBody,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't update course enablement.`,
        e.response?.message ?? e.toString(),
        `Error updating v2 course enablement for course ID ${reqBody.courseId}.`,
        url,
      );
    }
  };

  static updateCourseV2Archived = async (reqBody: {
    courseId: string;
    archived: boolean;
    updatedBy: string;
  }) => {
    const url = `internal/coursesV2/updateArchived`;
    try {
      return await apiFetch<{ success: boolean; message?: string; error?: string }>(null, url, {
        method: 'POST',
        body: reqBody,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't ${reqBody.archived ? 'archive' : 'unarchive'} course.`,
        e.response?.message ?? e.toString(),
        `Error ${reqBody.archived ? 'archiving' : 'unarchiving'} v2 course for course ID ${
          reqBody.courseId
        }. ${e.response?.message ?? e.toString()}`,
        url,
      );
    }
  };

  static getCourseV2VersionHistory = async (params: GetCourseV2VersionHistoryParams) => {
    const url = `internal/coursesV2/versionHistory/${params.courseId}`;
    try {
      return await apiFetch<GetCourseV2VersionHistoryResponse>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't get course version history.`,
        e.response?.message ?? e.toString(),
        `Error getting course version history for course ID ${params.courseId}.`,
        url,
      );
    }
  };

  static revertToCourseV2Version = async (params: PostCourseV2VersionRevertParams) => {
    const { courseId, version } = params;
    const url = `internal/coursesV2/versionHistory/${courseId}/revert/${version}`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't revert to course v2 version.`,
        e.response?.message ?? e.toString(),
        `Error reverting to course v2 version for course ID ${courseId}, version ${version}.`,
        url,
      );
    }
  };

  static rejectFinanceApprovalRequest = async (requestId: string, reviewerUserId: string) => {
    const url = `corp-api/corp-project-group-manager/financeApprovalRequests/reject`;
    try {
      return await apiFetch<{ success: boolean; error?: string }>(null, url, {
        method: 'PUT',
        body: { requestId, reviewerUserId },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't reject finance approval request.",
        e.response?.message ?? e.toString(),
        `Error rejecting finance approval request for request ID ${requestId}, reviewerUser ID ${reviewerUserId}.`,
        url,
      );
    }
  };

  static commentFinanceApprovalRequest = async (
    requestId: string,
    commenterUserId: string,
    commentString: string,
  ) => {
    const url = `corp-api/corp-project-group-manager/financeApprovalRequests/comment`;
    try {
      return await apiFetch<{ success: boolean; error?: string }>(null, url, {
        method: 'PUT',
        body: { requestId, commenterUserId, commentString },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't submit finance approval request comment.",
        e.response?.message ?? e.toString(),
        `Error submitting finance approval request comment for request ID ${requestId}, commenterUser ID ${commenterUserId}.`,
        url,
      );
    }
  };

  static cancelFinanceApprovalRequest = async (requestId: string) => {
    const url = `corp-api/corp-project-group-manager/financeApprovalRequests/cancel`;
    try {
      return await apiFetch<{ success: boolean; error?: string }>(null, url, {
        method: 'PUT',
        body: { requestId },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't cancel finance approval request.",
        e.response?.message ?? e.toString(),
        `Error canceling finance approval request for request ID ${requestId}.`,
        url,
      );
    }
  };

  static getUserAndProjectNames = async (approvalRequests: IFinanceApprovalRequest[]) => {
    const url = `corp-api/corp-project-group-manager/financeApprovalRequests/userAndProjectNames`;
    try {
      return await apiFetch<FinanceIdNameMapResponse>(null, url, {
        method: 'POST',
        body: { approvalRequests },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get finance approval request user & project names.",
        e.response?.message ?? e.toString(),
        `Error getting finance approval request user & project names.`,
        url,
      );
    }
  };

  static getAdminFinanceDefaults = async () => {
    const url = `corp-api/corp-project-group-manager/adminFinance/defaults`;
    try {
      return await apiFetch<AdminFinanceDefaultsResponse>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get admin finance defaults.",
        e.response?.message ?? e.toString(),
        'Error getting admin finance defaults.',
        url,
      );
    }
  };

  static updateAdminFinanceDefaults = async ({
    pagerDutyUserEmail,
    slackPodLeadEmails,
  }: {
    pagerDutyUserEmail: string;
    slackPodLeadEmails: string[];
  }) => {
    const url = `corp-api/corp-project-group-manager/adminFinance/defaults`;
    try {
      return await apiFetch<{ success: boolean }>(null, url, {
        method: 'PUT',
        body: { pagerDutyUserEmail, slackPodLeadEmails },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't update admin finance defaults.",
        e.response?.message ?? e.toString(),
        `Error updating admin finance defaults.`,
        url,
      );
    }
  };

  static processGuardrailUpload = async (
    request: BulkUploadGuardrailRequest,
  ): Promise<BulkUploadGuardrailResponse> => {
    const url = `corp-api/corp-project-group-manager/adminFinance/processGuardrailUpload`;
    try {
      return await apiFetch<BulkUploadGuardrailResponse>(null, url, {
        method: 'POST',
        body: request,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't process guardrail upload.",
        e.response?.message ?? e.toString(),
        `Error processing guardrail upload.`,
        url,
      );
    }
  };

  static revertAdminAction = async (actionId: string) => {
    const url = `corp-api/admin_action/${actionId}/revert`;
    try {
      return await apiFetch<{ success: true }>(null, url, {
        method: 'POST',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't revert admin action.",
        e.response?.message ?? e.toString(),
        e.response?.message ?? e.toString(),
        url,
      );
    }
  };

  static runRedashQuery = async (props: IUseRedashQueryProps) => {
    const cleanQuery = {
      queryParams: JSON.stringify(props.queryParams),
      queryUrl: getQueryId(props.queryUrl),
    };
    const url = `corp-api/redash/fetchQuery`;
    try {
      return await apiFetch<Record<string, any>[]>(null, url, {
        method: 'GET',
        query: cleanQuery,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't run redash query.",
        e.response?.message ?? e.toString(),
        e.response?.message ?? e.toString(),
        url,
      );
    }
  };

  // This is based on this file corp/components/JsonSchemaConfigs/configTransforms.ts
  static updateConfigData = async ({
    configMeta,
    configAttributes,
    lastVersion,
    changes,
    configJsonSchema,
  }: {
    configMeta: IConfigMeta;
    configAttributes: ConfigAttributes;
    configJsonSchema: any;
    lastVersion: number;
    changes: Record<string, any>;
  }) => {
    const nestedChanges: Record<string, any> = {};

    for (const valuePath in changes) {
      const value = changes[valuePath];
      const parsedValue = parseMixedJSON(value, configJsonSchema, splitDotSeparatedPath(valuePath));
      nestedChanges[valuePath] = parsedValue;
    }

    const url = `corp-api/lookup/${configMeta.id}/config`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          lastVersion,
          query: getConfigQuery(configMeta, configAttributes),
          changes: nestedChanges,
          attributes: configAttributes,
          type: configMeta.type,
          valuesOnly: true,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't update config data.",
        e.response?.message ?? e.toString(),
        `Error updating config data for configMeta ID ${configMeta.id}.`,
        url,
      );
    }
  };

  static attemptAuditLookup = async (taskId: string): Promise<TaskAttemptAudit> => {
    const url = `corp-api/attemptAudit/lookup/${taskId}`;
    try {
      return await apiFetch(null, url, { method: 'GET' });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get audit attempt for task.",
        e.response?.message ?? e.toString(),
        `Error getting audit attempt for task ID ${taskId}.`,
        url,
      );
    }
  };

  static getBatches = async (projectId: string, search?: any, status?: TaskBatchStatus) => {
    const url = `corp-api/find/batches`;
    try {
      return await apiFetch<BatchResults>(null, url, {
        query: { projectId, search, status },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get task batches.",
        e.response?.message ?? e.toString(),
        `Error getting task batches for project ID ${projectId}.`,
        url,
      );
    }
  };

  static generateInviteLinks = async (body: GenerateInviteLinksRequest) => {
    const url = `internal/genai/generateInviteLinks`;
    try {
      return await apiFetch<GenerateInviteLinksResponse>(null, url, {
        method: 'POST',
        body,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't generate invite links.",
        e.response?.message ?? e.toString(),
        `Error generating invite links for ${body.projectId}.`,
        url,
      );
    }
  };

  static getLinterRatesStats = async ({
    projectId,
    from,
    to,
  }: {
    projectId: string;
    from: Date;
    to: Date;
  }) => {
    const url = `internal/genai/linterRatesStats`;
    try {
      return await apiFetch<LinterRatesStatsResponse>(null, url, {
        method: 'GET',
        query: { projectId, from: from.toString(), to: to.toString() },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Coulnd't get linter rater stats.",
        e.response?.message ?? e.toString(),
        `Error getting linter rater stats for project ID ${projectId}.`,
        url,
      );
    }
  };

  static streamLinterWithAgent = async ({
    projectId,
    chatHistory,
    currentLintRule,
    addMessage,
  }: {
    projectId: string;
    chatHistory: Message[];
    currentLintRule: LintRule<ScriptLinterCondition>;
    addMessage: (message: Message) => void;
  }): Promise<{ history: Message[]; draftLintRule: LintRule<ScriptLinterCondition> }> => {
    const url = `internal/linters/update_linter_with_agent_stream`;

    let response: Response;
    try {
      response = await apiFetch<Response, UpdateLinterWithAgentStreamRequest>(null, url, {
        method: 'POST',
        returnRawResponse: true,
        body: { projectId, chatHistory, currentLintRule },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Stream creation failed.',
        e.response?.message ?? e.toString(),
        `Stream failed with agent for prompt ${e.toString()}.`,
        url,
      );
    }

    if (_.isNull(response.body)) {
      throw new EnrichedError(
        500,
        'Unable to open response stream.',
        'Stream body is null.',
        'Unable to open response stream.',
        url,
      );
    }

    const reader = response.body.getReader();

    try {
      let readResult = await reader.read();

      while (!readResult.done) {
        const streamedData = readResult.value;
        /** For whatever reason, the chunk can be hold a JSONL string */
        const streamingChunks = new TextDecoder()
          .decode(streamedData)
          .split(/\n\n/g)
          .map(chunk => chunk.trim())
          .filter(chunk => !_.isEmpty(chunk));

        for (const rawChunk of streamingChunks) {
          const chunk: StreamableLinterAgentChunk = JSON.parse(rawChunk);

          if (chunk.type === StreamableAgentChunkType.Completion) {
            addMessage(chunk.message);
          } else if (chunk.type === StreamableAgentChunkType.Termination) {
            return {
              history: chunk.history,
              draftLintRule: chunk.draftLintRule,
            };
          } else if (chunk.type === StreamableAgentChunkType.Error) {
            throw new Error(chunk.error);
          }
        }

        readResult = await reader.read();
      }

      // Should return if a termination chunk is found. If not, throw an error.
      throw new Error('Stream completed without a termination chunk');
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Stream reading failed.',
        e.response?.message ?? e.toString(),
        `Stream failed with agent for prompt ${e.toString()}.`,
        url,
      );
    }
  };

  static testSingleLinter = async ({
    projectId,
    attemptIds,
    localLintRule,
    stepId,
    previewMode,
    turnNumber,
    stepType,
    extendedTimeoutEnabled,
  }: {
    projectId: string;
    attemptIds: AttemptId[];
    localLintRule: LintRule<ScriptLinterCondition>;
    stepId: string;
    previewMode?: boolean;
    turnNumber?: number;
    stepType: string;
    extendedTimeoutEnabled?: boolean;
  }) => {
    const url = `internal/linters/test_single_linter`;
    try {
      return await apiFetch<any>(null, url, {
        method: 'POST',
        body: {
          projectId,
          attemptIds,
          localLintRule,
          stepId,
          previewMode,
          turnNumber,
          stepType,
          extendedTimeoutEnabled,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't test linter.",
        e.response?.message ?? e.toString(),
        `Error testing linter for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getJSONataEvaluationContext = async ({
    projectId,
    attemptId,
    localLintRule,
    stepId,
    turnNumber,
  }: {
    projectId: string;
    attemptId: AttemptId;
    localLintRule: LintRule<ScriptLinterCondition>;
    stepId: string;
    turnNumber?: number;
  }) => {
    const url = `internal/linters/get_jsonata_evaluation_context`;
    try {
      return await apiFetch<any>(null, url, {
        method: 'POST',
        body: {
          projectId,
          attemptId,
          localLintRule,
          stepId,
          turnNumber,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get JSONata evaluation context.",
        e.response?.message ?? e.toString(),
        `Error getting JSONata evaluation context for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getLinterRuns = async ({
    projectId,
    linterName,
  }: {
    projectId: string;
    linterName: string;
  }) => {
    const url = `internal/linters/linter_runs`;
    try {
      return await apiFetch<LinterRun[]>(null, url, {
        method: 'GET',
        query: { projectId, linterName },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get linter runs.",
        e.response?.message ?? e.toString(),
        `Error getting linter runs for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getLinterPerformance = async ({
    projectId,
    linterName,
  }: {
    projectId: string;
    linterName: string;
  }): Promise<LinterPerformance[]> => {
    const url = `internal/linters/linter_performance`;
    try {
      return await apiFetch<LinterPerformance[]>(null, url, {
        method: 'GET',
        query: { projectId, linterName },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get linter performance.",
        e.response?.message ?? e.toString(),
        `Error getting linter performance for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getLinterCosts = async ({
    projectId,
    startDate,
    endDate,
  }: LinterCostsRequest): Promise<LinterCostsResponse> => {
    const url = `internal/linters/linter_costs`;
    try {
      return await apiFetch<LinterCostsResponse>(null, url, {
        method: 'GET',
        query: { projectId, startDate, endDate },
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't get linter costs.",
        error.response?.message ?? error.toString(),
        `Error getting linter costs for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getL8EscalatedTasksRate = async ({ projectId }: { projectId: string }) => {
    const url = `internal/genai/cheating/l8EscalatedTaskRate`;
    try {
      return await apiFetch<L8EscalatedTaskRate>(null, url, {
        method: 'GET',
        query: { projectId },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't get L8 escalated task rate`,
        e.response?.message ?? e.toString(),
        `Error getting L8 escalated task rate for project ID ${projectId}`,
        url,
      );
    }
  };

  static getTaskersWithStrikeTags = async ({ projectId }: TaskersWithStrikeTagsRequest) => {
    const url = `internal/genai/cheating/taskersWithStrikeTags`;
    try {
      return await apiFetch<TaskersWithStrikeTagsResponse>(null, url, {
        method: 'GET',
        query: { projectId },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get taskers with strike tags.",
        e.response?.message ?? e.toString(),
        `Error getting taskers with strike tags for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getCheatingFlaggedTasks = async ({ projectId }: { projectId: string }) => {
    const url = `internal/genai/cheating/cheatingFlaggedTasks`;
    try {
      return await apiFetch<CheatingFlaggedTasksResponse>(null, url, {
        method: 'GET',
        query: { projectId },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't get cheating flagged tasks`,
        e.response?.message ?? e.toString(),
        `Error getting cheating flagged tasks for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getTasksTouchedByConfirmedCheaters = async ({ projectId }: { projectId: string }) => {
    const url = `internal/genai/cheating/tasksTouchedByConfirmedCheaters`;
    try {
      return await apiFetch<TasksTouchedByConfirmedCheatersResponse>(null, url, {
        method: 'GET',
        query: { projectId },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't get tasks touched by confirmed cheaters`,
        e.response?.message ?? e.toString(),
        `Error getting tasks touched by confirmed cheaters for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getWorkersDeactivatedForCheating = async ({ projectId }: { projectId: string }) => {
    const url = `internal/genai/cheating/workersDeactivatedForCheating`;
    try {
      return await apiFetch<WorkersDeactivatedForCheatingResponse>(null, url, {
        method: 'GET',
        query: { projectId },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't get workers deactivated for cheating`,
        e.response?.message ?? e.toString(),
        `Error getting workers deactivated for cheating for project ID ${projectId}.`,
        url,
      );
    }
  };

  static findGenAIProjects = async (searchText: string, includeNonGenAIProjects?: boolean) => {
    const url = `internal/genai/searchGenAiProjects`;
    try {
      const res = await apiFetch<{ projects: ProjectWithStatus[] }>(null, url, {
        query: {
          includeTestProjects: false,
          search: searchText,
          includeNonGenAIProjects,
        },
      });
      return _.orderBy(res.projects, ['isActive', 'isDisabled'], ['desc', 'asc']);
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get GenAI projects search results.",
        e.response?.message ?? e.toString(),
        `Error getting GenAI projects search results for searchText ${searchText}.`,
        url,
      );
    }
  };

  static getQMsByProjects = async (projectIds: string[]) => {
    const url = `internal/genai/getQMsByProjects`;
    try {
      const res: {
        success: boolean;
        qmsByProjects: Record<
          string,
          {
            email: string;
            user_id: string;
            team_classification: string;
          }[]
        >;
      } = await apiFetch(null, url, {
        query: {
          projectIds,
        },
      });
      return res.qmsByProjects;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get QMs info.",
        e.response?.message ?? e.toString(),
        `Error getting QMs info for weekly project checkup.`,
        url,
      );
    }
  };

  static getFlagsForStaleRoles = async (userId: string) => {
    const url = `internal/genai/flagsForStaleRoles`;
    try {
      const res = await apiFetch<{
        success: boolean;
        flagsForStaleRoles: FlagsForStaleRolesResponse;
      }>(null, url, {
        query: {
          userId,
        },
        method: 'GET',
      });
      if (!res.success) {
        throw new Error('Failed to get flags for stale roles');
      }
      return res.flagsForStaleRoles;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get flags for stale roles.",
        e.response?.message ?? e.toString(),
        `Error getting flags for stale roles.`,
        url,
      );
    }
  };

  static createCSATResponses = async (responses: any[]) => {
    const url = `internal/genai/csat/createResponses`;
    try {
      const res: { success: boolean } = await apiFetch(null, url, {
        body: {
          responses,
        },
        method: 'POST',
      });
      return res.success;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't create CSAT responses.",
        e.response?.message ?? e.toString(),
        `Error creating CSAT responses.`,
        url,
      );
    }
  };

  static getCSATResponsesFromLastWeekByCampaignName = async (campaignName: string) => {
    const url = `internal/genai/csat/getResponsesFromLastWeekByCampaignName`;
    try {
      const res = await apiFetch(null, url, {
        method: 'GET',
        query: { campaignName },
      });
      if (!res.success) {
        throw new Error('Failed to get CSAT responses from last week by campaign name');
      }
      return res.responses;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get CSAT responses from last week by campaign name.",
        e.response?.message ?? e.toString(),
        `Error getting CSAT responses from last week by campaign name for campaign ${campaignName}.`,
        url,
      );
    }
  };

  static getOperationsRoster = async () => {
    const url = 'internal/genai/getOperationsRoster';
    try {
      const res: { success: boolean; operationsRosterEmails?: string[]; message?: string } =
        await apiFetch(null, url, {});
      if (res.success) {
        return res.operationsRosterEmails;
      }
      throw new Error(res.message);
    } catch (error) {
      throw new EnrichedError(
        error.status,
        'Error getting Operations roster',
        error.response?.message ?? error.toString(),
        'Error getting Operations roster',
        url,
      );
    }
  };

  static getOperationsRosterForWeeklyRolePopUp = async () => {
    const url = 'internal/genai/getOperationsRosterForWeeklyRolePopUp';
    try {
      const res: { success: boolean; emailsForWeeklyRolePopUp?: string[]; message?: string } =
        await apiFetch(null, url, {});
      if (res.success) {
        return res.emailsForWeeklyRolePopUp;
      }
      throw new Error(res.message);
    } catch (error) {
      throw new EnrichedError(
        error.status,
        'Error getting Operations roster for weekly role pop up',
        error.response?.message ?? error.toString(),
        'Error getting Operations roster for weekly role pop up',
        url,
      );
    }
  };

  static getSTORoster = async () => {
    const url = 'internal/genai/getSTORoster';

    try {
      const res: { success: boolean; stoRosterEmails?: string[]; message?: string } =
        await apiFetch(null, url, {});

      if (res.success) {
        return res.stoRosterEmails;
      }
      throw new Error(res.message);
    } catch (error) {
      throw new EnrichedError(
        error.status,
        'Error getting STO roster',
        error.response?.message ?? error.toString(),
        'Error getting STO roster for assignment dropdown',
        url,
      );
    }
  };

  static createProjectChangelogEntryBatch = async (
    projectChangelogBatch: ProjectChangelogCreateBatchDTO,
  ) => {
    const url = `internal/genai/projectChangelog/createBatch`;
    try {
      const response = await apiFetch(null, url, {
        method: 'POST',
        body: projectChangelogBatch,
      });
      return response;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't create project changelog.",
        e.response?.message ?? e.toString(),
        `Error creating project changelog.`,
        url,
      );
    }
  };

  static getProjectChangelog = async (projectId: string) => {
    const url = `internal/genai/projectChangelog/${projectId}`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get project changelog.",
        e.response?.message ?? e.toString(),
        `Error getting project changelog for project ID ${projectId}.`,
        url,
      );
    }
  };

  static fetchProjectLibraryEntries = async (
    filters?: FilterObject,
  ): Promise<GetProjectLibraryEntriesResponse> => {
    const inputFilters: Filter[] = filters?.filters ?? [];

    const url = `internal/genai/getProjectLibraryEntries`;
    try {
      return await apiFetch(null, url, {
        body: {
          includeTestProjects: false,
          businessUnit: BusinessUnit.GenerativeAIDataEngine,
          businessUnitProduct: [BusinessUnitProduct.RLHF, BusinessUnitProduct.RLHFExperimental],
          operator: filters?.operator,
          multiSearchKeys: inputFilters
            .filter(f => f.field === 'projectName' && f.value && f.value.length > 0)
            .map(f => f.value),
          projectGroupSearchKeys: inputFilters
            .filter(f => f.field === 'projectGroupName' && f.value && f.value.length > 0)
            .map(f => f.value),
          customerIdSearchKeys: inputFilters
            .filter(f => f.field === 'customerId')
            .map(f => f.value),
          projectTypes: inputFilters.filter(
            f => f.field === 'projectType' && f.value && f.value.length > 0,
          ),
          projectMediums: inputFilters.filter(
            f => f.field === 'projectMedium' && f.value && f.value.length > 0,
          ),
          projectUseCases: inputFilters.filter(
            f => f.field === 'projectUseCase' && f.value && f.value.length > 0,
          ),
          statuses: inputFilters.filter(f => f.field === 'activityStatus'),
          pods: inputFilters.filter(f => f.field === 'pod' && f.value && f.value.length > 0),
          latestActivity: inputFilters.filter(f => f.field === 'latestActivity').map(f => f.value),
          onPlatform: inputFilters.filter(f => f.field === 'onPlatform'),
          paused: inputFilters.filter(f => f.field === 'paused'),
          taskTypes: inputFilters.filter(f => f.field === 'taskType'),
          inTableau: inputFilters.filter(f => f.field === 'inTableau'),
        },
        method: 'POST',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get Project Library.",
        e.response?.message ?? e.toString(),
        `Error getting Project Library entries with filters: ${inputFilters.toString()}.`,
        url,
      );
    }
  };

  static fetchProjectLibraryEntriesByIds = async (
    projectIds: string[],
  ): Promise<GetProjectLibraryEntriesResponse> => {
    const url = `internal/genai/getProjectLibraryEntriesByIds`;
    try {
      return await apiFetch(null, url, {
        body: {
          projectIds,
        },
        method: 'POST',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get Project Library entries.",
        e.response?.message ?? e.toString(),
        `Error getting Project Library entries for project IDs: ${projectIds.toString()}.`,
        url,
      );
    }
  };

  static fetchCourseV2LibraryEntriesById = async (
    projectId: string,
    limit?: number,
    page?: number,
    searchString?: string,
    archived?: boolean,
  ): Promise<GetCourseV2LibraryEntriesResponse> => {
    const url = `internal/genai/getCourseV2LibraryEntriesById`;
    try {
      return await apiFetch(null, url, {
        body: {
          projectId,
          limit,
          page,
          searchString,
          archived,
        },
        method: 'POST',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get Course Library for project.",
        e.response?.message ?? e.toString(),
        `Error getting v2 Course Library entries for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getCourseV2ById = async (courseId: string) => {
    const url = `internal/genai/getCourseV2/${courseId}`;
    try {
      return await apiFetch<GetCourseV2Response>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get course.",
        e.response?.message ?? e.toString(),
        `Error getting v2 course for course ID ${courseId}.`,
        url,
      );
    }
  };

  static getUserFullstory = async (userId: string): Promise<IFullstoryResponse[]> => {
    const url = `corp-api/lookup/${userId}/fullstory`;
    try {
      return await apiFetch(null, url, { method: 'GET' });
    } catch (e) {
      if (e.status === 404) {
        // If the onboarding state doesn't exists is not an error but a valid state
        return [] as IFullstoryResponse[];
      }
      throw new EnrichedError(
        e.status,
        `Couldn't get user fullstory for user ${userId}.`,
        e.response?.message ?? e.toString(),
        `Error while getting user fullstory for user ${userId}.`,
        url,
      );
    }
  };

  static tryToFetchUserOnboardingState = async (userId: string) => {
    const url = `corp-api/onboarding-control-panel/userOnboardingState/${userId}`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      if (e.status === 404) {
        // If the onboarding state doesn't exists is not an error but a valid state
        return null;
      }
      throw new EnrichedError(
        e.status,
        `Couldn't get user onboarding state for user ${userId}.`,
        e.response?.message ?? e.toString(),
        `Error while getting user onboarding state for user ${userId}.`,
        url,
      );
    }
  };

  static getAllCourseV2Templates = async () => {
    const url = `internal/coursesV2/getAllCoursesV2Templates`;
    try {
      return await apiFetch<GetCourseV2Templates>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get all course templates.",
        e.response?.message ?? e.toString(),
        `Error getting all v2 course templates.`,
        url,
      );
    }
  };

  static getQualificationByCourseName = async (name: string) => {
    return await apiFetch<GetCourseV2ExistingQual>(
      null,
      `internal/coursesV2/checkQualificationExists`,
      {
        body: {
          name,
        },
        method: 'POST',
      },
    );
  };

  static getCourseV2ReadOnlyChatTask = async (taskId: string) => {
    const url = `internal/courses/chat-preview-content/${taskId}`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get course chat task.",
        e.response?.message ?? e.toString(),
        `Error getting v2 chat task for task ID ${taskId}.`,
        url,
      );
    }
  };

  static getCourseV2ReadOnlyChatV2Task = async (taskId: string) => {
    const url = `internal/courses/chat-experimental-preview-content/${taskId}`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get course chatv2 task.",
        e.response?.message ?? e.toString(),
        `Error getting v2 chatv2 task for task ID ${taskId}.`,
        url,
      );
    }
  };

  static getCourseV2ReadOnlyTaskAttempt = async (
    attemptId: string,
  ): Promise<TaskAttemptPreviewCoursesAPIResponse> => {
    const url = `internal/courses/task-attempt-preview-content/${attemptId}`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get course task attempt.",
        e.response?.message ?? e.toString(),
        `Error getting task attempt for attempt ID ${attemptId}.`,
        url,
      );
    }
  };

  /**
   * Function to retrieve a v1 course by id.
   */
  static getCourseById = async (courseId: string) => {
    const url = `internal/courses/${courseId}`;
    try {
      return await apiFetch<ICourse>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get course.",
        e.response?.message ?? e.toString(),
        `Error getting course for course ID ${courseId}.`,
        url,
      );
    }
  };

  /**
   * This function fetches the corp job's cached value unless the job finished successfully, in which case it fetches the job itself.
   * @param corpJobId
   * @returns
   */
  static fetchCorpJobCache = async (
    corpJobId?: string,
  ): Promise<{
    cache: boolean;
    status?: CorpJobStatus;
    value: HydratedCorpJobJson | Record<string, any> | undefined;
  }> => {
    const defaultValue: Record<string, any> = {};
    if (!corpJobId) return { cache: false, value: defaultValue };

    const url = `corp-api/jobs/job/${corpJobId}/cachedValue`;
    try {
      return await apiFetch<
        | { cache: false; value: HydratedCorpJobJson | undefined; status: CorpJobStatus }
        | { cache: true; value: Record<string, any>; status: CorpJobStatus }
      >(null, url);
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get Corp job.",
        e.response?.message ?? e.toString(),
        `Error getting cached Corp job for corpJob ID ${corpJobId}.`,
        url,
      );
    }
  };

  static fetchJobsByQuery = async (
    query: IQueryCorpJobs,
  ): Promise<{ jobs: HydratedCorpJobJson[] }> => {
    const url = `corp-api/jobs/list`;

    try {
      const jobs = await apiFetch<HydratedCorpJobJson[]>(null, url, {
        method: 'GET',
        query,
      });
      return { jobs };
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get Corp jobs.",
        e.response?.message ?? e.toString(),
        `Error getting Corp jobs for query ${JSON.stringify(query)}.`,
        url,
      );
    }
  };

  static startCorpJob = async (jobName: string, body: any) => {
    const url = `corp-api/jobs/${jobName}`;
    try {
      return await apiFetch<{ id: string; status: string }>(null, url, {
        method: 'POST',
        body,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't start Corp job: ${jobName}.`,
        e.response?.message ?? e.toString(),
        `Error starting Corp job for job name ${jobName}.`,
        url,
      );
    }
  };

  static fetchTaskReplications = async (projectId: string) => {
    const url = `corp-api/task-replication/${projectId}`;
    try {
      return await apiFetch(null, url);
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get task replications for project.",
        e.response?.message ?? e.toString(),
        `Error getting task replications for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getChatBulkAudits = async (projectId: string) => {
    const url = `corp-api/chatBulkAudit/batch/project/${projectId}`;
    try {
      return await apiFetch(null, url);
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get Chat bulk audits for project.",
        e.response?.message ?? e.toString(),
        `Error getting Chat bulk audits for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getChatBulkAuditForUser = async (projectId: string, userId: string) => {
    const url = `corp-api/chatBulkAudit/batch/project/${projectId}/${userId}`;
    try {
      return await apiFetch(null, url);
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get Chat bulk audits for project and user.",
        e.response?.message ?? e.toString(),
        `Error getting Chat bulk audits for project ID ${projectId} and user ID ${userId}.`,
        url,
      );
    }
  };

  static chatBulkAuditObjectIdsWithAttempts = async (
    objectIds: string[] | undefined,
  ): Promise<ChatBulkAuditObjectIdsWithAttemptsResponse> => {
    if (!objectIds)
      return {
        objectIdsWithAttempts: [],
        objectIdsWithoutAttempts: [],
      };
    const url = `corp-api/chatBulkAudit/objectIdsWithAttempts`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { objectIds },
      });
    } catch (e) {
      return {
        objectIdsWithAttempts: [],
        objectIdsWithoutAttempts: [],
      };
    }
  };

  static getProjectSquads = async (projectId: string) => {
    const url = `internal/squad-management/projects/${projectId}/squads`;
    try {
      return await apiFetch<{ squads: SquadStats[] }>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get Project Squads.",
        e.response?.message ?? e.toString(),
        `Error getting Project Squads for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getProjectSquadTaskStats = async (projectId: string) => {
    const url = `internal/squad-management/projects/${projectId}/squad-task-stats`;
    try {
      return await apiFetch<GetProjectSquadTaskStatsResponse>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get Project Squad Task Stats.",
        e.response?.message ?? e.toString(),
        `Error getting Project Squad Task Stats for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getSquad = async (squadId: string, projectIds: string[]) => {
    const url = `internal/squad-management/squads/${squadId}`;
    try {
      return await apiFetch<GetSquadResponse>(null, url, {
        method: 'POST',
        body: { projectIds },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get squad.",
        e.response?.message ?? e.toString(),
        `Error getting squad with ID ${squadId}.`,
        url,
      );
    }
  };

  static getSquadTaskStats = async (squadId: string, projectIds: string[]) => {
    const url = `internal/squad-management/squads/${squadId}/task-stats`;
    try {
      return await apiFetch<GetSquadTaskStatsResponse>(null, url, {
        method: 'POST',
        body: { projectIds },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get squad task stats.",
        e.response?.message ?? e.toString(),
        `Error getting squad task stats for squad ID ${squadId}.`,
        url,
      );
    }
  };

  static getSquadAudits = async (squadId: string, projectIds: string[]) => {
    const url = `internal/squad-management/squads/${squadId}/audits`;
    try {
      return await apiFetch<GetSquadAuditsResponse>(null, url, {
        method: 'POST',
        body: { projectIds },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get squad audits.",
        e.response?.message ?? e.toString(),
        `Error getting squad audits for squad ID ${squadId}.`,
        url,
      );
    }
  };

  static getSquadHealth = async (squadId: string, projectIds: string[]) => {
    const url = `internal/squad-management/squads/${squadId}/health`;
    try {
      return await apiFetch<GetSquadHealthResponse>(null, url, {
        method: 'POST',
        body: { projectIds },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get squad health.",
        e.response?.message ?? e.toString(),
        `Error getting squad health for squad ID ${squadId}.`,
        url,
      );
    }
  };

  static getSquadPendingTasks = async (squadId: string, projectIds: string[]) => {
    const url = `internal/squad-management/squads/${squadId}/pending-tasks`;
    try {
      return await apiFetch<GetSquadPendingTasksResponse>(null, url, {
        method: 'POST',
        body: { projectIds },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get squad pending tasks.",
        e.response?.message ?? e.toString(),
        `Error getting squad pending tasks for squad ID ${squadId}.`,
        url,
      );
    }
  };

  static checkAssignableSquads = async (sourceProjectId: string, destinationProjectId: string) => {
    const url = `internal/squad-management/projects/${destinationProjectId}/assignable-squads`;
    try {
      return await apiFetch<MaybeAssignableSquad[]>(null, url, {
        method: 'POST',
        body: { sourceProjectId },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't check assignable squads.",
        e.response?.message ?? e.toString(),
        `Error checking assignable squads for source project ID ${sourceProjectId} and destination project ID ${destinationProjectId}.`,
        url,
      );
    }
  };

  static getSquadMemberAttempts = async (squadId: string, projectIds: string[]) => {
    const url = `internal/squad-management/squads/${squadId}/member-attempts`;
    try {
      return await apiFetch<GetSquadMemberAttemptsResponse>(null, url, {
        method: 'POST',
        body: { projectIds },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get squad member tasks.",
        e.response?.message ?? e.toString(),
        `Error getting squad member tasks for squad ID ${squadId}.`,
        url,
      );
    }
  };

  static getReviewerSquads = async () => {
    const url = `internal/squad-management/squads/reviewer`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get reviewer squads.",
        e.response?.message ?? e.toString(),
        `Error getting reviewer squads.`,
        url,
      );
    }
  };

  static getSquadMemberMetadata = async (
    squadMembers: string[],
  ): Promise<GetSquadMemberMetadataResponse> => {
    const url = `internal/squad-management/squads/metadata`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { squadMembers },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get Squad member metadata.",
        e.response?.message ?? e.toString(),
        `Error getting Squad member metadata.`,
        url,
      );
    }
  };

  static searchSquadUsers = async (search: string): Promise<SquadUsersResult> => {
    const url = `internal/squad-management/user/search`;
    try {
      const query = {
        search: search.trim().toLowerCase(),
      };
      return await apiFetch(null, url, { query });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't search Squad users by ID or email.",
        e.response?.message ?? e.toString(),
        `Error searching Squad users by ID or email.`,
        url,
      );
    }
  };

  static lookupSquadUsers = async (
    projectId: string,
    idsOrEmails: string[],
  ): Promise<SquadUsersResult> => {
    const url = `internal/squad-management/user/lookup/${projectId}`;
    try {
      return await apiFetch(null, url, { method: 'POST', body: { idsOrEmails } });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't lookup Squad users by ID or email.",
        e.response?.message ?? e.toString(),
        `Error looking up Squad users by ID or email.`,
        url,
      );
    }
  };

  static removeUsersFromExistingSquads = async (userIds: string[]) => {
    const url = `internal/squad-management/user/remove-from-existing-squads`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { userIds },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't remove users from existing squads.",
        e.response?.message ?? e.toString(),
        `Error removing users from existing squads.`,
        url,
      );
    }
  };

  static validateSquadName = async (squadName: string): Promise<{ exists: boolean }> => {
    const url = `internal/squad-management/squads/validate-name`;
    try {
      return await apiFetch(null, url, { method: 'POST', body: { squadName } });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't validate Squad name.",
        e.response?.message ?? e.toString(),
        `Error validating Squad name.`,
        url,
      );
    }
  };

  static lookupSquadResponsibleAdmins = async (
    idsOrEmails: string[],
  ): Promise<{ id: string; email: string }[]> => {
    const url = `internal/squad-management/user/lookup-responsible-admins`;
    try {
      return await apiFetch(null, url, { method: 'POST', body: { idsOrEmails } });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't lookup Squad responsible admins by ID or email.",
        e.response?.message ?? e.toString(),
        `Error looking up Squad responsible admins by ID or email.`,
        url,
      );
    }
  };

  static getSquadProjects = async (squadId: string) => {
    const url = `internal/squad-management/squads/${squadId}/projects`;
    try {
      return await apiFetch<ProjectInfo[]>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get squad projects.",
        e.response?.message ?? e.toString(),
        `Error getting squad projects for squad ID ${squadId}.`,
        url,
      );
    }
  };

  static getUserProjectOwnership = async (
    projectId: string,
  ): Promise<GetUserProjectOwnershipResult> => {
    const url = `internal/squad-management/projects/${projectId}/ownership`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get user project ownership.",
        e.response?.message ?? e.toString(),
        `Error getting user project ownership for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getSquadInternalProjects = async (squadId: string) => {
    const url = `internal/squad-management/squads/${squadId}/internal-projects`;
    try {
      return await apiFetch<ProjectInfo[]>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get squad internal project names.",
        e.response?.message ?? e.toString(),
        `Error getting squad internal project names for squad ID ${squadId}.`,
        url,
      );
    }
  };

  static createProjectSquad = async (
    req: CreateProjectSquadParams,
  ): Promise<CreateProjectSquadResult> => {
    const url = `internal/squad-management/squads/create`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: req,
      });
    } catch (e) {
      return {
        error: new EnrichedError(
          e.status,
          "Couldn't create Project Squad.",
          e.response?.message ?? e.toString(),
          `Error creating Project Squad for project ID ${req.projectId}.`,
          url,
        ),
      };
    }
  };

  static validateBulkCreateSquadInput = async ({
    rows,
    projectId,
  }: {
    rows: SquadCSVUploadDataRow[];
    projectId: string;
  }): Promise<ValidateBulkCreateSquadInputResult> => {
    const url = `internal/squad-management/squads/create/bulk-validate`;
    try {
      return await apiFetch<ValidateBulkCreateSquadInputResult>(null, url, {
        method: 'POST',
        body: { rows, projectId },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't validate bulk create squad input.",
        e.response?.message ?? e.toString(),
        `Error validating bulk create squad input for project ID ${projectId}.`,
        url,
      );
    }
  };

  static validateBulkUpdateSquadInput = async ({
    rows,
    projectId,
  }: {
    rows: SquadCSVUpdateDataRow[];
    projectId: string;
  }): Promise<ValidateBulkUpdateSquadInputResult> => {
    const url = `internal/squad-management/squads/update/bulk-validate`;
    try {
      return await apiFetch<ValidateBulkUpdateSquadInputResult>(null, url, {
        method: 'POST',
        body: { rows, projectId },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't validate bulk update squad input.",
        e.response?.message ?? e.toString(),
        `Error validating bulk update squad input for project ID ${projectId}.`,
        url,
      );
    }
  };

  static bulkCreateSquads = async ({
    rows,
    projectId,
  }: {
    rows: ParsedSquadCSVUploadDataRow[];
    projectId: string;
  }): Promise<BulkCreateSquadsResult> => {
    const url = `internal/squad-management/squads/create/bulk`;
    try {
      return await apiFetch<BulkCreateSquadsResult>(null, url, {
        method: 'POST',
        body: { rows, projectId },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't create squads in bulk.",
        e.response?.message ?? e.toString(),
        `Error creating squads in bulk for project ID ${projectId}.`,
        url,
      );
    }
  };

  static bulkUpdateSquads = async ({
    rows,
    projectId,
  }: {
    rows: ParsedSquadCSVUpdateDataRow[];
    projectId: string;
  }): Promise<BulkUpdateSquadsResult> => {
    const url = `internal/squad-management/squads/update/bulk`;
    try {
      return await apiFetch<BulkUpdateSquadsResult>(null, url, {
        method: 'POST',
        body: { rows, projectId },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't update squads in bulk.",
        e.response?.message ?? e.toString(),
        `Error updating squads in bulk for project ID ${projectId}.`,
        url,
      );
    }
  };

  static updateProjectSquad = async (
    req: UpdateProjectSquadParams,
  ): Promise<UpdateProjectSquadResult> => {
    const url = `internal/squad-management/squads/${req.squadId}/update`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: req,
      });
    } catch (e) {
      return {
        error: new EnrichedError(
          e.status,
          "Couldn't update squad.",
          e.response?.message ?? e.toString(),
          `Error updating squad with ID ${req.squadId}.`,
          url,
        ),
      };
    }
  };

  static getProjectSquadsHealth = async (
    projectId: string,
    squadIds: string[],
  ): Promise<GetProjectSquadsHealthResponse> => {
    const url = `internal/squad-management/projects/${projectId}/health`;
    try {
      return await apiFetch(null, url, { method: 'POST', body: { squadIds } });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get Project Squads health.",
        e.response?.message ?? e.toString(),
        `Error getting Project Squads health for project ID ${projectId}.`,
        url,
      );
    }
  };

  static assignSquadsToProject = async ({
    projectId,
    squadIds,
  }: {
    projectId: string;
    squadIds: string[];
  }): Promise<AssignSquadsToProjectResult> => {
    const url = `internal/squad-management/projects/${projectId}/assign-squads`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { squadIds },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't assign squads to project.",
        e.response?.message ?? e.toString(),
        `Error assigning squads to project ID ${projectId}.`,
        url,
      );
    }
  };

  static rerunQueue = async (
    request: RerunQueueAndGetHealthRequest,
  ): Promise<UserLevelHealthStatus> => {
    const url = `internal/squad-management/health/rerun-queue`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: request,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't rerun queue.",
        e.response?.message ?? e.toString(),
        `Error rerunning queue with request ${request}.`,
        url,
      );
    }
  };

  static lookupSquadAttempt = async (attemptId: string) => {
    const url = `internal/squad-management/lookup/attempt/${attemptId}`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't lookup for this attempt id: "${attemptId}".`,
        e.response?.message ?? e.toString(),
        `Error looking up for id: ${attemptId}`,
        url,
      );
    }
  };

  static getProjectSquadsConfig = async (projectId: string) => {
    const url = `internal/squad-management/project/${projectId}/config`;
    try {
      return await apiFetch<SquadsConfig>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get project squads config.",
        e.response?.message ?? e.toString(),
        `Error getting squads config for project ID ${projectId}.`,
        url,
      );
    }
  };

  static updateProjectSquadsConfig = async (projectId: string, squadsConfig: SquadsConfig) => {
    const url = `internal/squad-management/project/${projectId}/config`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: squadsConfig,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't update project squads config.",
        e.response?.message ?? e.toString(),
        `Error updating squads config for project ID ${projectId}.`,
        url,
      );
    }
  };

  static createSquadReviewersCommunityChannel = async (projectId: string) => {
    const url = `internal/squad-management/channel/create`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { projectId },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't create or link squad reviewers community channel.",
        e.response?.message ?? e.toString(),
        `Error creating or linking squad reviewers community channel for project ID ${projectId}.`,
        url,
      );
    }
  };

  static archiveProjectSquad = async (
    projectId: string,
    squadId: string,
    reason: string,
    notes: string,
  ): Promise<ArchiveProjectSquadResult> => {
    const url = `internal/squad-management/squads/${squadId}/archive`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { reason, notes },
      });
    } catch (e) {
      return {
        error: new EnrichedError(
          e.status,
          "Couldn't archive Project Squad.",
          e.response?.message ?? e.toString(),
          `Error archiving Project Squad for project ID ${projectId}.`,
          url,
        ),
      };
    }
  };

  static validateUnassignSquadFromProject = async (
    projectId: string,
    squadIds: string[],
  ): Promise<ValidateUnassignSquadFromProjectResponse> => {
    const url = `internal/squad-management/projects/${projectId}/validate-unassign-squads`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { squadIds },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't validate unassign squad.",
        e.response?.message ?? e.toString(),
        `Error validating unassign squad for project ID ${projectId} and squad IDs ${squadIds.join(
          ', ',
        )}.`,
        url,
      );
    }
  };

  static unassignSquadsFromProject = async (projectId: string, squadIds: string[]) => {
    const url = `internal/squad-management/projects/${projectId}/unassign-squads`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { squadIds },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to unassign some squads from project.',
        e.response?.message ?? e.toString(),
        `Error unassigning squads from project ${projectId}.`,
        url,
      );
    }
  };

  static createMDQConfigMap = async (projectId: string) => {
    const url = `internal/mdqConfig/create_mdq_config_map`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { projectId },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't create MDQ config map.",
        e.response?.message ?? e.toString(),
        `Error creating MDQ config map for project ID ${projectId}.`,
        url,
      );
    }
  };

  static updateMDQConfigMap = async (projectId: string, mapping: any) => {
    const url = `internal/mdqConfig/update_mdq_config_map`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { projectId, mapping },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't update MDQ config map.",
        e.response?.message ?? e.toString(),
        `Error updating MDQ config map for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getGQADataForAttempts = async (projectId: string, attemptIds: string[]) => {
    const url = `internal/archieQC/get_gqa_data_for_attempts/${projectId}`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { attemptIds },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get GQA data for attempts.",
        e.response?.message ?? e.toString(),
        `Error getting GQA data for attempts for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getArchieQCSpecDoc = async (projectId: string) => {
    const url = `internal/archieQC/get_archie_qc_spec_doc/${projectId}`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get ArchieQC Spec Doc.",
        e.response?.message ?? e.toString(),
        `Error getting ArchieQC Spec Doc for project ID ${projectId}.`,
        url,
      );
    }
  };

  static validateArchieTaskAttempts = async (projectId: string, taskAttemptIds?: string[]) => {
    const url = `internal/archieQC/validate_archie_qc_task_attempts`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { projectId, taskAttemptIds },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't validate Archie task attempts.",
        e.response?.message ?? e.toString(),
        `Error validating Archie task attempts for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getLiveArchieQCSpecDoc = async (projectId: string) => {
    const url = `internal/archieQC/get_live_archie_qc_spec_doc/${projectId}`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get live ArchieQC Spec Doc.",
        e.response?.message ?? e.toString(),
        `Error getting live ArchieQC Spec Doc for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getAllPublishedArchieQCSpecDocs = async (projectId: string) => {
    const url = `internal/archieQC/get_all_published_archie_qc_spec_docs/${projectId}`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get all published ArchieQC Spec Docs.",
        e.response?.message ?? e.toString(),
        `Error getting all published ArchieQC Spec Docs for project ID ${projectId}.`,
        url,
      );
    }
  };

  static runArchieAssistantEndpoint = async (
    projectId: string,
    action: ArchieAssistantActions,
    prompt?: string,
    spec_doc_url?: string,
    instruction_doc_url?: string,
    taxonomy?: string,
    clarifications?: ClarificationQA[],
    launchTaskIdHandle?: string,
  ) => {
    const url = `internal/archieQC/run_archie_assistant_endpoint`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          project_id: projectId,
          action,
          prompt,
          spec_doc_url,
          instruction_doc_url,
          taxonomy,
          clarifications,
          launchTaskIdHandle,
        },
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't get response from ArchieQC import requirement endpoint.",
        error.response?.message ?? error.toString(),
        `Error getting ArchieQC import requirement response for project ID ${projectId}.`,
        url,
      );
    }
  };

  // Contributor Copilot
  static getCopilotConfiguration = async (projectId: string) => {
    const url = `internal/copilot/get_copilot_configuration`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { projectId },
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't get copilot configuration.",
        error.response?.message ?? error.toString(),
        `Error getting copilot configuration for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getCopilotConfigurationAggregateStats = async (projectId: string) => {
    const url = `internal/copilot/get_aggregate_stats_for_copilot_configuration`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { projectId },
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't get aggregate stats for copilot configuration.",
        error.response?.message ?? error.toString(),
        `Error getting aggregate stats for copilot configuration for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getHillclimbingStatsForConfiguration = async (projectId: string) => {
    const url = `internal/copilot/get_hillclimbing_stats_for_configuration`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { projectId },
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't get hillclimbing stats for copilot configuration.",
        error.response?.message ?? error.toString(),
        `Error getting hillclimbing stats for copilot configuration for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getCopilotConfigurationAllStats = async (
    checkId: string,
    timeFilter: CopilotStatsTimeFilter,
  ) => {
    const url = `internal/copilot/get_all_stats_for_copilot_check`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { checkId, timeFilter },
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't get all stats for copilot check.",
        error.response?.message ?? error.toString(),
        `Error getting all stats for copilot check for check ID ${checkId}.`,
        url,
      );
    }
  };

  static getCopilotCheckLastKRuns = async (
    checkId: string,
    filter: CopilotLastKRunsFilter,
    k = 20,
  ) => {
    const url = `internal/copilot/get_copilot_check_last_k_runs`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { checkId, filter, k },
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't get copilot check last k runs.",
        error.response?.message ?? error.toString(),
        `Error getting copilot check last k runs for check ID ${checkId}.`,
        url,
      );
    }
  };

  static updateCopilotConfigurationCheck = async (
    projectId: string,
    checkId: string,
    copilotCheck: CopilotCheck,
  ) => {
    const url = `internal/copilot/update_copilot_configuration`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { projectId, checkId, copilotCheck },
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't update copilot configuration.",
        error.response?.message ?? error.toString(),
        `Error updating copilot configuration for project ID ${projectId}.`,
        url,
      );
    }
  };

  static createCopilotConfigurationCheck = async (
    projectId: string,
    name: string,
    enabled: boolean,
    checkType: CopilotAPIRequestType,
    requirement?: CopilotRequirement,
    severity?: CopilotCheckSeverity,
    timeoutSeconds?: number,
  ) => {
    const url = `internal/copilot/create_copilot_configuration_check`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          projectId,
          name,
          requirement,
          enabled,
          checkType,
          severity,
          timeoutSeconds,
        },
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't create copilot configuration check.",
        error.response?.message ?? error.toString(),
        `Error creating copilot configuration check for project ID ${projectId}.`,
        url,
      );
    }
  };

  static deleteCopilotConfigurationCheck = async (projectId: string, checkId: string) => {
    const url = `internal/copilot/delete_copilot_configuration_check`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { projectId, checkId },
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't delete copilot configuration check.",
        error.response?.message ?? error.toString(),
        `Error deleting copilot configuration check for project ID ${projectId}.`,
        url,
      );
    }
  };

  static importCopilotConfigurationCheck = async ({
    targetProjectId,
    sourceProjectId,
    sourceCheckId,
  }: {
    targetProjectId: string;
    sourceProjectId: string;
    sourceCheckId: string;
  }) => {
    const url = `internal/copilot/import_copilot_configuration_check`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { targetProjectId, sourceProjectId, sourceCheckId },
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't import copilot configuration check.",
        error.response?.message ?? error.toString(),
        `Error importing copilot configuration check into target project ID ${targetProjectId} from source project ID ${sourceProjectId} (check ID ${sourceCheckId}).`,
        url,
      );
    }
  };

  static getCopilotAgentDatabase = async () => {
    const url = `internal/copilot/get_copilot_agent_database`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't get copilot agent database.",
        error.response?.message ?? error.toString(),
        `Error getting copilot agent database.`,
        url,
      );
    }
  };

  static getCopilotRequirementLibrary = async () => {
    const url = `internal/copilot/get_copilot_requirement_library`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't get copilot requirement library.",
        error.response?.message ?? error.toString(),
        `Error getting copilot requirement library.`,
        url,
      );
    }
  };

  static getCopilotTaxonomy = async (attemptId: string) => {
    const url = `internal/copilot/get_copilot_taxonomy`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          attemptId,
        },
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't get copilot taxonomy for attempt ID.",
        error.response?.message ?? error.toString(),
        `Error getting copilot taxonomy for attempt ID ${attemptId}.`,
        url,
      );
    }
  };

  static launchCopilotHillclimbingTasksForCheckRunIds = async ({
    projectId,
    configurationId,
    checkId,
    checkRunIds,
    requirementOverride,
    cacheMode,
  }: {
    projectId: string;
    configurationId: string;
    checkId: string;
    checkRunIds: string[];
    requirementOverride?: any;
    cacheMode?: ArchieCopilotCacheMode;
  }) => {
    const url = `internal/copilot/launch_copilot_hillclimbing_tasks_for_check_run_ids`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          projectId,
          configurationId,
          checkId,
          checkRunIds,
          requirementOverride,
          cacheMode,
        },
      });
    } catch (error) {
      const checkRunIdsCount = Array.isArray(checkRunIds) ? checkRunIds.length : 0;
      throw new EnrichedError(
        error.status,
        "Couldn't launch copilot hillclimbing tasks for check run IDs.",
        error.response?.message ?? error.toString(),
        `Error launching copilot hillclimbing tasks for project ID ${projectId}, configuration ID ${configurationId}, check ID ${checkId}, ${checkRunIdsCount} check run IDs.`,
        url,
      );
    }
  };

  static pollCopilotHillclimbingTasks = async ({
    projectId,
    checkId,
    tasks,
  }: {
    projectId: string;
    checkId: string;
    tasks: { checkRunId: string; taskId: string }[];
  }) => {
    const url = `internal/copilot/poll_copilot_hillclimbing_tasks`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          projectId,
          checkId,
          tasks,
        },
      });
    } catch (error) {
      const tasksCount = Array.isArray(tasks) ? tasks.length : 0;
      throw new EnrichedError(
        error.status,
        "Couldn't poll copilot hillclimbing tasks.",
        error.response?.message ?? error.toString(),
        `Error polling copilot hillclimbing tasks for project ID ${projectId}, check ID ${checkId}, ${tasksCount} task IDs.`,
        url,
      );
    }
  };

  static getCopilotHillclimbingQueryParamsForCheckRunIds = async ({
    configurationId,
    checkId,
    checkRunIds,
  }: {
    configurationId: string;
    checkId: string;
    checkRunIds: string[];
  }) => {
    const url = `internal/copilot/get_copilot_hillclimbing_query_params_for_check_run_ids`;

    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          configurationId,
          checkId,
          checkRunIds,
        },
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't fetch copilot hillclimbing query params.",
        error.response?.message ?? error.toString(),
        `Error fetching copilot hillclimbing query params for configuration ID ${configurationId}, check ID ${checkId}, ${checkRunIds.length} check run IDs.`,
        url,
      );
    }
  };

  static getCopilotCheck = async ({
    projectId,
    checkId,
  }: {
    projectId: string;
    checkId: string;
  }) => {
    const url = `internal/copilot/get_copilot_check`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { projectId, checkId },
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't get copilot check.",
        error.response?.message ?? error.toString(),
        `Error getting copilot check for project ID ${projectId}, check ID ${checkId}.`,
        url,
      );
    }
  };

  static getCopilotHillclimbingData = async ({
    projectId,
    checkId,
  }: {
    projectId: string;
    checkId: string;
  }) => {
    const url = `internal/copilot/get_copilot_hillclimbing_data`;

    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          projectId,
          checkId,
        },
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't fetch copilot hillclimbing data.",
        error.response?.message ?? error.toString(),
        `Error fetching copilot hillclimbing data for project ID ${projectId}, check ID ${checkId}.`,
        url,
      );
    }
  };

  static updateCopilotHillclimbingExpectedResults = async ({
    projectId,
    checkId,
    updates,
  }: {
    projectId: string;
    checkId: string;
    updates: ExpectedCopilotResult[];
  }) => {
    const url = `internal/copilot/update_copilot_hillclimbing_expected_results`;

    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          projectId,
          checkId,
          updates,
        },
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't update copilot hillclimbing expected results.",
        error.response?.message ?? error.toString(),
        `Error updating copilot hillclimbing expected results for project ID ${projectId}, check ID ${checkId}.`,
        url,
      );
    }
  };

  static initializeCopilotHillclimbingDataForAttempts = async ({
    projectId,
    configurationId,
    checkId,
    attemptIds,
    prepopulatedExpectedResults,
  }: {
    projectId: string;
    configurationId: string;
    checkId: string;
    attemptIds: string[];
    prepopulatedExpectedResults: Record<string, CopilotResult>;
  }) => {
    const url = `internal/copilot/initialize_copilot_hillclimbing_data_for_attempts`;

    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          projectId,
          configurationId,
          checkId,
          attemptIds,
          prepopulatedExpectedResults,
        },
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't initialize copilot hillclimbing data for attempts.",
        error.response?.message ?? error.toString(),
        `Error initializing copilot hillclimbing data for project ID ${projectId}, check ID ${checkId}.`,
        url,
      );
    }
  };

  static getRecentCompletedAttemptForHillclimbing = async ({
    projectId,
    excludeAttemptIds,
  }: {
    projectId: string;
    excludeAttemptIds: string[];
  }): Promise<{ success: boolean; attemptId: string | null }> => {
    const url = `internal/copilot/get_recent_completed_attempt_for_hillclimbing`;

    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          projectId,
          excludeAttemptIds,
        },
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't get recent completed attempt for hillclimbing.",
        error.response?.message ?? error.toString(),
        `Error getting recent completed attempt for project ID ${projectId}.`,
        url,
      );
    }
  };

  static uploadHillclimbingCsv = async ({
    projectId,
    checkId,
    csvContent,
  }: {
    projectId: string;
    checkId: string;
    csvContent: string;
  }) => {
    const url = `internal/copilot/upload_hillclimbing_csv`;

    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          projectId,
          checkId,
          csvContent,
        },
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't upload hillclimbing CSV.",
        error.response?.message ?? error.toString(),
        `Error uploading hillclimbing CSV for project ID ${projectId}, check ID ${checkId}.`,
        url,
      );
    }
  };

  static deleteCopilotAttemptsByAttemptId = async ({
    projectId,
    checkId,
    attemptIds,
  }: {
    projectId: string;
    checkId: string;
    attemptIds: string[];
  }) => {
    const url = `internal/copilot/delete_copilot_attempts_by_attempt_id`;

    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          projectId,
          checkId,
          attemptIds,
        },
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't delete copilot attempts by attempt ID.",
        error.response?.message ?? error.toString(),
        `Error deleting ${attemptIds.length} copilot attempts for project ID ${projectId}, check ID ${checkId}.`,
        url,
      );
    }
  };

  static deleteCheckRunOutcome = async ({
    projectId,
    checkId,
    checkRunId,
  }: {
    projectId: string;
    checkId: string;
    checkRunId: string;
  }) => {
    const url = `internal/copilot/delete_check_run_outcome`;

    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          projectId,
          checkId,
          checkRunId,
        },
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't delete check run outcome.",
        error.response?.message ?? error.toString(),
        `Error deleting check run outcome for project ID ${projectId}, check ID ${checkId}.`,
        url,
      );
    }
  };

  static toggleCopilotCheckMetricsMode = async ({
    projectId,
    checkId,
    metricsMode,
  }: {
    projectId: string;
    checkId: string;
    metricsMode: CopilotMetricsMode;
  }) => {
    const url = `internal/copilot/toggle_copilot_check_metrics_mode`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { projectId, checkId, metricsMode },
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't toggle copilot check metrics mode.",
        error.response?.message ?? error.toString(),
        `Error toggling copilot check metrics mode for project ID ${projectId}, check ID ${checkId}.`,
        url,
      );
    }
  };

  static getCalculatedGroundTruthMetrics = async (
    projectId: string,
    groundTruthVersion: number,
    specDocVersion: number,
  ) => {
    const url = `internal/archieQC/get_calculated_ground_truth_metrics`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          projectId,
          groundTruthVersion,
          specDocVersion,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get calculated ground truth metrics.",
        e.response?.message ?? e.toString(),
        `Error getting calculated ground truth metrics for project ID ${projectId}, groundTruthVersion ${groundTruthVersion}, specDocVersion ${specDocVersion}.`,
        url,
      );
    }
  };

  static createQCSpecDoc = async (
    projectId: string,
    newSpecDocRubrics: any,
    newProjectSpecificErrorCategories: any,
  ) => {
    const url = `internal/archieQC/update_spec_doc_rubrics`;

    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          projectId,
          newSpecDocRubrics,
          newProjectSpecificErrorCategories,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't update Quality Control Spec Doc.",
        e.response?.message ?? e.toString(),
        `Error updating Quality Control Spec Doc for project ID ${projectId}.`,
        url,
      );
    }
  };

  static updateArchieQCSpecDoc = async (
    projectId: string,
    requirements: ArchieQCRequirementSpecDocInput,
  ) => {
    const url = `internal/archieQC/update_archie_spec_doc_requirements`;

    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          projectId,
          requirements,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't update Archie QC Spec Doc.",
        e.response?.message ?? e.toString(),
        `Error updating Archie QC Spec Doc for project ID ${projectId}.`,
        url,
      );
    }
  };

  static publishArchieQCSpecDoc = async (projectId: string, publishNotes: string) => {
    const url = `internal/archieQC/publish_archie_qc_spec_doc`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          projectId,
          publishNotes,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't publish ArchieQC Spec Doc.",
        e.response?.message ?? e.toString(),
        `Error publishing ArchieQC Spec Doc for project ID ${projectId}.`,
        url,
      );
    }
  };

  static revertToPublishedVersion = async (projectId: string, publishedVersion: number) => {
    const url = `internal/archieQC/revert_to_published_version`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { projectId, publishedVersion },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't revert to published version.",
        e.response?.message ?? e.toString(),
        `Error reverting to published version for project ID ${projectId}, publishedVersion ${publishedVersion}.`,
        url,
      );
    }
  };

  static createArchieQCSpecDoc = async (projectId: string) => {
    const url = `internal/archieQC/create_archie_qc_spec_doc`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          projectId,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't create Archie QC Spec Doc.",
        e.response?.message ?? e.toString(),
        `Error creating Archie QC Spec Doc for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getArchieQCTaxonomyMapping = async (
    projectId: string,
    attemptId: string,
    includeValueReference = false,
    includeDictWithValue = false,
  ) => {
    const url = `internal/archieQC/get_archie_qc_taxonomy_mapping`;
    try {
      if (attemptId === '') {
        return {
          taxonomyMapping: {},
        };
      }
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          projectId,
          attemptId,
          includeValueReference,
          includeDictWithValue,
        },
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't get taxonomy mapping from ArchieQC.",
        error.response?.message ?? error.toString(),
        `Error getting taxonomy mapping from ArchieQC for project ID ${projectId} and attempt ID ${attemptId}.`,
        url,
      );
    }
  };

  static getArchieQCAgents = async () => {
    const url = `internal/archieQC/get_archie_qc_agents`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't get ArchieQC agents.",
        error.response?.message ?? error.toString(),
        `Error getting ArchieQC agents.`,
        url,
      );
    }
  };

  static getRecentProjectAttempts = async (
    projectId: string,
    numAttempts: number,
    reviewLevel: ReviewLevel,
  ) => {
    const url = `internal/archieQC/get_recent_project_attempts`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { projectId, numAttempts, reviewLevel },
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't get recent project attempts.",
        error.response?.message ?? error.toString(),
        `Error getting recent project attempts for project ID ${projectId}.`,
        url,
      );
    }
  };

  static persistOffPlatformResults = async (
    projectId: string,
    archieQCEvalOutput: ArchieQCEnrichedEvalOutput,
  ) => {
    const url = `internal/archieQC/persist_off_platform_results`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { projectId, archieQCEvalOutput },
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't persist off platform results.",
        error.response?.message ?? error.toString(),
        `Error persisting off platform results for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getArchieQCEndpointResponse = async (
    projectId: string,
    attemptIds: string[],
    requirements: ArchieQCRequirementSpecDocInput[] | null,
    specDocVersion: number,
    launchTaskIdHandle: string | undefined,
    filterOutAttemptIdsWithoutQCResults: boolean,
    saveAsGroundTruthMetrics?: boolean,
    customData?: CustomData,
  ) => {
    const url = `internal/archieQC/run_archie_qc_eval_endpoint`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          projectId,
          attemptIds,
          requirements,
          specDocVersion,
          launchTaskIdHandle,
          filterOutAttemptIdsWithoutQCResults,
          saveAsGroundTruthMetrics,
          customData,
        },
      });
    } catch (error) {
      if (error.status === 400) {
        const errorDetails = error.response || {};

        if (errorDetails.invalidAttemptIds) {
          throw new EnrichedError(
            error.status,
            'Invalid attempt IDs provided',
            errorDetails.error || "Some attempt IDs don't exist",
            `The following attempt IDs don't exist: ${errorDetails.invalidAttemptIds.join(', ')}`,
            url,
          );
        }

        if (errorDetails.mismatchedAttemptIds) {
          throw new EnrichedError(
            error.status,
            "Attempt IDs don't match project",
            errorDetails.error || "Some attempt IDs don't belong to the specified project",
            `The following attempt IDs don't belong to project ${projectId}: ${errorDetails.mismatchedAttemptIds.join(
              ', ',
            )}`,
            url,
          );
        }
      }
      throw new EnrichedError(
        error.status,
        `Error getting ArchieQC Response: ${error.response?.message ?? error.toString()}`,
        error.response?.message ?? error.toString(),
        `Error getting ArchieQC response for project ID ${projectId} and attempt IDs ${attemptIds}.`,
        url,
      );
    }
  };

  static summarizeAQCDisagreements = async (
    projectId: string,
    disagreementData: ArchieQCDisagreementData,
    launchTaskIdHandle?: string,
  ) => {
    const url = `internal/archieQC/summarize_aqc_disagreements`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          project_id: projectId,
          disagreement_data: disagreementData,
          launchTaskIdHandle,
        },
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't summarize AQC disagreements.",
        error.response?.message ?? error.toString(),
        `Error summarizing AQC disagreements for project ID ${projectId}.`,
        url,
      );
    }
  };

  static runArchieBMCreationEndpointResponse = async (projectId: string) => {
    const url = `internal/archieBM/run_archie_bm_creation_endpoint`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          projectId,
        },
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        "Couldn't get response from ArchieBM endpoint.",
        error.response?.message ?? error.toString(),
        `Error getting ArchieBM response for project ID ${projectId}`,
        url,
      );
    }
  };

  static createChatBulkAuditBatch = async (
    projectId: string,
    objectIds: string,
    auditName: string,
    shouldClaim?: boolean,
    objectType?: ChatBulkAuditObjectType,
    dateRange?: { startDate: Date; endDate: Date },
    limit?: number,
    reviewLevels?: string[],
  ) => {
    const parsedObjectIds = objectIds
      .split(/\s+|,/)
      .map((id: string) => id.trim())
      .filter((id: string) => id);

    const url = `corp-api/chatBulkAudit/batch`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          projectId,
          name: auditName,
          objectIds: parsedObjectIds,
          claimTasks: shouldClaim,
          objectType,
          dateRange,
          limit,
          reviewLevels,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't create Chat bulk audit batch: ${auditName}`,
        e.response?.message ?? e.toString(),
        `Error creating Chat bulk audit batch for project ID ${projectId}, auditName ${auditName}.`,
        url,
      );
    }
  };

  static getAttemptAuditScores = async ({ attemptAuditId }: { attemptAuditId: string }) => {
    return await getComponentScoresByAttemptAuditId(attemptAuditId);
  };

  static chatBulkAuditFilteredTasks = async ({
    projectId,
    filters,
    sampleNumber,
    selectedFilter,
  }: {
    projectId: string;
    filters: CustomTaskFilters[];
    sampleNumber: number | undefined;
    selectedFilter: string;
  }) => {
    const isCustomFilter = selectedFilter === 'Custom Filter';
    const isDefaultFilter = [
      ChatBulkAuditDefaultFilters.RECENT_L_NEG_1_ATTEMPTS,
      ChatBulkAuditDefaultFilters.RECENT_L0_ATTEMPTS,
      ChatBulkAuditDefaultFilters.RECENT_L4_ATTEMPTS,
      ChatBulkAuditDefaultFilters.RECENT_L10_ATTEMPTS,
      ChatBulkAuditDefaultFilters.RECENT_L12_ATTEMPTS,
    ].includes(selectedFilter as ChatBulkAuditDefaultFilters);

    const processResponse = (response: FetchTasksResponse, validFilters: CustomFilter[] = []) => {
      let objectIds: string[] = [];

      if (response.allIds) {
        objectIds = Object.values(response.allIds).flat();
      }

      if (response.nonAuditedIds && validFilters.length > 0) {
        const auditedFilter: CustomFilter | undefined = validFilters.find(
          filter => filter.primary === 'Has been Audited',
        );
        const nonAuditedSet = new Set(Object.values(response.nonAuditedIds).flat());

        if (auditedFilter) {
          const shouldFilterNonAudited = auditedFilter.tertiary === 'false';
          objectIds = shouldFilterNonAudited
            ? Object.values(response.nonAuditedIds).flat()
            : objectIds.filter(id => !nonAuditedSet.has(id));
        }
      }

      return objectIds;
    };

    const bodyFilters: { reviewLevels?: number[] } = {};

    if (isCustomFilter) {
      filters.forEach(filter => {
        if (filter.primary === 'Review Level' && filter.secondary && filter.tertiary) {
          bodyFilters.reviewLevels = bodyFilters.reviewLevels || [];
          if (Array.isArray(filter.tertiary)) {
            filter.tertiary.forEach(tertiaryItem => {
              const parsedValue = parseInt(tertiaryItem, 10);
              if (!isNaN(parsedValue) && bodyFilters.reviewLevels) {
                bodyFilters.reviewLevels.push(parsedValue);
              }
            });
          } else {
            const parsedValue = parseInt(filter.tertiary, 10);
            if (!isNaN(parsedValue)) {
              bodyFilters.reviewLevels.push(parsedValue);
            }
          }
        }
      });
    }

    const url = `corp-api/chatBulkAudit/tasks/${projectId}`;
    try {
      const response = await apiFetch<FetchTasksResponse>(null, url, {
        method: 'POST',
        body: JSON.stringify({
          filters: bodyFilters,
          sampleNumber,
          selectedFilter,
        }),
        headers: { 'Content-Type': 'application/json' },
      });

      let objectIds: string[] = [];

      if (response) {
        const validFilters = isCustomFilter
          ? filters.filter(filter => filter.secondary && filter.tertiary)
          : [];
        objectIds =
          isCustomFilter || isDefaultFilter ? processResponse(response, validFilters) : [];
      }

      return objectIds;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get filtered Chat bulk audit tasks.",
        e.response?.message ?? e.toString(),
        `Error getting filtered Chat bulk audit tasks for project ID ${projectId}.`,
        url,
      );
    }
  };

  static quarantineTaskReplications = async (projectId: string, taskIds: string[]) => {
    const url = `corp-api/task-replication/quarantine`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { projectId, taskIds },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't quarantine task replications.",
        e.response?.message ?? e.toString(),
        `Error quarantining task replications for project ID ${projectId}.`,
        url,
      );
    }
  };

  static unquarantineTaskReplications = async (projectId: string, taskIds: string[]) => {
    const url = `corp-api/task-replication/unquarantine`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { projectId, taskIds },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't unquarantine task replications.",
        e.response?.message ?? e.toString(),
        `Error unquarantining task replications for project ID ${projectId}.`,
        url,
      );
    }
  };

  static provisionSphereWorkspaceLinks = async (params: ProvisionWorkspaceRequest) => {
    const url = `internal/genai/provisionWorkspaceLinks`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: params,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't create Sphere workspace links.",
        e.response?.message ?? e.toString(),
        `Error creating Sphere workspace links.`,
        url,
      );
    }
  };

  static getTagsById = async (tagsIds: string[]) => {
    const url = `corp-api/lookup/batch`;
    try {
      const response = await apiFetch(null, url, {
        method: 'POST',
        body: {
          batch: tagsIds.map(id => ({ id, type: INamedObjectTypes.Tag })),
        },
      });
      return response.objects;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get tags.",
        e.response?.message ?? e.toString(),
        `Error getting tags for the provided IDs: ${tagsIds.join(', ')}.`,
        url,
      );
    }
  };

  static getProjectSxsQualitySignals = async ({
    projectId,
    startDate,
    endDate,
  }: {
    projectId: string;
    startDate: Date;
    endDate: Date;
  }) => {
    const url = `internal/self_serve/qualityProfile/getProjectSxsQualitySignals`;
    try {
      return await apiFetch<GetProjectSxsQualitySignalsResponse>(null, url, {
        method: 'GET',
        query: {
          projectId,
          startDate: startDate.toString(),
          endDate: endDate.toString(),
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't get project SXS quality signals in date range ${startDate} to ${endDate}.`,
        e.response?.message ?? e.toString(),
        `Error getting project SXS quality signals for project ID ${projectId} in date range ${startDate} to ${endDate}.`,
        url,
      );
    }
  };

  static getTagsByName = async (search: string) => {
    const url = 'corp-api/tags';
    try {
      const res = await apiFetch(null, url, {
        query: { search },
      });
      return res.tags;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't get tags for ${search}.`,
        e.response?.message ?? e.toString(),
        `Error getting tags for search ${search}.`,
        url,
      );
    }
  };

  static updateHubstaffProject = async (projectHubstaff: string, createdProjectId: string) => {
    const url = `/internal/hubstaff/project/${projectHubstaff}/update`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          project: createdProjectId,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't update Hubstaff project.",
        e.response?.message ?? e.toString(),
        `Error updating Hubstaff project for projectHubstaff ${projectHubstaff}, createdProject ID ${createdProjectId}.`,
        url,
      );
    }
  };

  static getIsProjectActive = async (projectId: string) => {
    const url = `corp-api/project/${projectId}/isActive`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get project status.",
        e.response?.message ?? e.toString(),
        `Error getting project active status for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getProjectRoles = async (projectId: string, filters?: Role[]) => {
    const rolesQueryFilters = filters?.map(role => `role=${role}`).join('&') ?? '';
    const url = `corp-api/qm/project_roles/?project=${projectId}&${rolesQueryFilters}`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get project roles.",
        e.response?.message ?? e.toString(),
        `Error getting project roles for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getWorkerTeams = async (projectId: string) => {
    const url = `corp-api/worker_teams/list_teams_by_project/${projectId}`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get worker teams.",
        e.response?.message ?? e.toString(),
        `Error getting worker teams for project ID ${projectId}.`,
        url,
      );
    }
  };

  static updateProjectDetails = async (reqBody: GenAIProjectUpdateRequest) => {
    const url = `corp-api/update_project_details`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: reqBody,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't update project.",
        e.response?.message ?? e.toString(),
        `Error updating project ID ${reqBody.project}.`,
        url,
      );
    }
  };

  static updateProjectSLAGroup = async (
    {
      projectGroupId,
      name,
      projectIds,
      description,
      businessUnitProduct,
      businessUnit,
      fulfillmentMechanism,
      slaCadence,
      status,
    }: UpdateProjectGroupRequest & { projectGroupId: string },
    diff: ProjectGroupDiff,
  ) => {
    const url = `corp-api/project-groups/${projectGroupId}`;
    try {
      return await apiFetch(null, url, {
        method: 'PUT',
        body: {
          name,
          projectIds,
          description,
          businessUnit,
          slaCadence,
          status,
          businessUnitProduct,
          fulfillmentMechanism,
          diff,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't update project SLA group.",
        e.response?.message ?? e.toString(),
        `Error updating project SLA group for projectGroup ID ${projectGroupId}.`,
        url,
      );
    }
  };

  // note: fka disableProject
  static archiveProject = async (
    projectId: string,
    userId: string,
    automaticallyRemoveProjectRoles: boolean,
  ) => {
    const url = `corp-api/projects/${projectId}/maxCompletableTasks`;
    try {
      logEvent(Event.ProjectArchiveEvent, {
        projectId,
        userId,
        archivedAt: new Date(),
      });
      return await apiFetch(null, url, {
        method: 'PUT',
        body: { maxCompletableTasks: 0, automaticallyRemoveProjectRoles },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't archive project.",
        e.response?.message ?? e.toString(),
        `Error disabling project ID ${projectId}.`,
        url,
      );
    }
  };

  static enableProject = async (projectId: string, maxTaskCount: number) => {
    const url = `corp-api/projects/${projectId}/maxCompletableTasks`;
    try {
      return await apiFetch(null, url, {
        method: 'PUT',
        body: { maxCompletableTasks: maxTaskCount },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't enable project.",
        e.response?.message ?? e.toString(),
        `Error enabling project ID ${projectId}.`,
        url,
      );
    }
  };

  static fetchTaskTemplate = async (projectId: string) => {
    const url = `internal/self_serve/task_template/${projectId}`;
    try {
      return await apiFetch<GetTemplateResponse>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get task template for project.",
        e.response?.message ?? e.toString(),
        `Error getting task template for project ID ${projectId}.`,
        url,
      );
    }
  };

  static fetchChatTaskFeatureConfig = async (projectId: string) => {
    const url = `internal/task-template/chat-task-feature-config/${projectId}`;
    try {
      return await apiFetch(null, url);
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get Chat Task feature config for project.",
        e.response?.message ?? e.toString(),
        `Error getting Chat Task feature config for project ID ${projectId}.`,
        url,
      );
    }
  };

  static updateChatTaskFeatureConfig = async (
    projectId: string,
    chatTaskFeatureConfig: IChatTaskFeatureConfig,
  ) => {
    const url = `internal/task-template/chat-task-feature-config/${projectId}`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          chatTaskFeatureConfig,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't update Chat Task feature config for project.",
        e.response?.message ?? e.toString(),
        `Error updating Chat Task feature config for project ID ${projectId}.`,
        url,
      );
    }
  };

  // These Delivery Builder Configs are stored in the DeliveryBuilderConfig model.
  // These do NOT read from the Config Cache or Project Config collection.
  static fetchDeliveryBuilderConfigs = async (projectId: string) => {
    const url = `internal/task-template/delivery-builder-config/${projectId}`;
    try {
      return await apiFetch(null, url);
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get Delivery Builder Configs for project.",
        e.response?.message ?? e.toString(),
        `Error getting Delivery Builder Configs for project ID ${projectId}.`,
        url,
      );
    }
  };

  static fetchDeliveryBuilderConfigById = async (configId: string) => {
    const url = `internal/task-template/delivery-builder-config-by-id/${configId}`;
    try {
      return await apiFetch(null, url);
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get Delivery Builder Config by ID.",
        e.response?.message ?? e.toString(),
        `Error getting Delivery Builder Config for ID ${configId}.`,
        url,
      );
    }
  };

  // These Delivery Builder Configs are stored in the DeliveryBuilderConfig model.
  // These do NOT write to the Config Cache or Project Config collection.
  static setDeliveryBuilderConfig = async (
    deliveryBuilderConfigCreate: DeliveryBuilderConfigCreate,
  ) => {
    const url = `internal/task-template/delivery-builder-config/create`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: deliveryBuilderConfigCreate,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't create Delivery Builder Config for project.",
        e.response?.message ?? e.toString(),
        `Error creating Delivery Builder Config for project ID ${deliveryBuilderConfigCreate.projectId}.`,
        url,
      );
    }
  };

  // These Delivery Builder Configs are stored in the DeliveryBuilderConfig model.
  // These do NOT write to the Config Cache or Project Config collection.
  static disableDeliveryBuilderConfig = async (deliveryBuilderConfigId: string) => {
    const url = `internal/task-template/delivery-builder-config/disable`;
    try {
      return await apiFetch<DeliveryBuilderConfigDisable>(null, url, {
        method: 'POST',
        body: {
          _id: deliveryBuilderConfigId,
          disabled: true,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't disable Delivery Builder Config.",
        e.response?.message ?? e.toString(),
        `Error disabling Delivery Builder Config for ID ${deliveryBuilderConfigId}.`,
        url,
      );
    }
  };

  static createProjectSLAGroup = async ({
    name,
    projectIds,
    description,
    businessUnit,
    slaCadence,
    status,
    businessUnitProduct,
    fulfillmentMechanism,
    endActiveProjectRoles,
  }: CreateProjectGroupRequest & {
    endActiveProjectRoles?: boolean;
  }) => {
    if (endActiveProjectRoles) {
      const projectRolesPromises = projectIds.map(projectId => {
        const url = `corp-api/qm/project_roles/?project=${projectId}`;
        try {
          return apiFetch(null, url, {
            method: 'GET',
          });
        } catch (e) {
          throw new EnrichedError(
            e.status,
            "Couldn't get project roles.",
            e.response?.message ?? e.toString(),
            `Error getting project roles for project ID ${projectId}.`,
            url,
          );
        }
      });
      const projectRolesResults = (await Promise.all(projectRolesPromises))
        .flat()
        .filter(
          projectRole =>
            !projectRole.stopDate ||
            (projectRole.stopDate && new Date(projectRole.stopDate) > new Date()),
        );

      const projectRolesToRemove = projectRolesResults.map(projectRole => ({
        projectRoleId: projectRole._id!,
        newStartDate: isAfter(new Date(projectRole.startDate), new Date())
          ? subDays(new Date(), DAY_MS)
          : projectRole.startDate,
        newEndDate: new Date(),
      }));

      const url = `corp-api/qm/project_role_events/`;
      try {
        await apiFetch(null, url, {
          method: 'PATCH',
          body: projectRolesToRemove,
        });
      } catch (e) {
        throw new EnrichedError(
          e.status,
          "Couldn't remove project roles.",
          e.response?.message ?? e.toString(),
          `Error removing project roles for project IDs ${projectIds.join(', ')}.`,
          url,
        );
      }
    }

    const url = 'corp-api/project-groups';
    try {
      return await apiFetch<CreateProjectGroupResponse | UpdateProjectGroupResponse>(null, url, {
        method: 'POST',
        body: {
          name,
          projectIds,
          description,
          businessUnitProduct,
          slaCadence,
          status,
          businessUnit,
          fulfillmentMechanism,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't create/update project SLA group.",
        e.response?.message ?? e.toString(),
        `Error creating or updating project SLA group.`,
        url,
      );
    }
  };

  static getSLAGroupByProjectGroupId = async (projectGroupId: string) => {
    const url = `corp-api/project-groups/${projectGroupId}`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get project SLA group.",
        e.response?.message ?? e.toString(),
        `Error getting project SLA group for projectGroup ID ${projectGroupId}.`,
        url,
      );
    }
  };

  static updatePipelineV3HookConfig = async (projectId: string, hookConfig: any) => {
    const url = `corp-api/pipelineV3/updatePipelineV3HookConfig/${projectId}`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: hookConfig,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't update pipelineV3 hook config.",
        e.response?.message ?? e.toString(),
        `Error updating pipelineV3 hook config for project ID ${projectId}.`,
        url,
      );
    }
  };

  static updatePipelineV3Config = async (projectId: string, config: IPipelineV3Config) => {
    const url = `corp-api/pipelineV3/${projectId}/update`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          config,
          applyToExistingTasks: false,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't update pipelineV3 config.",
        e.response?.message ?? e.toString(),
        `Error updating pipelineV3 config for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getWorkersByWorkerTeam = async (workerTeamId: string, limit: number) => {
    const url = `corp-api/worker_teams/team/${workerTeamId}/members`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
        query: { limit },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get workers by worker team.",
        e.response?.message ?? e.toString(),
        `Error getting workers by worker team ID ${workerTeamId}.`,
        url,
      );
    }
  };

  static getAllWorkerTeams = async () => {
    const url = `corp-api/worker_teams/list-worker-teams`;
    try {
      return await apiFetch<{ workerTeams: IWorkerTeam[] }>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get all worker teams.",
        e.response?.message ?? e.toString(),
        `Error getting all worker teams.`,
        url,
      );
    }
  };

  static getWorkerTeamsDataByProjectId = async (projectId: string) => {
    const url = `corp-api/worker_teams/teams_and_members_by_project/${projectId}`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get worker team info.",
        e.response?.message ?? e.toString(),
        `Error getting worker team info for project ID ${projectId}.`,
        url,
      );
    }
  };

  static searchTeams = async (searchText: string): Promise<{ id: string; name: string }[]> => {
    const url = 'corp-api/worker_teams/autocomplete';
    try {
      const response = await apiFetch<{ results: any[] }>(null, url, {
        method: 'GET',
        query: { search: searchText },
      });
      return response.results;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't search worker teams.",
        e.response?.message ?? e.toString(),
        `Error searching worker teams.`,
        url,
      );
    }
  };

  static getRandomConfig = async (randomConfigName: string) => {
    const url = `corp-api/random-configs?name=${randomConfigName.trim()}`;
    try {
      const response = await apiFetch(null, url, {
        method: 'GET',
      });
      return response;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get random config.",
        e.response?.message ?? e.toString(),
        `Error getting random config for name ${randomConfigName}.`,
        url,
      );
    }
  };

  static chatCompletion = async (body: CreateCompletionRequest): Promise<string> => {
    const url = 'internal/self_serve/rlhf/createCompletion';
    try {
      const response = await apiFetch<CreateCompletionResponse>(null, url, {
        method: 'POST',
        body,
      });

      if (response.error) {
        throw new Error(`Model error (${response.error})`);
      }

      return response.choices[0].content;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't generate the script.",
        e.response?.message ?? e.toString(),
        `Error generating script.`,
        url,
      );
    }
  };

  static fetchTeam = async (teamId: string) => {
    const url = `corp-api/worker_teams/team/${teamId}`;
    try {
      return await apiFetch<HydratedWorkerTeam>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get worker team.",
        e.response?.message ?? e.toString(),
        `Error getting worker team for team ID ${teamId}.`,
        url,
      );
    }
  };

  static fetchTeamContributors = async (teamId: string) => {
    const url = `corp-api/worker_teams/team/${teamId}/contributors`;
    try {
      return await apiFetch<TeamContributorsResponse>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get worker team contributors.",
        e.response?.message ?? e.toString(),
        `Error getting worker team contributors for team ID ${teamId}.`,
        url,
      );
    }
  };

  static fetchTeamConfig = async (teamId: string) => {
    const url = `corp-api/worker_teams/team/${teamId}/config`;
    try {
      return await apiFetch<WorkerTeamConfigProperties>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get worker team config.",
        e.response?.message ?? e.toString(),
        `Error getting worker team config for team ID ${teamId}.`,
        url,
      );
    }
  };

  static fetchTeamAssignmentsWithExtras = async (teamId: string, includeInherited: boolean) => {
    const url = `corp-api/worker_teams/team/${teamId}/team_assignments_with_extras?includeInherited=${includeInherited}`;
    try {
      return await apiFetch<TeamAssignmentsExtrasResponse>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get worker team assignments.",
        e.response?.message ?? e.toString(),
        `Error getting worker team assignments for team ID ${teamId}.`,
        url,
      );
    }
  };

  static fetchTeamAssignmentEvents = async (teamId: string) => {
    const url = `corp-api/worker_teams/team/${teamId}/assignment_events`;
    try {
      return apiFetch<{ events: HydratedWorkerTeamEvent[] }>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get worker team assignment events.",
        e.response?.message ?? e.toString(),
        `Error getting worker team assignment events for team ID ${teamId}.`,
        url,
      );
    }
  };

  static fetchTeamContributorEvents = async (teamId: string) => {
    const url = `corp-api/worker_teams/team/${teamId}/contributor_events`;
    try {
      return apiFetch<{ events: HydratedWorkerTeamEvent[] }>(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get worker team contributor events.",
        e.response?.message ?? e.toString(),
        `Error getting worker team contributor events for team ID ${teamId}.`,
        url,
      );
    }
  };

  static getProjectMarketplaceStatusByWorkerSkill = async ({
    projectId,
  }: {
    projectId: string;
  }): Promise<ProjectMarketplaceStatusWorkerSkillsResponseDTO> => {
    const url = `internal/marketplace/projects/${projectId}/project_priorities/project_worker_skills_status`;
    try {
      return apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      return { projectWorkerSkillStatuses: {} }; // silent error for now
    }
  };

  static getProjectMarketplaceStatus = async ({
    projectId,
    query,
  }: {
    projectId: string;
    query?: Record<string, string>;
  }): Promise<ProjectMarketplaceStatus> => {
    const url = `internal/marketplace/projects/${projectId}/project_priorities/overall_status`;
    try {
      return apiFetch(null, url, {
        method: 'GET',
        query,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get project marketplace status.",
        e.response?.message ?? e.toString(),
        `Error getting project marketplace status.`,
        url,
      );
    }
  };

  static getProjectPrebuildStatus = async (projectId: string) => {
    const url = `internal/marketplace/projects/${projectId}/project_priorities/prebuild_status`;
    try {
      return apiFetch(null, url, { method: 'GET' });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get project prebuild status.",
        e.response?.message ?? e.toString(),
        `Error getting project prebuild status for project ID ${projectId}.`,
        url,
      );
    }
  };

  static fetchProjectPriorities = async (
    workerSkills?: string[],
    projectGroups?: string[],
    layer?: ProjectPriorityLayer,
    alpha?: string,
    statuses?: string[],
    searchTerm?: string,
    page?: number,
    pageSize?: number,
    matchAnyOption?: boolean,
  ) => {
    const url = `internal/user-projects/project-priority`;
    try {
      return apiFetch(null, url, {
        method: 'GET',
        query: {
          workerSkills,
          projectGroups,
          layer,
          alpha,
          statuses,
          searchTerm,
          page,
          pageSize,
          matchAnyOption,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get project priorities.",
        e.response?.message ?? e.toString(),
        `Error getting project priorities.`,
        url,
      );
    }
  };

  static fetchProjectGroupPriorities = async (
    page: number,
    pageSize: number,
  ): Promise<Paginated<MarketplaceProjectGroupDTO>> => {
    const url = `internal/marketplace/project_groups`;
    try {
      return apiFetch(null, url, {
        method: 'GET',
        query: { page, pageSize },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get project group priorities.",
        e.response?.message ?? e.toString(),
        `Error getting project group priorities.`,
        url,
      );
    }
  };

  static validateHasRole = async ({
    corpRole,
  }: {
    corpRole: string;
  }): Promise<{ isValid: boolean }> => {
    const url = `corp-api/find/corppermissionroles/validate?corpRoleId=${corpRole}`;
    try {
      return await apiFetch(null, url);
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't validate role.",
        e.response?.message ?? e.toString(),
        `Error validating role ${corpRole}.`,
        url,
      );
    }
  };

  static validateHasPermission = async ({
    permission,
  }: {
    permission: AccessPermission;
  }): Promise<{ hasPermission: boolean }> => {
    const url = `corp-api/corppermissionroles/checkPermission?permission=${permission}`;
    try {
      return apiFetch(null, url);
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't validate permission.",
        e.response?.message ?? e.toString(),
        `Error validating permission ${permission}.`,
        url,
      );
    }
  };

  static fetchProjectPriority = async ({
    projectId,
  }: {
    projectId: string;
  }): Promise<ProjectDemandInfo[]> => {
    const url = `internal/project-demand/${projectId}/projectPriority`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't get priority for project: ${projectId}.`,
        e.response?.message ?? e.toString(),
        `Error getting project priority for projectId: ${projectId}.`,
        url,
      );
    }
  };

  static fetchAllProjectPrioritiesForProject = async ({ projectId }: { projectId: string }) => {
    const url = `internal/project-demand/${projectId}/projectPriorities`;
    try {
      return await apiFetch(null, url, { method: 'GET' });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't get all project priorities for project: ${projectId}.`,
        e.response?.message ?? e.toString(),
        `Error getting all project priorities for projectId: ${projectId}.`,
        url,
      );
    }
  };

  static saveProjectPriority = async (projectId: string, body: SaveProjectPriorityRequestBody) => {
    const url = `internal/project-demand/${projectId}/projectPriority`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't save priority for project: ${projectId}.`,
        e.response?.message ?? e.toString(),
        `Error saving project priority for projectId: ${projectId}.`,
        url,
      );
    }
  };

  static createOrUpdateProjectPriority = async (
    projectId: string,
    body: CreateOrUpdateProjectPriorityRequestDTO,
  ) => {
    const url = `internal/marketplace/projects/${projectId}/project_priorities`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't create project priority for project: ${projectId}.`,
        e.response?.message ?? e.toString(),
        `Error creating project priority for projectId: ${projectId}.`,
        url,
      );
    }
  };

  static bulkUpdateProjectPriorities = async (
    projectId: string,
    body: BulkUpdateProjectPrioritiesRequestDTO,
  ) => {
    const url = `internal/marketplace/projects/${projectId}/project_priorities/bulk_update`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't bulk update project priorities for project: ${projectId}.`,
        e.response?.message ?? e.toString(),
        `Error bulk updating project priorities for projectId: ${projectId}.`,
        url,
      );
    }
  };

  static rerankProject = async (projectId: string, body: ReRankProjectRequestDTO) => {
    const url = `internal/marketplace/projects/${projectId}/re-rank`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't rerank project: ${projectId}.`,
        e.response?.message ?? e.toString(),
        `Error reranking project for projectId: ${projectId}.`,
        url,
      );
    }
  };

  static rerankProjectPreview = async (
    projectId: string,
    body: BulkUpdateProjectPrioritiesRequestDTO,
  ): Promise<BulkUpdateProjectPrioritiesImpactResponseDTO> => {
    const url = `internal/marketplace/projects/${projectId}/project_priorities/bulk_update_impact`;
    try {
      return await apiFetch<BulkUpdateProjectPrioritiesImpactResponseDTO>(null, url, {
        method: 'POST',
        body,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't preview rerank for project: ${projectId}.`,
        e.response?.message ?? e.toString(),
        `Error previewing rerank for projectId: ${projectId}.`,
        url,
      );
    }
  };

  static fetchProjectPipelineV3Config = async ({ projectId }: { projectId: string }) => {
    const url = `corp-api/pipelinev3/${projectId}`;
    try {
      const res = await apiFetch<GetPipelineV3ConfigResponse>(null, url, {
        method: 'GET',
      });
      return res.config as IPipelineV3Config;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't get pipelineV3 config for project: ${projectId}.`,
        e.response?.message ?? e.toString(),
        `Error getting pipelineV3 config for projectId: ${projectId}.`,
        url,
      );
    }
  };

  static fetchProjectConfigs = async ({
    projectId,
    configAttributes,
  }: {
    projectId: string;
    configAttributes: Record<string, string>;
  }) => {
    const query = { type: 'project', ...configAttributes };
    const url = `corp-api/lookup/${projectId}/config?${queryString.stringify(query)}`;
    try {
      return await apiFetch<{ config: Record<string, any>; version: number; allMeta: any }>(
        null,
        url,
        {
          method: 'GET',
        },
      );
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't get config for project: ${projectId}.`,
        e.response?.message ?? e.toString(),
        `Error getting config for projectId: ${projectId}.`,
        url,
      );
    }
  };

  static fetchProjectThroughput = async ({
    projectId,
    throughputType,
    scrollStartDateTime,
    scrollEndDateTime,
  }: {
    projectId: string;
    throughputType: ThroughputType;
    scrollStartDateTime?: Date;
    scrollEndDateTime?: Date;
  }) => {
    const url = `internal/project-demand/${projectId}/projectThroughput`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
        query: { throughputType, scrollStartDateTime, scrollEndDateTime },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't get project throughput for projectId: ${projectId}.`,
        e.response?.message ?? e.toString(),
        `Error getting project throughput for projectId: ${projectId}.`,
        url,
      );
    }
  };

  static fetchHistoricalRampPlanData = async ({
    projectId,
    startDate,
    endDate,
  }: {
    projectId: string;
    startDate: Date;
    endDate: Date;
  }) => {
    const url = `internal/ramp-plan/${projectId}/taskAttemptAggregation`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
        query: {
          startDate,
          endDate,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get historic ramp plan data.",
        e.response?.message ?? e.toString(),
        `Error getting historical ramp plan data for project ID ${projectId}.`,
        url,
      );
    }
  };

  static assignmentsDataByProjectId = async (projectId: string) => {
    const url = `internal/user-projects/${projectId}/assignments`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get assignment entries for project.",
        e.response?.message ?? e.toString(),
        `Error getting assignment entries for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getReviewLevels = async (projectId: string) => {
    const url = `corp-api/chatBulkAudit/reviewLevels/${projectId}`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get review levels for project.",
        e.response?.message ?? e.toString(),
        `Error getting review levels for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getThrottleStats = async (projectId: string) => {
    const url = `internal/permissions/project/${projectId}/throttle_status`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get throttle count statistics for project.",
        e.response?.message ?? e.toString(),
        `Error getting throttle count statistics for project ID ${projectId}.`,
        url,
      );
    }
  };

  static getProjectThrottleStates = async (projectId: string) => {
    const url = `internal/permissions/project/${projectId}/project_throttle_states`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get project throttle states for project.",
        e.response?.message ?? e.toString(),
        `Error getting project throttle states for project ID ${projectId}.`,
        url,
      );
    }
  };

  static lookupScaliens = async (
    email: string,
    options?: { signal?: AbortSignal },
  ): Promise<{ users: IUserJson[] }> => {
    if (!email) return Promise.resolve({ users: [] });

    const url = `corp-api/find/scaliens`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
        query: { email: encodeURIComponent(email.replace(/ /g, '.')), includeAllUsers: true },
        signal: options?.signal,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't perform Scalien lookup.",
        e.response?.message ?? e.toString(),
        `Error performing Scalien lookup for email ${email}.`,
        url,
      );
    }
  };

  static lookupUsersByEmail = async (
    email: string,
    options?: { signal?: AbortSignal; limitToContributors?: boolean },
  ): Promise<{ users: IUserJson[] }> => {
    if (!email) return Promise.resolve({ users: [] });

    const url = `corp-api/find/usersByEmail`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
        query: {
          email: encodeURIComponent(email.replace(/ /g, '.')),
          includeAllUsers: true,
          limitToContributors: options?.limitToContributors ?? false,
        },
        signal: options?.signal,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't perform lookup users by email.",
        e.response?.message ?? e.toString(),
        `Error performing users lookup by email ${email}.`,
        url,
      );
    }
  };

  static fetchUserEmailsByIds = async (userIds: string[]): Promise<Record<string, string>> => {
    if (!userIds.length) return Promise.resolve({});

    const url = `corp-api/user_lookup/emails`;
    try {
      return await apiFetch(null, url, {
        body: {
          userIds,
        },
        method: 'POST',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't fetch user emails.",
        e.response?.message ?? e.toString(),
        `Error fetching user emails for user IDs: ${userIds.toString()}.`,
        url,
      );
    }
  };

  static fetchUser = async (userId: string) => {
    if (!userId) return Promise.resolve({});

    const url = `corp-api/user/${userId}`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get user.",
        e.response?.message ?? e.toString(),
        `Error getting user for user ID ${userId}.`,
        url,
      );
    }
  };

  static addProjectRoles = async (newProjectRoles: ProjectRoleAddRequest) => {
    const url = `corp-api/qm/project_role_events/`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: newProjectRoles,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't add project roles.",
        e.response?.message ?? e.toString(),
        `Error adding project roles for project IDs ${newProjectRoles
          .map(r => r.project)
          .toString()}.`,
        url,
      );
    }
  };

  static updateProjectRoles = async (
    rolesToUpdate: {
      projectRoleId: string;
      newStartDate: string;
      newEndDate: string;
    }[],
  ) => {
    const url = `corp-api/qm/project_role_events/`;
    try {
      return await apiFetch(null, url, {
        method: 'PATCH',
        body: rolesToUpdate,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't update project roles.",
        e.response?.message ?? e.toString(),
        `Error updating project roles for project role IDs ${rolesToUpdate
          .map(r => r.projectRoleId)
          .toString()}.`,
        url,
      );
    }
  };

  static getProjectRolesByUser = async (userId: string) => {
    const url = `corp-api/qm/project_roles`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
        query: { user: userId },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get project roles for user.",
        e.response?.message ?? e.toString(),
        `Error getting project roles for user ID ${userId}.`,
        url,
      );
    }
  };

  static updateLastStaffingTime = async (userId: string) => {
    const url = `corp-api/qm/project_roles/last_staffing_time`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { user: userId },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't update last staffing time for user.",
        e.response?.message ?? e.toString(),
        `Error updating last staffing time for user ID ${userId}.`,
        url,
      );
    }
  };

  static reportIssue = async (body: ReportIssueReqBody): Promise<ReportIssueResBody> => {
    try {
      return await apiFetch(null, `corp-api/corp-project-group-manager/issues/report`, {
        method: 'POST',
        body,
      });
    } catch (e) {
      throw new Error(`Failed to create issue: ${e.message || e.toString()}`);
    }
  };

  static getProjectTaskModels = async (projectId: string) => {
    const url = `corp-api/corp-project-group-manager/projectTaskModels`;
    try {
      return await apiFetch<{ taskModels: object[]; error?: string }>(null, url, {
        method: 'POST',
        body: { projectId },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get project task models",
        e.response?.message ?? e.toString(),
        `Error getting project task models for project ID ${projectId}.`,
        url,
      );
    }
  };

  static lookupObject = async (objectId: string) => {
    const url = `corp-api/lookup/${objectId}`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't lookup for this object id: "${objectId}".`,
        e.response?.message ?? e.toString(),
        `Error looking up for id: ${objectId}`,
        url,
      );
    }
  };

  static lookupTask = async (taskId: string) => {
    const url = `corp-api/lookup/task/${taskId}`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't lookup for this task id: "${taskId}".`,
        e.response?.message ?? e.toString(),
        `Error looking up for id: ${taskId}`,
        url,
      );
    }
  };

  static lookupTaskLite = async (taskId: string) => {
    const url = `corp-api/lookup/task-lite/${taskId}`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't lookup for this task id: "${taskId}".`,
        e.response?.message ?? e.toString(),
        `Error looking up for id: ${taskId}`,
        url,
      );
    }
  };

  static lookupAttempt = async (attemptId: string) => {
    const url = `corp-api/lookup/attempt/${attemptId}`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't lookup for this attempt id: "${attemptId}".`,
        e.response?.message ?? e.toString(),
        `Error looking up for id: ${attemptId}`,
        url,
      );
    }
  };

  static lookupPreview = async (objectId: string, objectType: INamedObjectTypes) => {
    const url = `corp-api/lookup/preview/${objectType}/${objectId}`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't lookup for this object id: "${objectId}" of type "${objectType}".`,
        e.response?.message ?? e.toString(),
        `Error looking up for id: ${objectId} of type ${objectType}`,
        url,
      );
    }
  };

  static lookupSearch = async (searchText: string) => {
    const url = `corp-api/lookup/liteLookup/${searchText}`;
    try {
      if (matchesLookupId(searchText) || parseEmails(searchText)) {
        const lookupPreview = await apiFetch(null, url, {
          method: 'GET',
        });
        if (lookupPreview.type === INamedObjectTypes.Task) {
          return [
            {
              id: searchText,
              name: LookupSearchName.Task,
              type: 'OpsHub',
              redirectLookup: `${GenAIOpsHubPaths.Projects}/${lookupPreview.project_id}/${MainSidebarMenu.Throughput}/tasks/${lookupPreview.task_id}`,
            },
          ];
        } else if (lookupPreview.type === INamedObjectTypes.Project) {
          return [
            {
              id: searchText,
              name: LookupSearchName.Project,
              type: 'OpsHub',
              redirectLookup: `${GenAIOpsHubPaths.Projects}/${lookupPreview.project_id}`,
            },
          ];
        } else if (lookupPreview.type === INamedObjectTypes.TaskAttempt) {
          return [
            {
              id: searchText,
              name: LookupSearchName.TaskAttempt,
              type: 'OpsHub',
              redirectLookup: `${GenAIOpsHubPaths.Projects}/${lookupPreview.project_id}/${MainSidebarMenu.Throughput}/tasks/${lookupPreview.task_id}/${lookupPreview.attempt_id}`,
            },
          ];
        } else if (lookupPreview.type === INamedObjectTypes.User) {
          return [
            {
              id: searchText,
              name: LookupSearchName.User,
              type: 'OpsHub',
              redirectLookup: routeForUser(lookupPreview.user_id),
            },
          ];
        }
        // If the lookup type is not a task or attempt, return the Corp Lookup route
        return [
          {
            id: searchText,
            name: LookupSearchName.Corp,
            type: 'Corp',
            redirectLookup: `${GenAIOpsHubPaths.CorpLookup}/${searchText}`,
          },
        ];
      } else {
        // If the lookup type is not an ID, return the Corp Search route
        return [
          {
            id: searchText,
            name: LookupSearchName.Corp,
            type: 'Corp',
            redirectLookup: `${GenAIOpsHubPaths.CorpSearch}`,
            redirectLookupSearch: `?q=${searchText}`,
          },
        ];
      }
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Couldn't lookup for this object id: "${searchText}".`,
        e.response?.message ?? e.toString(),
        `Error looking up for id: ${searchText}`,
        url,
      );
    }
  };

  static getPipelineV3History = async (objectId: string) => {
    const url = `corp-api/lookup/${objectId}/pipelineHistory`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      // We shouldn't throw an error when no history is found
      return { executions: [], manualInterventions: [], _id: objectId };
    }
  };

  static runLookupCommand = async (
    objectId: string,
    commandName: CorpCommand,
    args?: Record<string, any>,
  ) => {
    const url = `corp-api/lookup/${objectId}/runCommand/${commandName}`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: args,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Failed to run ${commandName} on object with id ${objectId}.`,
        e.response?.message ?? e.toString(),
        `Error running ${commandName} on object with id ${objectId}.`,
        url,
      );
    }
  };

  static findCustomers = async (joinedString: string) => {
    const url = `corp-api/find/customers`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
        query: { objectId: joinedString },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Failed to find users from string ${joinedString}`,
        e.response?.message ?? e.toString(),
        `Error running finding users from joined string ${joinedString}`,
        url,
      );
    }
  };

  static getLiteLookup = async (searchId: string) => {
    const url = `corp-api/lookup/liteLookup/${searchId}`;
    try {
      return await apiFetch(null, url, { method: 'GET' });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Failed to get search preview for ${searchId}`,
        e.response?.message ?? e.toString(),
        `Error getting search preview for ${searchId}`,
        url,
      );
    }
  };

  static updateProjectSettingsBillingVersion = async (id: string, podWorkflow: string | null) => {
    const url = `corp-api/billing-versions/project-setup/update/${id}`;
    try {
      await apiFetch(null, url, {
        method: 'post',
        body: {
          podWorkflow,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Failed to update project settings billing version for project ${id}`,
        e.response?.message ?? e.toString(),
        `Error updating project settings billing version for project ${id}`,
        url,
      );
    }
  };

  static getProjectBillingPartitions = async (projectId: string) => {
    const url = `corp-api/billing-versions/partitions/${projectId}`;
    try {
      return await apiFetch(null, url, {});
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Failed to get project settings billing version for project ${projectId}`,
        e.response?.message ?? e.toString(),
        `Error getting project settings billing version for project ${projectId}`,
        url,
      );
    }
  };

  static approveBillingProposal = async (billingProposal: IProjectBillingSetup) => {
    const url = `corp-api/billing-versions/approve-billing-proposal/`;
    try {
      return await apiFetch(null, url, {
        method: 'post',
        body: {
          billingProposal,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        `Failed to approve billing proposal for project ${billingProposal.projectId}`,
        e.response?.message ?? e.toString(),
        `Error to approving billing proposal for project ${billingProposal.projectId}`,
        url,
      );
    }
  };

  static quarantineTasks = async (taskIds: string[], reason: string) => {
    const url = `corp-api/corp-project-group-manager/quarantinePipelineTasks`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { taskIds, reason },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't quarantine tasks.",
        e.response?.message ?? e.toString(),
        `Error quarantining tasks.`,
        url,
      );
    }
  };

  static addModelEndpoint = async (newModelEndpoint: ModelRegistryEndpoint) => {
    const url = `corp-api/model-registry/new`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: newModelEndpoint,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't add new model endpoint.",
        e.response?.message ?? e.toString(),
        `Error adding model endpoint ${newModelEndpoint.name} - ${newModelEndpoint.url}.`,
        url,
      );
    }
  };

  static updateModelEndpoint = async (modelEndpoint: ModelRegistryEndpoint) => {
    const url = `corp-api/model-registry/update`;
    try {
      return await apiFetch(null, url, {
        method: 'PUT',
        body: modelEndpoint,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't update model endpoint.",
        e.response?.message ?? e.toString(),
        `Error updating model endpoint ${modelEndpoint.name} - ${modelEndpoint.url}.`,
        url,
      );
    }
  };

  static getModelEndpoints = async () => {
    const url = `corp-api/model-registry/`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get model endpoints.",
        e.response?.message ?? e.toString(),
        `Error getting model endpoints.`,
        url,
      );
    }
  };

  static validateDelivery = async (
    customer: string,
    files: File[],
    useO1ReasoningValidation: boolean,
  ) => {
    const url = `corp-api/delivery-nexus/validate`;
    try {
      const formData = new FormData();
      formData.append('customer', customer);

      files.forEach((file, _) => {
        formData.append('files', file, file.name);
      });

      formData.append('useO1ReasoningValidation', useO1ReasoningValidation.toString());

      return await apiFetch(null, url, {
        method: 'POST',
        body: formData,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't validate delivery files.",
        e.response?.message ?? e.toString(),
        `Error validating delivery files.`,
        url,
      );
    }
  };

  static extractFileInfo = async (files: File[], datestamp: moment.Moment) => {
    try {
      const formData = new FormData();

      files.forEach(file => {
        formData.append('files', file);
      });

      formData.append('datestamp', datestamp.toISOString());

      const response = await apiFetch(null, 'corp-api/s3-upload/get-file-info', {
        method: 'POST',
        body: formData,
      });

      return response;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't extract file info.",
        e.message ?? e.toString(),
        `Error extracting file info.`,
        `corp-api/s3-upload/get-file-info`,
      );
    }
  };

  static summarizeDeliveries = async (customer: string, files: File[]) => {
    const url = `corp-api/delivery-nexus/summary`;
    try {
      const formData = new FormData();
      formData.append('customer', customer);

      files.forEach((file, _) => {
        formData.append('files', file, file.name);
      });

      return await apiFetch(null, url, {
        method: 'POST',
        body: formData,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't summarize deliveries.",
        e.response?.message ?? e.toString(),
        `Error summarizing deliveries.`,
        url,
      );
    }
  };

  static syncKratosSchema = async (projectIds: string[], useScaleBucket: boolean) => {
    const url = `corp-api/delivery-nexus/sync-kratos-schema`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { projectIds, useScaleBucket },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't sync Kratos schema.",
        e.response?.message ?? e.toString(),
        `Error syncing Kratos schema for project IDs ${projectIds.toString()}.`,
        url,
      );
    }
  };

  static fixKratosDeliveries = async (
    prefix: string,
    filename: string,
    isRecallOnly: boolean,
    isTest: boolean,
    isOverride: boolean,
  ) => {
    const url = `corp-api/delivery-nexus/fix-kratos-deliveries`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { prefix, filename, isRecallOnly, isTest, isOverride },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't fix Kratos deliveries",
        e.response?.message ?? e.toString(),
        `Error fix Kratos deliveries`,
        url,
      );
    }
  };

  static fixKratosDeliveriesByTaskIds = async (
    prefix: string,
    filename: string,
    taskIds: string[],
    isTest: boolean,
  ) => {
    const url = `corp-api/delivery-nexus/fix-kratos-deliveries-by-task-ids`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { prefix, filename, taskIds, isTest },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't fix Kratos deliveries by task IDs",
        e.response?.message ?? e.toString(),
        `Error fix Kratos deliveries by task IDs`,
        url,
      );
    }
  };

  static redeliverKratosFiles = async (filePathsToRedeliver: string[]) => {
    const url = `corp-api/delivery-customer-upload/redeliver-kratos-files`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { filePathsToRedeliver },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't redeliver Kratos files",
        e.response?.message ?? e.toString(),
        `Error redelivering Kratos files`,
        url,
      );
    }
  };

  static addJSONSchema = async (schema: DeliveryJSONSchema) => {
    const url = `corp-api/schema-registry/`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: schema,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't add new JSON schema.",
        e.response?.message ?? e.toString(),
        `Error adding JSON schema ${schema.name}.`,
        url,
      );
    }
  };

  static updateJSONSchema = async (schema: DeliveryJSONSchema) => {
    const url = `corp-api/schema-registry/${schema.id}`;
    try {
      return await apiFetch(null, url, {
        method: 'PUT',
        body: schema,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't update JSON schema.",
        e.response?.message ?? e.toString(),
        `Error updating JSON schema ${schema.name}.`,
        url,
      );
    }
  };

  static getJSONSchemas = async () => {
    const url = `corp-api/schema-registry/`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get JSON schemas.",
        e.response?.message ?? e.toString(),
        `Error getting JSON schemas.`,
        url,
      );
    }
  };

  static assignJSONSchemaToProject = async (schemaId: string, projectId: string) => {
    const url = `corp-api/schema-registry/assign`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { schemaId, projectId },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't assign JSON schema to project.",
        e.response?.message ?? e.toString(),
        `Error assigning JSON schema ${schemaId} to project ${projectId}.`,
        url,
      );
    }
  };

  static getProjectSchemaId = async (projectId: string) => {
    const url = `corp-api/schema-registry/project/${projectId}/schemaId`;
    try {
      return await apiFetch(null, url, { method: 'GET' });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get project schema id.",
        e.response?.message ?? e.toString(),
        `Error getting project schema id for project ${projectId}.`,
        url,
      );
    }
  };

  static getProjectSchema = async (projectId: string) => {
    const url = `corp-api/schema-registry/project/${projectId}/schema`;
    try {
      return await apiFetch(null, url, { method: 'GET' });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get project schema.",
        e.response?.message ?? e.toString(),
        `Error getting project schema for project ${projectId}.`,
        url,
      );
    }
  };

  static getSchemaById = async (schemaId: string) => {
    const url = `corp-api/schema-registry/${schemaId}`;
    try {
      return await apiFetch(null, url, { method: 'GET' });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't get schema.",
        e.response?.message ?? e.toString(),
        `Error getting schema for schemaId ${schemaId}.`,
        url,
      );
    }
  };

  static validateSingleJSON = async (schemaId: string, json: any) => {
    const url = `corp-api/schema-registry/validate`;
    try {
      return await apiFetch(null, url, { method: 'POST', body: { schemaId, json } });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't validate single JSON.",
        e.response?.message ?? e.toString(),
        `Error validating single JSON against schema ${schemaId}.`,
        url,
      );
    }
  };

  static getPayPerTaskInfo = async (projectId: string) => {
    const result = await apiFetch<Record<string, GetPayPerTaskInfoResponse>, {}>(
      null,
      `corp-api/pay-per-task/get-pay-per-task-info`,
      {
        method: 'POST',
        body: { projectId },
        parseAndHandle: true,
      },
    );
    return result;
  };

  static updatePayPerTaskInfo = async (
    projectId: string,
    isPayPerTaskEnabled: boolean,
    isDynamicPricingEnabled: boolean,
    isBasePayRangeEnabled: boolean,
    isAssessmentRateBonusEnabled: boolean,
  ) => {
    const result = await apiFetch<Record<string, GetPayPerTaskInfoResponse>, {}>(
      null,
      `corp-api/pay-per-task/update-pay-per-task-info`,
      {
        method: 'POST',
        body: {
          projectId,
          isPayPerTaskEnabled,
          isDynamicPricingEnabled,
          isBasePayRangeEnabled,
          isAssessmentRateBonusEnabled,
        },
        parseAndHandle: true,
      },
    );

    return result;
  };

  static getProjectPayConfigs = async (projectId: string) => {
    const result = await apiFetch<Record<string, GetProjectPayConfigsResponse>, {}>(
      null,
      `corp-api/project-pay-configs/get-project-pay-configs?projectId=${projectId}`,
      {
        method: 'GET',
        parseAndHandle: true,
      },
    );
    return result;
  };

  static updateCorpProfile = async (userId: string, newData: CorpProfileProps) => {
    const url = `corp-api/lookup/user/${userId}/scale_profile`;

    try {
      const result = await apiFetch<any>(null, url, {
        method: 'POST',
        body: { ...newData },
      });

      return result;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to update corp profile',
        e.response?.message ?? e.toString(),
        `Error updating corp profile for user ID ${userId}.`,
        url,
      );
    }
  };

  static getConfigChangelog = async (projectId: string) => {
    const url = `corp-api/lookup/${projectId}/changelog`;
    try {
      const result = await apiFetch(null, url, { method: 'GET' });
      return result;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to get config changelog',
        e.response?.message ?? e.toString(),
        `Error getting config changelog for project ${projectId}.`,
        url,
      );
    }
  };

  static getQuestionBankByProjectId = async (projectId: string) => {
    const url = `corp-api/question-bank/${projectId}`;
    try {
      return await apiFetch(null, url, { method: 'GET' });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to get Question Bank by project id',
        e.response?.message ?? e.toString(),
        `Error getting Question Bank by project id ${projectId}.`,
        url,
      );
    }
  };

  static getQuestionBanks = async () => {
    const url = `corp-api/question-bank/`;
    try {
      return await apiFetch(null, url, { method: 'GET' });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to get Question Banks',
        e.response?.message ?? e.toString(),
        `Error getting Question Banks.`,
        url,
      );
    }
  };

  static createQuestionBank = async (request: CreateQuestionBankRequest) => {
    const url = `corp-api/question-bank/`;
    try {
      return await apiFetch(null, url, { method: 'POST', body: request });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to create Question Bank',
        e.response?.message ?? e.toString(),
        `Error creating Question Bank ${request.name}.`,
        url,
      );
    }
  };

  static updateQuestionBank = async (request: UpdateQuestionBankRequest) => {
    const url = `corp-api/question-bank/${request.id}`;
    try {
      return await apiFetch(null, url, { method: 'PUT', body: request });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to update Question Bank',
        e.response?.message ?? e.toString(),
        `Error updating Question Bank ${request.id}.`,
        url,
      );
    }
  };

  static archiveQuestionBank = async (questionBankId: string) => {
    const url = `corp-api/question-bank/${questionBankId}/archive`;
    try {
      return await apiFetch(null, url, { method: 'POST' });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to archive Question Bank',
        e.response?.message ?? e.toString(),
        `Error archiving Question Bank ${questionBankId}.`,
        url,
      );
    }
  };

  static getQuestionsByQuestionBankId = async (questionBankId: string) => {
    const url = `corp-api/question-bank/${questionBankId}/questions`;
    try {
      return await apiFetch(null, url, { method: 'GET' });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to get Questions by Question Bank id',
        e.response?.message ?? e.toString(),
        `Error getting Questions by Question Bank id ${questionBankId}.`,
        url,
      );
    }
  };

  static createQuestionBankQuestion = async (request: CreateQuestionRequest) => {
    const url = `corp-api/question-bank/${request.questionBankId}/questions`;
    try {
      return await apiFetch(null, url, { method: 'POST', body: request });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to create Question',
        e.response?.message ?? e.toString(),
        `Error creating Question ${request.name} for Question Bank ${request.questionBankId}.`,
        url,
      );
    }
  };

  static updateQuestionBankQuestion = async (request: UpdateQuestionRequest) => {
    const url = `corp-api/question-bank/questions/${request.id}`;
    try {
      return await apiFetch(null, url, { method: 'PUT', body: request });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to update Question',
        e.response?.message ?? e.toString(),
        `Error updating Question ${request.id}.`,
        url,
      );
    }
  };

  static archiveQuestionBankQuestion = async (questionId: string) => {
    const url = `corp-api/question-bank/questions/${questionId}/archive`;
    try {
      return await apiFetch(null, url, { method: 'POST' });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to archive Question',
        e.response?.message ?? e.toString(),
        `Error archiving Question ${questionId}.`,
        url,
      );
    }
  };

  static getProjectsByQuestionBankId = async (questionBankId: string) => {
    const url = `corp-api/question-bank/${questionBankId}/projects`;
    try {
      return await apiFetch(null, url, { method: 'GET' });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to get Projects by Question Bank id',
        e.response?.message ?? e.toString(),
        `Error getting Projects by Question Bank id ${questionBankId}.`,
        url,
      );
    }
  };

  static assignProjectsToQuestionBank = async (questionBankId: string, projectIds: string[]) => {
    const url = `corp-api/question-bank/${questionBankId}/projects`;
    try {
      return await apiFetch(null, url, { method: 'POST', body: { projectIds } });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to assign Projects to Question Bank',
        e.response?.message ?? e.toString(),
        `Error assigning Projects to Question Bank ${questionBankId}.`,
        url,
      );
    }
  };

  static unAssignProjectsFromQuestionBank = async (
    questionBankId: string,
    projectIds: string[],
  ) => {
    const url = `corp-api/question-bank/${questionBankId}/projects`;
    try {
      return await apiFetch(null, url, { method: 'DELETE', body: { projectIds } });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to unassign Projects from Question Bank',
        e.response?.message ?? e.toString(),
        `Error unassigning Projects from Question Bank ${questionBankId}.`,
        url,
      );
    }
  };

  static getAllWorkerSkills = async (
    tree: boolean,
    treeLevels?: number,
  ): Promise<WorkerSkillResponse[]> => {
    const url = '/corp-api/worker-skills';
    try {
      const response = await apiFetch<GetWorkerSkillsResult>(null, url, {
        method: 'GET',
        query: {
          tree,
          treeLevels,
        },
      });

      return response.workerSkills;
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to get all worker skills',
        e.response?.message ?? e.toString(),
        `Error getting all worker skills.`,
        url,
      );
    }
  };

  static getProjectsForQuestion = async (questionId: string) => {
    const url = `corp-api/question-bank/questions/${questionId}/projects`;
    try {
      return await apiFetch<QuestionProjectInfo[]>(null, url, { method: 'GET' });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to get projects for question',
        e.response?.message ?? e.toString(),
        `Error getting projects for question ID ${questionId}.`,
        url,
      );
    }
  };

  static getQuestionsForProject = async (projectId: string) => {
    const url = `corp-api/question-bank/projects/${projectId}/questions`;
    try {
      return await apiFetch(null, url, { method: 'GET' });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to get questions for project',
        e.response?.message ?? e.toString(),
        `Error getting questions for project ID ${projectId}.`,
        url,
      );
    }
  };

  static assignProjectsToQuestion = async (questionId: string, projectIds: string[]) => {
    const url = `corp-api/question-bank/questions/${questionId}/projects`;
    try {
      return await apiFetch(null, url, { method: 'POST', body: { projectIds } });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to assign projects to question',
        e.response?.message ?? e.toString(),
        `Error assigning projects to question ID ${questionId}.`,
        url,
      );
    }
  };

  static unassignProjectsFromQuestion = async (questionId: string, projectIds: string[]) => {
    const url = `corp-api/question-bank/questions/${questionId}/projects`;
    try {
      return await apiFetch(null, url, { method: 'DELETE', body: { projectIds } });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to unassign projects from question',
        e.response?.message ?? e.toString(),
        `Error unassigning projects from question ID ${questionId}.`,
        url,
      );
    }
  };

  static getStagingFilesForDelivery = async (customer: string, datestamp: string) => {
    const url = `corp-api/delivery-customer-upload/list-staging-files`;
    try {
      return await apiFetch(null, url, { method: 'GET', query: { customer, datestamp } });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to fetch staging files',
        e.response?.message ?? e.toString(),
        `Failed to fetch staging files for customer ${customer} and datestamp ${datestamp}`,
        url,
      );
    }
  };

  static getExpectedFilesForDelivery = async (customer: string, datestamp: string) => {
    const url = `corp-api/delivery-customer-upload/fetch-expected-files`;
    try {
      return await apiFetch(null, url, { method: 'GET', query: { customer, datestamp } });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to fetch expected files',
        e.response?.message ?? e.toString(),
        `Failed to fetch expected files for customer ${customer} and datestamp ${datestamp}`,
        url,
      );
    }
  };

  static uploadStagingFilesToCustomer = async (
    customer: string,
    datestamp: string,
    filePathsToUpload?: string[],
  ) => {
    const url = `corp-api/delivery-customer-upload/upload-staging-files`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          customer,
          datestamp,
          filePathsToUpload,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to upload staging files',
        e.response?.message ?? e.toString(),
        `Failed to upload staging files for customer ${customer} and datestamp ${datestamp}`,
        url,
      );
    }
  };

  static warmupToolHealth = async (tool: string, image: string) => {
    const url = `corp-api/tool-health/warmup`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { tool, image },
      });
    } catch {
      // Warmup failures are non-critical — swallow silently
    }
  };

  static checkToolHealth = async (tool: string, query: string, image?: string) => {
    const url = `corp-api/tool-health/check`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          tool,
          query,
          ...(image && { image }),
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to check tool health',
        e.response?.message ?? e.toString(),
        `Failed to check health for tool ${tool}`,
        url,
      );
    }
  };

  static combineDeliveriesToStaging = async (deliveryIds: string[], datestamp: string) => {
    const url = `corp-api/delivery-customer-upload/combine-deliveries-to-staging`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          deliveryIds,
          datestamp,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to combine and upload staging files',
        e.response?.message ?? e.toString(),
        `Failed to combine deliveries to staging for delivery IDs ${deliveryIds.toString()} and datestamp ${datestamp}`,
        url,
      );
    }
  };

  static specialI18nSafetyDelivery = async (deliveryIds: string[], datestamp: string) => {
    const url = `corp-api/delivery-customer-upload/special-i18n-safety-delivery`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          deliveryIds,
          datestamp,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to process special i18n safety delivery',
        e.response?.message ?? e.toString(),
        `Failed to process special i18n safety delivery for delivery IDs ${deliveryIds.toString()} and datestamp ${datestamp}`,
        url,
      );
    }
  };

  static deleteStagingFiles = async (customer: string, filePathsToDelete: string[]) => {
    const url = `corp-api/delivery-customer-upload/delete-staging-files`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          customer,
          filePathsToDelete,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to delete staging files',
        e.response?.message ?? e.toString(),
        `Failed to delete staging files for customer ${customer}`,
        url,
      );
    }
  };

  static deleteCustomerFiles = async (customer: string, filePathsToDelete: string[]) => {
    const url = `corp-api/delivery-customer-upload/delete-customer-files`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          customer,
          filePathsToDelete,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to delete customer files',
        e.response?.message ?? e.toString(),
        `Failed to delete customer files for customer ${customer}`,
        url,
      );
    }
  };

  static recallTasksFromKratosBucket = async (taskIdsToRecall: string[]) => {
    const url = `corp-api/delivery-customer-upload/recall-tasks-from-kratos-bucket`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          taskIdsToRecall,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to recall tasks from Kratos bucket',
        e.response?.message ?? e.toString(),
        `Failed to recall ${taskIdsToRecall.length} tasks from Kratos bucket`,
        url,
      );
    }
  };

  static getDeliveryOpsTaskAttempts = async (filters: {
    projectId: string;
    statuses?: AuditStatus[];
  }) => {
    const url = `/corp-api/trust-and-safety/delivery-ops-task-attempt-audits`;
    try {
      return await apiFetch<SuspiciousTaskAttemptAudit[]>(null, url, {
        method: 'GET',
        query: {
          projectId: filters.projectId,
          status: filters.statuses,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to get suspicious task attempts',
        e.response?.message ?? e.toString(),
        `Failed to get suspicious task attempts for project ${filters.projectId}`,
        url,
      );
    }
  };

  static getDeliveryOpsTaskAttemptAuditById = async (auditId: string) => {
    const url = `/corp-api/trust-and-safety/delivery-ops-task-attempt-audits/${auditId}`;
    try {
      return await apiFetch<SuspiciousTaskAttemptAudit>(null, url, { method: 'GET' });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to get suspicious task attempt audit',
        e.response?.message ?? e.toString(),
        `Failed to get suspicious task attempt audit for audit ID ${auditId}`,
        url,
      );
    }
  };

  static updateDeliveryOpsTaskAttemptAudit = async (params: {
    auditId: string;
    status?: AuditStatus;
    outcomes?: AuditOutcomeEnum[];
    rationale?: string;
  }) => {
    const url = `/corp-api/trust-and-safety/delivery-ops-task-attempt-audits/${params.auditId}`;
    try {
      return await apiFetch(null, url, {
        method: 'PATCH',
        body: params,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to update suspicious task attempt audit',
        e.response?.message ?? e.toString(),
        `Failed to update suspicious task attempt audit for audit ID ${params.auditId}`,
        url,
      );
    }
  };

  static getAuditById = async (auditId: string) => {
    const url = `/corp-api/trust-and-safety/tns-audits/${auditId}`;
    try {
      return await apiFetch<TnsAuditJson>(null, url, {
        method: 'GET',
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        `Couldn't get audit with id ${auditId}.`,
        error.response?.message ?? error.toString(),
        `Error getting audit with id ${auditId}`,
        url,
      );
    }
  };

  static getAuditsByTypeAndStatus = async (types?: AuditType[], statuses?: AuditStatus[]) => {
    const url = '/corp-api/trust-and-safety/tns-audits';
    try {
      return await apiFetch<{ audits: TnsAuditJson[] }>(null, url, {
        method: 'GET',
        query: { types, statuses },
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        `Couldn't get audits matching types: ${types}, statuses ${statuses}.`,
        error.response?.message ?? error.toString(),
        `Error getting audits matching types: ${types}, statuses ${statuses}.`,
        url,
      );
    }
  };

  static getAuditAttachmentUrls = async (auditId: string) => {
    const url = `/corp-api/trust-and-safety/tns-audits/audit-attachment-urls/${auditId}`;
    try {
      return await apiFetch<{ fileName: string; url: string }[]>(null, url, { method: 'GET' });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        `Couldn't get audit attachments for ${auditId}.`,
        error.response?.message ?? error.toString(),
        `Error getting audit attachments for ${auditId}`,
        url,
      );
    }
  };

  static submitSuspiciousBehaviorAudit = async (
    auditId: string,
    outcomes: AuditOutcomeEnum[],
    rationale: string,
  ) => {
    if (outcomes.length > 0) {
      const url = `/corp-api/trust-and-safety/tns-audits/${auditId}/submit`;
      try {
        return await apiFetch<{ success: boolean }>(null, url, {
          method: 'POST',
          body: { outcomes, rationale },
        });
      } catch (error) {
        throw new EnrichedError(
          error.status,
          `Couldn't submit audit ${auditId}.`,
          error.response?.message ?? error.toString(),
          `Error submitting audit ${auditId}`,
          url,
        );
      }
    }
    return false;
  };

  static getQuickGrabTasksByReviewLevel = async (projectId: string) => {
    const url = `/corp-api/corp-project-group-manager/quickGrab/tasks-by-review-level/${projectId}`;
    try {
      return await apiFetch<QuickGrabTasksResponse>(null, url, { method: 'GET' });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        `Couldn't get quick grab tasks by review level for ${projectId}.`,
        error.response?.message ?? error.toString(),
        `Error getting quick grab tasks by review level for ${projectId}.`,
        url,
      );
    }
  };

  static getFinanceAccess = async (projectId: string, userId: string) => {
    const url = `corp-api/corp-project-group-manager/financeAccess/${projectId}/${userId}`;
    try {
      return await apiFetch(null, url, { method: 'GET' });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to get finance access',
        e.response?.message ?? e.toString(),
        `Failed to get finance access for project ${projectId} and user ${userId}`,
        url,
      );
    }
  };

  static uploadCompletedTasksToStaging = async ({
    projectIds,
    billingDate,
    overrideValidation,
  }: {
    projectIds: string[];
    billingDate: string;
    overrideValidation: boolean;
  }): Promise<{ jobId: string }> => {
    const url = `corp-api/delivery-customer-upload/upload-completed-tasks-to-staging`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          projectIds,
          billingDate,
          overrideValidation,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to initiate upload of completed tasks to staging',
        e.response?.message ?? e.toString(),
        `Failed to initiate upload of completed tasks to staging for project(s) ${projectIds.join(
          ', ',
        )}`,
        url,
      );
    }
  };

  static countCompletedTasksForStaging = async ({
    projectIds,
    billingDate,
  }: {
    projectIds: string[];
    billingDate: string;
  }): Promise<{ totalTaskCount: number }> => {
    const queryParams = new URLSearchParams();
    projectIds.forEach(id => queryParams.append('projectIds', id));
    queryParams.append('billingDate', billingDate);

    const url = `corp-api/delivery-customer-upload/count-completed-tasks-for-staging?${queryParams.toString()}`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to count completed tasks for staging',
        e.response?.message ?? e.toString(),
        `Failed to count completed tasks for staging for project(s) ${projectIds.join(', ')}`,
        url,
      );
    }
  };

  static getUserEligibilityForInviteBatches = async (
    projectId: string,
    userIds: string[],
  ): Promise<UserEligibilityDTO[]> => {
    const url = `internal/marketplace/projects/${projectId}/invite_batches/user_eligibility`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { userIds },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to get user eligibility for invite batches',
        e.response?.message ?? e.toString(),
        `Failed to get user eligibility for invite batches for project ${projectId}`,
        url,
      );
    }
  };

  static createInviteBatch = async (
    projectId: string,
    workerIds: string[],
    createdById: string,
    expiresAt: Date,
    insertOrder?: number,
    groupName?: string,
    useLatestBatchExpiresAt?: boolean,
  ): Promise<InviteBatchDTO> => {
    const url = 'internal/marketplace/projects/invite_batches';
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          projectId,
          workerIds,
          createdById,
          expiresAt: expiresAt.toISOString(),
          insertOrder,
          name: groupName,
          useLatestBatchExpiresAt,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to create invite batch',
        e.response?.message ?? e.toString(),
        `Failed to create invite batch for project ${projectId}`,
        url,
      );
    }
  };

  static getInviteBatchesForProject = async (projectId: string) => {
    const url = `internal/marketplace/projects/${projectId}/invite_batches`;
    try {
      return await apiFetch<InviteBatchStatusDTO[]>(null, url, { method: 'GET' });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to get invite batches for project',
        e.response?.message ?? e.toString(),
        `Failed to get invite batches for project ${projectId}`,
        url,
      );
    }
  };

  static getEligibleContributors = async ({
    projectId,
    workerSkillIds,
  }: GetEligibleContributorsRequestDTO): Promise<GetEligibleContributorsResponseDTO> => {
    const url = 'internal/marketplace/projects/contributors/eligible';
    try {
      return await apiFetch<GetEligibleContributorsResponseDTO>(null, url, {
        method: 'POST',
        body: {
          projectId,
          workerSkillIds,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to get eligible contributors',
        e.response?.message ?? e.toString(),
        `Failed to get eligible contributors for project ${projectId}`,
        url,
      );
    }
  };

  static directAssignContributors = async (
    request: DirectAssignContributorsRequestDTO,
  ): Promise<BatchAssignments> => {
    const url = 'internal/user-projects/direct';
    try {
      return await apiFetch<BatchAssignments>(null, url, {
        method: 'POST',
        body: request,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to direct assign contributors',
        e.response?.message ?? e.toString(),
        `Failed to direct assign contributors for project ${request.projectId}`,
        url,
      );
    }
  };

  static assignOrMigrateAndAssignContributors = async (
    request: AssignOrMigrateAndAssignRequestDTO,
  ): Promise<AssignOrMigrateAndAssignResponseDTO> => {
    const url = 'internal/marketplace/projects/assign-or-migrate';
    try {
      return await apiFetch<AssignOrMigrateAndAssignResponseDTO>(null, url, {
        method: 'POST',
        body: request,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to assign or migrate and assign contributors',
        e.response?.message ?? e.toString(),
        `Failed to assign or migrate and assign contributors for project ${request.projectId}`,
        url,
      );
    }
  };

  static getS3DeliveryFiles = async (
    projectId: string,
    source: 'staging' | 'customer' = 'customer',
  ) => {
    const url = `corp-api/delivery-customer-upload/fetch-project-files`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
        query: { projectId, source },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to fetch S3 delivery files',
        e.response?.message ?? e.toString(),
        `Failed to fetch S3 delivery files for project ${projectId}`,
        url,
      );
    }
  };

  static getS3DeliveryFilesByPrefix = async (
    prefix: string,
    startDate: string,
    endDate: string,
  ) => {
    const url = `corp-api/delivery-customer-upload/fetch-s3-files-by-prefix`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
        query: { prefix, startDate, endDate },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to fetch S3 delivery files by prefix',
        e.response?.message ?? e.toString(),
        `Failed to fetch S3 delivery files by prefix for prefix ${prefix}`,
        url,
      );
    }
  };

  static downloadS3Jsons = async (
    projectId: string,
    selectedFiles: { date: string; filename: string }[],
    source: 'staging' | 'customer' = 'staging',
  ): Promise<Blob> => {
    const url = '/corp-api/delivery-customer-upload/download-jsons';
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          projectId,
          selectedFiles,
          source,
        },
        binary: true,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to download S3 JSONs',
        e.response?.message ?? e.toString(),
        `Failed to download S3 JSONs for project ${projectId}`,
        url,
      );
    }
  };

  static lookupTasksInDeliveryState = async (projectId: string, taskIds: string[]) => {
    const url = '/corp-api/task-delivery/lookup-tasks-in-delivery-state';
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: {
          projectId,
          taskIds,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to lookup tasks in delivery state',
        e.response?.message ?? e.toString(),
        `Failed to lookup tasks in delivery state for project ${projectId}`,
        url,
      );
    }
  };

  static getLatestTnSSpecDocForProject = async (projectId: string) => {
    const url = `corp-api/trust-and-safety/cheating/projects/${projectId}/tns-spec-doc/latest`;
    try {
      return await apiFetch<TnSSpecDocDTO | null>(null, url, { method: 'GET' });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to get latest Tns spec doc for project',
        e.response?.message ?? e.toString(),
        `Failed to get latest Tns spec doc for project ${projectId}`,
        url,
      );
    }
  };

  static getArchieCheatingAgents = async () => {
    const url = `corp-api/trust-and-safety/cheating/archie-agents/cheating`;
    try {
      return await apiFetch<ArchieCheatingAgentEndpointOutput>(null, url, { method: 'GET' });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to get archie cheating agents',
        e.response?.message ?? e.toString(),
        `Failed to get archie cheating agents`,
        url,
      );
    }
  };

  static saveTnSSpecDoc = async (projectId: string, requirements: TnSRequirementSpecDocInput[]) => {
    const url = `corp-api/trust-and-safety/cheating/tns-spec-doc`;
    try {
      return await apiFetch(null, url, { method: 'POST', body: { projectId, requirements } });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to save TnS spec doc',
        e.response?.message ?? e.toString(),
        `Failed to save TnS spec doc for project ${projectId}`,
        url,
      );
    }
  };

  static updatePipelineV3ArchieTnsEvaluationHookConfig = async (
    projectId: string,
    attemptSubmissionEnabled: boolean,
    reviewSubmissionEnabled: boolean,
  ) => {
    const url = `corp-api/pipelineV3/updateArchieTnsEvaluationHookConfig/${projectId}`;
    const body = {
      attemptSubmissionEnabled,
      reviewSubmissionEnabled,
    };
    try {
      return await apiFetch(null, url, { method: 'POST', body });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to update archie TNS evaluation hook config',
        e.response?.message ?? e.toString(),
        `Failed to update archie TNS evaluation hook config for project ${projectId}`,
        url,
      );
    }
  };

  static getArchieResults = async (projectId: string, hardRefresh: boolean = false) => {
    const url = `corp-api/trust-and-safety/cheating/projects/${projectId}/archie-results`;
    try {
      return await apiFetch(null, url, {
        method: 'GET',
        query: hardRefresh ? { hardRefresh: 'true' } : undefined,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to get archie results',
        e.response?.message ?? e.toString(),
        `Failed to get archie results for project ${projectId}`,
        url,
      );
    }
  };

  static backfillTnsEvaluation = async (projectId: string, attemptIds: string[]) => {
    const url = `corp-api/trust-and-safety/cheating/backfill`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { projectId, attemptIds },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to backfill TNS evaluation',
        e.response?.message ?? e.toString(),
        `Failed to backfill TNS evaluation for project ${projectId}`,
        url,
      );
    }
  };

  static syncOffPlatformData = async (projectId: string, customData: any) => {
    const url = `internal/archieQC/sync_off_platform_data/${projectId}`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: customData,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't sync off-platform data.",
        e.response?.message ?? e.toString(),
        `Error syncing off-platform data for project ID ${projectId}.`,
        url,
      );
    }
  };

  static checkExistingDelivery = async (
    projectId: string,
    billingDate: string,
  ): Promise<{ alreadyDelivered: boolean }> => {
    return await apiFetch(
      null,
      `corp-api/delivery-customer-upload/check-existing-delivery?projectId=${projectId}&billingDate=${billingDate}`,
      {
        method: 'GET',
      },
    );
  };

  static getWorkerSkillSupplyImpact = async ({
    userIds,
  }: GetWorkerSkillSupplyImpactRequestDTO): Promise<GetWorkerSkillSupplyImpactResponseDTO> => {
    const url = 'internal/marketplace/projects/contributors/worker-skill-supply-impact';
    try {
      return await apiFetch<GetWorkerSkillSupplyImpactResponseDTO>(null, url, {
        method: 'POST',
        body: {
          userIds,
        },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to get worker skill supply impact',
        e.response?.message ?? e.toString(),
        `Failed to get worker skill supply impact for users: ${userIds.join(', ')}`,
        url,
      );
    }
  };

  static syncKratosBuckets = async (params: {
    startDate?: string;
    endDate?: string;
    projectIds?: string[];
    taskIds?: string[];
    filePaths?: string[];
    deliveryIds?: string[];
    modifiedStartDate?: string;
    modifiedEndDate?: string;
  }) => {
    const url = `corp-api/delivery-customer-upload/sync-kratos-buckets`;
    try {
      return await apiFetch(null, url, { method: 'POST', body: params });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to sync Kratos customer and staging buckets',
        e.response?.message ?? e.toString(),
        `Failed to sync Kratos customer and staging buckets`,
        url,
      );
    }
  };

  static createOffPlatformDataUploadUrl = async (projectId: string, fileName: string) => {
    const url = `internal/archieQC/off-platform-data/presigned-upload-url`;
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { projectId, fileName },
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't create upload URL for off-platform data.",
        e.response?.message ?? e.toString(),
        `Error creating presigned upload URL for project ID ${projectId} and file ${fileName}.`,
        url,
      );
    }
  };

  static copyFilesFromCustomerToStaging = async () => {
    const url = `corp-api/delivery-customer-upload/copy-customer-files-to-staging`;
    try {
      return await apiFetch(null, url, { method: 'POST', body: {} });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        'Failed to copy files from customer to staging',
        e.response?.message ?? e.toString(),
        `Failed to copy files from customer to staging`,
        url,
      );
    }
  };

  static uploadOffPlatformDataFile = async (projectId: string, file: File) => {
    const url = `internal/archieQC/off-platform-data/upload`;
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('projectId', projectId);

      return await apiFetch(null, url, {
        method: 'POST',
        body: formData,
      });
    } catch (e) {
      throw new EnrichedError(
        e.status,
        "Couldn't upload off-platform data file!",
        e.response?.message ?? e.toString(),
        `Error uploading file ${file.name} for project ID ${projectId}.`,
        url,
      );
    }
  };
  static getS3BrowseContents = async (bucket: string, prefix: string = '') => {
    const url = `corp-api/s3/browse?bucket=${encodeURIComponent(bucket)}${
      prefix ? `&prefix=${encodeURIComponent(prefix)}` : ''
    }`;
    try {
      return await apiFetch(null, url, { method: 'GET' });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        error.response?.message ?? error.toString(),
        'There was a problem while browsing the S3 bucket.',
        'Check your bucket and prefix, and try again.',
        url,
      );
    }
  };

  static downloadS3Files = async (
    bucket: string,
    files: Array<{ key: string; name: string }>,
  ): Promise<Blob> => {
    const url = 'corp-api/s3/download';
    try {
      return await apiFetch(null, url, {
        method: 'POST',
        body: { bucket, files },
        binary: true,
      });
    } catch (error) {
      throw new EnrichedError(
        error.status,
        error.response?.message ?? error.toString(),
        'There was a problem while downloading files from S3.',
        'Check your files and try again.',
        url,
      );
    }
  };

  static getCohorts = async (params: {
    cohortIds?: string[];
    cohortVersionIds?: string[];
    page?: number;
    pageSize?: number;
    search?: string;
    filterToCreatedByUser?: boolean;
    filterToUserFavorites?: boolean;
    workforceProjectId?: string;
    referencedProjectIds?: string[];
    filters?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    archived?: boolean;
  }) => {
    const url = `internal/cohorts`;
    return await apiFetch(null, url, {
      method: 'GET',
      query: params,
    });
  };

  static createCohort = async (params: {
    name: string;
    filters: FilterGroup;
    description: string;
    ownerIds: string[];
    clonedFromCohort?: string;
    excludeCBsInFraudHolding: boolean;
    cohortMappings?: Record<string, string>;
    workforceProjectId?: string;
    ignoreAutoArchival?: boolean;
  }) => {
    const url = `internal/cohorts`;
    return await apiFetch(null, url, { method: 'POST', body: params });
  };

  static getCohortCounts = async (referencedProjectIds?: string[]) => {
    const url = `internal/cohorts/counts`;
    return await apiFetch(null, url, { method: 'GET', query: { referencedProjectIds } });
  };

  static checkCohortNameAvailability = async (name: string): Promise<{ available: boolean }> => {
    const url = `internal/cohorts/check-name/${encodeURIComponent(name)}`;
    return await apiFetch(null, url, { method: 'GET' });
  };

  static updateCohort = async (params: {
    cohortId: string;
    currentCohortVersionId: string;
    name: string;
    filters: FilterGroup;
    description: string;
    ownerIds: string[];
    excludeCBsInFraudHolding: boolean;
    cohortMappings?: Record<string, string>;
    ignoreAutoArchival?: boolean;
  }) => {
    const { cohortId, ...body } = params;
    const url = `internal/cohorts/${cohortId}`;
    return await apiFetch(null, url, { method: 'PUT', body });
  };

  static getCohortDetails = async (cohortId: string, hoursOffset: number) => {
    const url = `internal/cohorts/${cohortId}/details`;
    return await apiFetch(null, url, { method: 'GET', query: { hoursOffset } });
  };

  static getCohortContributorDetails = async (cohortId: string) => {
    const url = `internal/cohorts/${cohortId}/contributor-details`;
    return await apiFetch(null, url, { method: 'GET' });
  };

  static getContributorDetails = async (
    filters: FilterGroup,
    excludeCBsInFraudHolding: boolean,
    cohortId?: string,
  ) => {
    const url = `internal/cohorts/contributor-details`;
    return await apiFetch(null, url, {
      method: 'GET',
      query: { filters: JSON.stringify(filters), excludeCBsInFraudHolding, cohortId },
    });
  };

  static getCohortVersions = async (cohortId: string, includeArchived: boolean = true) => {
    const url = `internal/cohorts/${cohortId}/versions`;
    return await apiFetch(null, url, { method: 'GET', query: { includeArchived } });
  };

  static getMembershipEval = async (
    filters: FilterGroup,
    excludeCBsInFraudHolding: boolean,
    cohortId?: string,
  ): Promise<{
    contributorsMatchingFiltersCount: number;
    exactMatchCohort: any | undefined;
    inflowCount?: number;
    outflowCount?: number;
  }> => {
    const url = `internal/cohorts/membership-eval`;
    return await apiFetch(null, url, {
      method: 'GET',
      query: { filters: JSON.stringify(filters), excludeCBsInFraudHolding, cohortId },
    });
  };

  static updateCohortFavorite = async (cohortId: string, favorite: boolean) => {
    const url = `internal/cohorts/${cohortId}/favorite`;
    return await apiFetch(null, url, { method: 'PUT', body: { favorite } });
  };

  static delayCohortArchival = async (cohortId: string) => {
    const url = `internal/cohorts/${cohortId}/delay-archival`;
    return await apiFetch(null, url, { method: 'POST' });
  };

  static unarchiveCohort = async (cohortId: string) => {
    const url = `internal/cohorts/${cohortId}/unarchive`;
    return await apiFetch(null, url, { method: 'POST' });
  };

  static getCohortMappingsByConsumer = async (
    consumerId: string,
    consumerType: string,
  ): Promise<{
    mappings: Array<{
      _id: string;
      consumerId: string;
      consumerType: string;
      cohortId: string;
      cohortName: string;
      cohortVersionId: string;
      subscriptionType: string;
      versionNumber: number;
    }>;
  }> => {
    const url = `internal/cohorts/mappings?consumerId=${encodeURIComponent(
      consumerId,
    )}&consumerType=${encodeURIComponent(consumerType)}`;
    return await apiFetch(null, url, { method: 'GET' });
  };

  static archiveCohort = async (cohortId: string) => {
    const url = `internal/cohorts/${cohortId}/archive`;
    return await apiFetch(null, url, { method: 'POST' });
  };

  static tempCopyAudioFiles = async ({
    isStaging,
    baseDirectory,
  }: {
    isStaging: boolean;
    baseDirectory: string;
  }): Promise<{ jobIds: { jobId: string }[] }> => {
    const endpoint = 'corp-api/delivery-customer-upload/temp-copy-audio-files';
    const options = {
      method: 'POST',
      body: JSON.stringify({
        isStaging,
        baseDirectory,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    };
    try {
      return await apiFetch(null, endpoint, options);
    } catch (error) {
      throw new EnrichedError(
        error.status,
        'Failed to initiate temp copy audio files job',
        error.response?.message ?? error.toString(),
        `Failed to initiate temp copy audio files job for isStaging: ${isStaging}, baseDirectory: ${baseDirectory}`,
        endpoint,
      );
    }
  };
}

export async function getTaskPreviewConfig(projectId: string): Promise<TaskPreviewConfig> {
  return await apiFetch<TaskPreviewConfig>(
    null,
    `internal/task-preview/task-preview-config/${projectId}`,
    {
      method: 'GET',
    },
  );
}

export async function getTaskPreviewBenchmarkData(
  benchmarkId: string,
): Promise<{ benchmark: QMBenchmark }> {
  return await apiFetch<{ benchmark: QMBenchmark }>(null, `corp-api/qm/benchmark/${benchmarkId}`, {
    method: 'GET',
  });
}

export async function getTaskPreviewProjectBenchmarks(
  projectId: string,
): Promise<BenchmarkCandidate[]> {
  return await apiFetch<BenchmarkCandidate[]>(
    null,
    `internal/task-preview/project-benchmarks/${projectId}`,
    {
      method: 'GET',
    },
  );
}

export async function markBenchmarkDisabledTutorial(
  benchmarkId: string,
): Promise<{ success: boolean }> {
  const url = `internal/task-preview/mark-disabled-tutorial/${benchmarkId}`;
  try {
    return await apiFetch<{ success: boolean }>(null, url, {
      method: 'POST',
    });
  } catch (e) {
    throw new EnrichedError(
      e.status,
      'Failed to update benchmark',
      e.response?.message ?? e.toString(),
      `Error updating benchmark to disabled tutorial for benchmark ID ${benchmarkId}.`,
      url,
    );
  }
}

export async function setTaskPreviewConfig(
  projectId: string,
  benchmarkId: string | undefined,
  taskPreviewConfig: TaskPreviewConfig,
): Promise<{ success: boolean }> {
  const url = benchmarkId
    ? `internal/task-preview/mark-disabled-tutorial-set-config/${projectId}/${benchmarkId}`
    : `internal/task-preview/set-task-preview-config/${projectId}`;
  try {
    return await apiFetch<{ success: boolean }>(null, url, {
      method: 'POST',
      body: taskPreviewConfig,
    });
  } catch (e) {
    throw new EnrichedError(
      e.status,
      'Failed to set task preview config',
      e.response?.message ?? e.toString(),
      `Error setting task preview config for project ID ${projectId}.`,
      url,
    );
  }
}

export async function getTaskPreviewMetrics(
  projectId: string,
  dateRange: TaskPreviewDateRange,
): Promise<TaskPreviewEvent[]> {
  const url = `internal/task-preview/metrics`;
  try {
    return await apiFetch<TaskPreviewEvent[]>(null, url, {
      method: 'GET',
      query: { projectId, dateRange },
    });
  } catch (e) {
    throw new EnrichedError(
      e.status,
      'Failed to get task preview metrics',
      e.response?.message ?? e.toString(),
      `Error getting task preview metrics for project ID ${projectId} and date range ${dateRange}.`,
      url,
    );
  }
}
